﻿
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, inject, signal, OnDestroy, effect, computed, untracked, ViewChild } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { TwitchChatStateService } from './services/twitch-chat-state.service.js';
import { TwitchChatService } from './services/twitch-chat.service.js';
import { ExportService } from './services/export.service.js';
import { DownloadService } from './services/download.service.js';
import { ChatOverlayComponent } from './components/chat-overlay.component.js';
import { ChatSideComponent } from './components/chat-side.component.js';
import { HelpModalComponent } from './components/help-modal.component.js';
import { PathHelpModalComponent } from './components/path-help-modal.component.js';
import { FirebaseService } from './services/firebase.service.js';
import { LibraryService } from './services/library.service.js';
import { StateConflict, SyncService } from './services/sync.service.js';
import { SyncMigrationService } from './services/sync-migration.service.js';
import { ActivityBin, Highlight } from './models/twitch.model';

import { ChannelLibraryOverlayComponent } from './components/channel-library-overlay.component.js';
import { MarkerSourcesModalComponent } from './components/marker-sources-modal.component.js';
import { AdminPanelComponent } from './components/admin-panel/admin-panel.component.js';
import { TourOverlayComponent } from './components/tour-overlay.component.js';
import { TOUR_ANCHORS } from './components/tour-data-anchors.constants.js';
import { TooltipDirective } from './directives/tooltip.directive.js';
import { TooltipService } from './services/tooltip.service.js';
import { RegionScopeBadgeComponent } from './components/region-scope-badge.component.js';
import { TimeRegion, clampRegion, formatRegion, formatRegionDuration, isVisibleInRegion, moveRegionEdge, setRegionEdgeAt } from './utils/region.js';
import { buildRulerTicks } from './utils/heatmap-ruler.js';
import { HeatmapColumn, STEP_DEFAULT, ViewWindow, ZoomLimits, binsPerColumn, buildColumns, clampStep, detailStep, scrollLeftForStart, viewWindow, zoomLimits, zoomToWindow } from './utils/heatmap-zoom.js';
import { ClipRange, centeredZoom, clipHandleZones, clipZoomWindow, ensureVisibleStart, followStart, formatClipDuration, formatTimecode, isTimeVisible, moveClipEdge, snapTime } from './utils/heatmap-timeline.js';
import { VodChapter, chapterAt } from './utils/vod-chapters.js';
import { HeatmapZoomBarComponent, ZoomBarEdgeDrag, ZoomBarSegment } from './components/heatmap-zoom-bar.component.js';
import { FloatRect, placeFloating } from './utils/floating-position.js';
import { MarkersService } from './services/markers.service.js';
import { DemoService } from './services/demo.service.js';
import { OnboardingTourService } from './services/onboarding-tour.service.js';
import { QuotaExceededError, QuotaKind, QuotaService, QuotaState } from './services/quota.service.js';
import { ViewportService } from './services/viewport.service.js';
import { MobileControlBarComponent } from './components/mobile-control-bar.component.js';
import { TranscriptPanelComponent } from './components/transcript-panel.component.js';
import { TranscriptService } from './services/transcript.service.js';
import { UndoHistoryService } from './services/undo-history.service.js';
import { isTranscriptSource, TRANSCRIPT_CATEGORIES, TRANSCRIPT_CATEGORY_KEYS, TRANSCRIPT_SEARCH_STYLE, TranscriptCategory } from './models/transcript.model.js';

declare var Twitch: any; // For Twitch Player JS API

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, ChatOverlayComponent, ChatSideComponent, HelpModalComponent, PathHelpModalComponent, ChannelLibraryOverlayComponent, MarkerSourcesModalComponent, AdminPanelComponent, TourOverlayComponent, TooltipDirective, MobileControlBarComponent, TranscriptPanelComponent, RegionScopeBadgeComponent, HeatmapZoomBarComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(window:keydown)': 'handleKeyboardEvents($event)',
    '(window:keyup)': 'onWindowKeyUp($event)',
    '(window:focus)': 'onWindowFocus()',
    '(window:blur)': 'onWindowBlur()',
    '(window:resize)': 'onWindowResize()',
    '(document:click)': 'onDocumentClick()',
    'class': 'flex flex-col h-[100dvh] w-screen overflow-hidden bg-gray-950 text-gray-200 font-sans'
  },
  template: `
<!-- System banner (admin broadcast). INLINE (not position:fixed) — Twitch
     player SDK rejects play() when ANY position:fixed/absolute element with
     z-index above the iframe sits in the viewport, even if bboxes don't
     intersect. The chat-overlay handshake hit the same wall (see src/CLAUDE.md
     > Chat overlay handshake — solved via full DOM removal). For the banner,
     we make it part of the host's flex-col layout so it occupies real layout
     space at the top, and the rest of the app fills the remaining height.
     Trade-off: auth overlays (fixed inset-0 z-[100]) cover the banner during
     login/loading screens — operators only see it post-login. Acceptable for
     maintenance broadcasts. -->
@if (systemBanner(); as b) {
  <div class="shrink-0 w-full px-4 py-2 text-xs text-center font-medium border-b shadow-lg"
       [class.bg-amber-950]="b.severity === 'warning'"
       [class.text-amber-200]="b.severity === 'warning'"
       [class.border-amber-700]="b.severity === 'warning'"
       [class.bg-blue-950]="b.severity !== 'warning'"
       [class.text-blue-200]="b.severity !== 'warning'"
       [class.border-blue-700]="b.severity !== 'warning'">
    {{ b.message }}
  </div>
}

<!-- Auth Overlays -->
@if (isAuthLoading()) {
  <div class="fixed inset-0 z-[100] bg-gray-950 flex flex-col items-center justify-center">
    <svg class="animate-spin h-8 w-8 text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
  </div>
}

@if (!isAuthLoading() && !currentUser() && !demoService.isDemoMode()) {
  <div class="fixed inset-0 z-[100] bg-gray-950 flex flex-col items-center justify-center space-y-8 animate-fade-in overflow-hidden">
    <!-- Particles/Glow bg -->
    <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-indigo-600/20 rounded-full blur-[100px]"></div>

    <div class="text-center space-y-4 max-w-sm w-full px-6 relative z-10">
       <div class="w-16 h-16 mx-auto bg-gray-900 border border-indigo-500/30 rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-500/20">
          <svg class="w-8 h-8 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
       </div>
       <h1 class="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400 tracking-tight">Peakster</h1>
       <p class="text-sm text-gray-500">Находим пики в чате. Делаем клипы из лучших моментов.</p>

       <div class="mt-6 text-left bg-gray-900/40 border border-gray-800 rounded-lg p-3">
         <label class="flex items-start gap-2.5 cursor-pointer text-[11px] text-gray-400 leading-relaxed select-none">
            <input type="checkbox"
                   [checked]="consentGiven()"
                   (change)="consentGiven.set($any($event.target).checked)"
                   class="mt-0.5 w-4 h-4 shrink-0 accent-indigo-500 cursor-pointer">
            <span>
               Я ознакомился с
               <a href="/terms" target="_blank" rel="noopener" (click)="$event.stopPropagation()" class="text-indigo-400 hover:text-indigo-300 underline decoration-dotted">Пользовательским соглашением</a>
               и
               <a href="/privacy" target="_blank" rel="noopener" (click)="$event.stopPropagation()" class="text-indigo-400 hover:text-indigo-300 underline decoration-dotted">Политикой конфиденциальности</a>,
               даю согласие на обработку моих персональных данных, в том числе на их трансграничную передачу в США (Google LLC). Подтверждаю, что мне исполнилось 18 лет.
            </span>
         </label>
       </div>

       <button (click)="loginWithTwitch()"
               [disabled]="!consentGiven()"
               class="w-full mt-3 py-3 px-4 bg-[#9146FF] hover:bg-[#7b32e6] disabled:bg-gray-700 disabled:hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-60 text-white font-bold rounded-lg transition-all hover:scale-[1.02] active:scale-95 disabled:hover:scale-100 flex items-center justify-center gap-3 shadow-lg shadow-[#9146FF]/20 disabled:shadow-none">
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/></svg>
          Войти через Twitch
       </button>

       @if (loginRedirectStuck()) {
         <div class="mt-2 p-2.5 bg-amber-950/40 border border-amber-700/40 rounded-lg text-left">
           <p class="text-[11px] text-amber-300 leading-relaxed">
             Не удалось подключиться к Twitch. Попробуйте обновить страницу или сменить сеть.
           </p>
         </div>
       }

       <div class="flex items-center gap-3 my-2 text-[10px] text-gray-500 uppercase tracking-wider">
          <div class="flex-1 h-px bg-gray-800"></div>
          <span>или</span>
          <div class="flex-1 h-px bg-gray-800"></div>
       </div>

       <button (click)="enterDemo()" class="w-full py-2.5 px-4 bg-gray-800 hover:bg-gray-700 text-gray-200 border border-gray-700 hover:border-indigo-500/50 font-semibold text-sm rounded-lg transition-colors flex items-center justify-center gap-2">
          <svg class="w-4 h-4 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"/></svg>
          Попробовать в демо-режиме
       </button>
       <p class="text-[11px] text-gray-500 mt-2">Демо — это синтетический стрим с готовыми моментами и AI-описаниями. Без регистрации, ничего не сохраняется.</p>

       <p class="text-[10px] text-gray-600 mt-4 leading-relaxed">
         Peakster не аффилирован с Twitch Interactive, Inc.
       </p>

       @if (useEmulators()) {
         <div class="mt-4 p-3 border border-amber-700/40 bg-amber-950/30 rounded-lg text-left">
            <p class="text-[10px] font-bold text-amber-400 uppercase tracking-wider">🔧 Emulator mode</p>
            <button (click)="loginAnonymousDev()" class="mt-2 w-full py-2 px-3 bg-amber-700 hover:bg-amber-600 text-white text-xs rounded transition-colors">
              Войти анонимно (тест)
            </button>
            <p class="text-[10px] text-amber-200/70 mt-2 leading-tight">UID будет создан автоматически. Чтобы сделать юзера Pro — открой Emulator UI (http://127.0.0.1:4000), Firestore, найди users/&lt;uid&gt;, поставь isPro=true, перезагрузи страницу.</p>
         </div>
       }
    </div>
  </div>
}

<!-- Consent migration banner (legacy users without an up-to-date consent record).
     Soft mode: dismissable in-flow until 1 июня 2026; hard blocker after that
     (rendered separately below). -->
@if (currentUser() && needsConsentRefresh() && !isConsentBlocking()) {
  <div class="fixed top-0 left-0 right-0 z-[90] bg-amber-950/95 border-b border-amber-700/40 backdrop-blur-sm shadow-lg">
    <div class="max-w-5xl mx-auto px-4 py-2.5 flex items-center gap-3 text-sm">
      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-amber-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
      <div class="flex-1 text-amber-100 leading-snug">
        Мы опубликовали
        <a href="/privacy" target="_blank" rel="noopener" class="underline decoration-dotted hover:text-white">Политику конфиденциальности</a>
        и
        <a href="/terms" target="_blank" rel="noopener" class="underline decoration-dotted hover:text-white">Пользовательское соглашение</a>.
        Подтвердите согласие до 1 июня 2026 г., чтобы продолжить пользоваться сервисом.
      </div>
      <button (click)="acceptConsent()"
              [disabled]="isRecordingConsent()"
              class="px-3 py-1.5 bg-amber-600 hover:bg-amber-500 disabled:opacity-60 disabled:cursor-wait text-white text-xs font-semibold rounded transition-colors shrink-0">
        @if (isRecordingConsent()) {
          Сохраняем...
        } @else {
          Подтверждаю
        }
      </button>
    </div>
  </div>
}

<!-- Consent blocking modal (legacy users after grace deadline). Same visual
     weight as the auth overlay; the user must accept or sign out to proceed. -->
@if (currentUser() && isConsentBlocking()) {
  <div class="fixed inset-0 z-[100] bg-gray-950/95 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
    <div class="max-w-md w-full bg-gray-900 border border-amber-700/40 rounded-xl shadow-2xl shadow-amber-900/20 p-6 space-y-4">
      <div class="flex items-center gap-3">
        <div class="w-10 h-10 rounded-full bg-amber-500/15 flex items-center justify-center shrink-0">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
        </div>
        <h2 class="text-lg font-bold text-gray-100">Требуется согласие</h2>
      </div>
      <p class="text-sm text-gray-300 leading-relaxed">
        Чтобы продолжить пользоваться Peakster, ознакомьтесь с актуальными редакциями документов и подтвердите согласие на обработку персональных данных, включая их трансграничную передачу в США (Google LLC).
      </p>
      <div class="flex flex-col gap-2">
        <a href="/terms" target="_blank" rel="noopener" class="block text-center px-3 py-2 bg-gray-800 hover:bg-gray-700 text-indigo-300 hover:text-indigo-200 text-sm rounded transition-colors">
          Открыть Пользовательское соглашение
        </a>
        <a href="/privacy" target="_blank" rel="noopener" class="block text-center px-3 py-2 bg-gray-800 hover:bg-gray-700 text-indigo-300 hover:text-indigo-200 text-sm rounded transition-colors">
          Открыть Политику конфиденциальности
        </a>
      </div>
      <p class="text-[11px] text-gray-500 leading-relaxed">
        Нажимая «Согласен», вы подтверждаете, что вам исполнилось 18 лет, и принимаете обе редакции в полном объёме.
      </p>
      <div class="flex gap-2">
        <button (click)="logout()"
                class="px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-300 text-sm font-medium rounded transition-colors">
          Выйти
        </button>
        <button (click)="acceptConsent()"
                [disabled]="isRecordingConsent()"
                class="flex-1 px-4 py-2.5 bg-amber-600 hover:bg-amber-500 disabled:opacity-60 disabled:cursor-wait text-white font-semibold text-sm rounded transition-colors">
          @if (isRecordingConsent()) {
            Сохраняем...
          } @else {
            Согласен
          }
        </button>
      </div>
    </div>
  </div>
}

<!-- Global Container — flex-1 inside the host's flex-col layout, so it takes
     all remaining height after the inline banner (when present). w-screen is
     OK because the host already constrains width. min-h-0 is required for the
     flex-1 + overflow-hidden combo to work without children growing beyond
     bounds. -->
<div class="flex flex-1 min-h-0 w-screen overflow-hidden bg-gray-950 text-gray-200 relative">

  <!-- Mobile-only hamburger — sidebar is a translated-off-screen drawer below
       md, so a control living inside it can't be tapped to reopen it. -->
  <button (click)="toggleSidebar()"
          class="md:hidden fixed top-3 left-3 z-50 p-2 rounded-lg bg-gray-900/90 border border-gray-700 text-gray-300 shadow-lg"
          [class.hidden]="!isSidebarCollapsed()"
          appTooltip="Открыть панель">
    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" /></svg>
  </button>

  <!-- Mobile-only backdrop — closes the drawer on outside tap. Desktop never
       shows this (md:hidden), so the icon-rail toggle behavior is untouched. -->
  <div class="md:hidden fixed inset-0 z-30 bg-black/60"
       [class.hidden]="isSidebarCollapsed()"
       (click)="toggleSidebar()"></div>

  <!-- Left Sidebar (Control Panel). Below md: off-canvas drawer (fixed,
       translate-toggled). At md+: identical to the original in-flow icon-rail/
       expanded behavior — every mobile-only class is countered by an md: class
       restoring today's exact layout, so desktop is provably unaffected. -->
  <aside [class]="isSidebarCollapsed()
           ? 'fixed inset-y-0 left-0 z-40 w-80 -translate-x-full md:relative md:translate-x-0 md:w-14 flex-shrink-0 flex flex-col border-r border-gray-800 bg-gray-900/50 backdrop-blur-sm md:z-20 transition-transform md:transition-[width] duration-200'
           : 'fixed inset-y-0 left-0 z-40 w-80 translate-x-0 md:relative md:w-80 flex-shrink-0 flex flex-col border-r border-gray-800 bg-gray-900/50 backdrop-blur-sm md:z-20 transition-transform md:transition-[width] duration-200'">

    @if (!isSidebarCollapsed()) {
      <!-- Branding (expanded) -->
      <div class="p-6 border-b border-gray-800 flex items-center justify-between">
        <h1 class="text-xl font-bold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">
          Peakster
        </h1>
        <div class="flex items-center gap-1">
          <button (click)="openHelpModal()" appTooltip="Справка" class="text-gray-500 hover:text-indigo-400 p-2 transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
            </svg>
          </button>
          <button (click)="toggleSidebar()" appTooltip="Свернуть панель" class="text-gray-500 hover:text-indigo-400 p-2 transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" /></svg>
          </button>
        </div>
      </div>

    } @else {
      <!-- Collapsed icon-rail header -->
      <div class="flex flex-col items-center gap-1 py-3 border-b border-gray-800">
        <button (click)="toggleSidebar()" appTooltip="Развернуть панель" class="p-2 text-indigo-400 hover:bg-gray-800 rounded-lg transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 5l7 7-7 7M5 5l7 7-7 7" /></svg>
        </button>
        <button (click)="openHelpModal()" appTooltip="Справка" class="text-gray-500 hover:text-indigo-400 p-2 transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
          </svg>
        </button>
      </div>
    }

    <!-- Demo-mode "Login" slot — same position as the user profile so the layout
         feels consistent. Visible only when demo flag is on AND no real user. -->
    @if (demoService.isDemoMode() && !currentUser()) {
      <div class="border-b border-gray-800 bg-gray-900/80 shrink-0">
        @if (!isSidebarCollapsed()) {
          <div class="p-3 flex flex-col gap-2">
            <div class="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-amber-400">
              <span class="inline-block w-2 h-2 rounded-full bg-amber-400 animate-pulse"></span>
              Демо-режим
            </div>
            <button (click)="loginWithTwitch()"
                    class="w-full py-2 px-3 rounded-lg bg-[#9146FF] hover:bg-[#7b32e6] text-white font-bold text-sm shadow shadow-[#9146FF]/30 flex items-center justify-center gap-2 transition-transform hover:scale-[1.02] active:scale-95">
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/></svg>
              Войти через Twitch
            </button>
            <p class="text-[11px] text-gray-500 leading-tight">Анализируйте свой стрим — добавьте канал, выберите VOD.</p>
          </div>
        } @else {
          <button (click)="loginWithTwitch()"
                  class="w-full p-2 flex items-center justify-center hover:bg-gray-800/50 transition-colors"
                  appTooltip="Войти через Twitch (выйти из демо)">
            <div class="w-9 h-9 rounded-full bg-[#9146FF] flex items-center justify-center text-white shadow shadow-[#9146FF]/40">
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/></svg>
            </div>
          </button>
        }
      </div>
    }

    <!-- User Profile / Settings Dropdown -->
    @if (currentUser()) {
      <div class="border-b border-gray-800 bg-gray-900/80 shrink-0 relative">
        @if (!isSidebarCollapsed()) {
          <!-- Expanded: full row, click to open dropdown -->
          <button (click)="toggleAvatarMenu($event)"
                  class="w-full p-3 flex items-center gap-3 hover:bg-gray-800/50 transition-colors text-left">
            @if (userProfile()?.photoURL) {
              <img [src]="userProfile()?.photoURL" class="w-10 h-10 rounded-full border border-gray-700 bg-gray-800 shrink-0">
            } @else {
              <div class="w-10 h-10 rounded-full bg-gray-800 border border-gray-700 flex items-center justify-center text-gray-400 font-bold uppercase shrink-0">
                {{ userProfile()?.displayName?.charAt(0) || 'U' }}
              </div>
            }
            <div class="flex-1 min-w-0">
              <p class="text-sm font-bold text-gray-200 truncate" [appTooltip]="userProfile()?.displayName">{{ userProfile()?.displayName || 'Пользователь' }}</p>
              <div class="mt-1 flex items-center gap-2">
                @if (userProfile()?.isPro || userProfile()?.role === 'admin') {
                  <span class="px-2 py-0.5 rounded text-[10px] font-bold bg-gradient-to-r from-purple-500 to-indigo-500 text-white shadow shadow-purple-900/50">PRO</span>
                } @else {
                  <span class="px-2 py-0.5 rounded text-[10px] font-bold bg-gray-800 text-gray-400 border border-gray-700">FREE</span>
                }
                @if (isSyncEligible()) {
                  <span class="inline-flex items-center gap-1 text-[10px] text-gray-400" [appTooltip]="syncIndicatorTooltip()">
                    <span class="inline-block w-2 h-2 rounded-full" [class]="syncIndicatorColor()"></span>
                    <span>Sync</span>
                  </span>
                }
              </div>
            </div>
            <svg class="h-3 w-3 text-gray-500 transition-transform shrink-0" [class.rotate-180]="isAvatarMenuOpen()" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" /></svg>
          </button>
        } @else {
          <!-- Collapsed: just avatar, click to open dropdown -->
          <button (click)="toggleAvatarMenu($event)"
                  class="w-full p-2 flex items-center justify-center hover:bg-gray-800/50 transition-colors relative"
                  [appTooltip]="userProfile()?.displayName || 'Пользователь'">
            @if (userProfile()?.photoURL) {
              <img [src]="userProfile()?.photoURL" class="w-9 h-9 rounded-full border border-gray-700 bg-gray-800">
            } @else {
              <div class="w-9 h-9 rounded-full bg-gray-800 border border-gray-700 flex items-center justify-center text-gray-400 font-bold uppercase text-sm">
                {{ userProfile()?.displayName?.charAt(0) || 'U' }}
              </div>
            }
            @if (isSyncEligible()) {
              <span class="absolute bottom-1 right-1 w-2 h-2 rounded-full ring-2 ring-gray-900" [class]="syncIndicatorColor()" [appTooltip]="syncIndicatorTooltip()"></span>
            }
          </button>
        }

        @if (isAvatarMenuOpen()) {
          <div class="absolute z-30 bg-gray-900 border border-gray-700 rounded-lg shadow-2xl animate-fade-in"
               [class.left-2]="!isSidebarCollapsed()"
               [class.right-2]="!isSidebarCollapsed()"
               [class.top-full]="!isSidebarCollapsed()"
               [class.mt-1]="!isSidebarCollapsed()"
               [class.left-full]="isSidebarCollapsed()"
               [class.ml-2]="isSidebarCollapsed()"
               [class.top-2]="isSidebarCollapsed()"
               [class.w-64]="isSidebarCollapsed()"
               (click)="$event.stopPropagation()">
            <!-- Subscription -->
            <div class="p-3 border-b border-gray-800">
              @if (userProfile()?.isPro || userProfile()?.role === 'admin') {
                <div class="flex items-center justify-between">
                  <span class="text-xs text-gray-400">Подписка</span>
                  <span class="px-2 py-0.5 rounded text-[10px] font-bold bg-gradient-to-r from-purple-500 to-indigo-500 text-white">PRO</span>
                </div>
                <p class="text-[10px] text-gray-500 mt-1">Активна</p>
              } @else {
                <p class="text-xs text-gray-400 mb-2">Тариф: <span class="text-gray-300">FREE</span></p>
                <button onclick="window.open('https://boosty.to', '_blank')"
                        class="w-full py-2 px-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white rounded text-xs font-bold transition-colors">
                  Перейти на PRO
                </button>
              }
            </div>

            <!-- Interface Scale (moved from sidebar bottom) -->
            <div class="p-3 border-b border-gray-800 space-y-2">
              <div class="flex items-center justify-between mb-2">
                <span class="text-xs font-semibold text-gray-400 uppercase tracking-wider">Интерфейс</span>
                <span class="text-xs text-gray-500">{{ interfaceScale() }}%</span>
              </div>
              <div class="flex bg-gray-800/50 rounded p-1 border border-gray-700">
                <button (click)="setScale(90)" [class]="getScaleClass(90)" class="flex-1 py-1 text-xs rounded transition-colors" appTooltip="Компактный">Aa</button>
                <button (click)="setScale(100)" [class]="getScaleClass(100)" class="flex-1 py-1 text-xs rounded transition-colors" appTooltip="Стандартный">Aa</button>
                <button (click)="setScale(110)" [class]="getScaleClass(110)" class="flex-1 py-1 text-sm rounded transition-colors" appTooltip="Крупный">Aa</button>
                <button (click)="setScale(125)" [class]="getScaleClass(125)" class="flex-1 py-1 text-base rounded transition-colors" appTooltip="Очень крупный">Aa</button>
              </div>

              <div class="flex items-center justify-between pt-1">
                <span class="text-[11px] text-gray-400">Тепловая карта</span>
                <div class="flex bg-gray-800/50 rounded p-0.5 border border-gray-700">
                  <button (click)="setHeatmapScale('log')" [class]="getHeatmapScaleClass('log')" class="px-2 py-0.5 text-[11px] rounded transition-colors" appTooltip="Логарифмическая — компромисс между низкой и высокой активностью">лог</button>
                  <button (click)="setHeatmapScale('linear')" [class]="getHeatmapScaleClass('linear')" class="px-2 py-0.5 text-[11px] rounded transition-colors" appTooltip="Линейная — строгая пропорциональность к maxActivity">лин</button>
                </div>
              </div>
            </div>

            <!-- StreamMarkers connections (Pro only) -->
            @if (userProfile()?.isPro || userProfile()?.role === 'admin') {
              <div class="p-2 border-b border-gray-800">
                <button (click)="openMarkerSourcesModal()" class="w-full text-left flex items-center gap-2 p-2 hover:bg-gray-800 rounded text-xs text-gray-300 hover:text-cyan-300 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" /></svg>
                  Подключения StreamMarkers
                </button>
              </div>
            }

            <!-- Admin panel (admin role only — backend re-validates every request) -->
            @if (isAdmin()) {
              <div class="p-2 border-b border-gray-800">
                <button (click)="openAdminPanel()" class="w-full text-left flex items-center gap-2 p-2 hover:bg-gray-800 rounded text-xs text-gray-300 hover:text-purple-300 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>
                  Админ-панель
                </button>
              </div>
            }

            <!-- Logout -->
            <div class="p-2">
              <button (click)="logout()" class="w-full text-left flex items-center gap-2 p-2 hover:bg-gray-800 rounded text-xs text-gray-300 hover:text-red-400 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                Выйти
              </button>
            </div>
          </div>
        }
      </div>
    }

    <!-- Library Button (placed under user profile) -->
    <div class="border-b border-gray-800">
      @if (!isSidebarCollapsed()) {
        <div class="px-4 py-3">
          <button (click)="toggleLibrary()"
                  class="w-full flex items-center gap-2 py-2 px-3 rounded-lg transition-colors text-sm font-medium"
                  [class]="isLibraryOpen() ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-900/30' : 'bg-gray-800 hover:bg-gray-700 text-gray-200'">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
            </svg>
            <span>Библиотека каналов</span>
          </button>
        </div>
      } @else {
        <div class="flex justify-center py-2">
          <button (click)="toggleLibrary()"
                  class="p-2 rounded-lg transition-colors"
                  [class]="isLibraryOpen() ? 'bg-indigo-600 text-white' : 'text-gray-400 hover:bg-gray-800 hover:text-white'"
                  appTooltip="Библиотека каналов">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
            </svg>
          </button>
        </div>
      }
    </div>

    @if (!isSidebarCollapsed()) {
    <!-- Scrollable Sidebar Content -->
    <div class="flex-1 overflow-y-auto p-4 space-y-6 scrollbar-thin scrollbar-thumb-gray-700">

      <!-- Step 1: Input (collapsible after isProcessed) -->
      <section>
        @if (isSourceSectionCollapsed() && (fileName() || streamerName())) {
          <button (click)="isSourceSectionCollapsed.set(false)"
                  class="w-full text-left p-2.5 bg-gray-800/30 rounded-lg border border-gray-700/50 hover:bg-gray-800 transition-colors">
            <div class="flex items-center justify-between gap-2 text-xs">
              <div class="flex items-center gap-2 shrink-0">
                <span class="flex items-center justify-center w-5 h-5 rounded-full bg-indigo-900/50 text-indigo-400 text-xs">1</span>
                <span class="font-semibold text-gray-400 uppercase tracking-wider">Источник</span>
              </div>
              <span class="text-gray-300 truncate" [appTooltip]="fileName() || streamerName()">
                {{ fileName() || streamerName() }}
              </span>
            </div>
          </button>
        } @else {
        <div class="flex items-center gap-2 mb-2 text-sm font-semibold text-gray-400 uppercase tracking-wider">
          <span class="flex items-center justify-center w-5 h-5 rounded-full bg-indigo-900/50 text-indigo-400 text-xs">1</span>
          <span class="flex-1">Источник</span>
          @if (fileName() || streamerName()) {
            <button (click)="isSourceSectionCollapsed.set(true)" class="text-gray-500 hover:text-gray-300 p-1 rounded transition-colors" appTooltip="Свернуть">
              <svg class="h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" /></svg>
            </button>
          }
        </div>

        <!-- Source Type Selector — JSON tab hidden in demo (no own-data uploads) -->
        @if (!demoService.isDemoMode()) {
        <div class="flex p-1 bg-gray-800 rounded-lg mb-3">
            <button (click)="setSourceMode('url')"
                    class="flex-1 py-1.5 text-xs font-medium rounded-md transition-all duration-200"
                    [class]="sourceMode() === 'url' ? 'bg-indigo-900/50 text-indigo-200 shadow-sm' : 'text-gray-400 hover:text-gray-300'">
                Ссылка (Auto)
            </button>
            <button (click)="setSourceMode('file')"
                    class="flex-1 py-1.5 text-xs font-medium rounded-md transition-all duration-200"
                    [class]="sourceMode() === 'file' ? 'bg-gray-700 text-white shadow-sm' : 'text-gray-400 hover:text-gray-300'">
                Файл (JSON)
            </button>
        </div>
        }

        @if (sourceMode() === 'file' && !demoService.isDemoMode()) {
            @if (!fileName()) {
            <label class="flex flex-col items-center justify-center w-full h-24 border-2 border-gray-700 border-dashed rounded-lg cursor-pointer bg-gray-800/30 hover:bg-gray-800 hover:border-indigo-500 transition-all group">
                <div class="flex flex-col items-center justify-center pt-5 pb-6">
                    <svg class="w-6 h-6 mb-1 text-gray-500 group-hover:text-indigo-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path></svg>
                    <p class="text-xs text-gray-500 group-hover:text-gray-300">Выбрать файл</p>
                </div>
                <input type="file" class="hidden" accept=".json,.csv" (change)="onFileSelected($event)" />
            </label>
            } @else {
            <div class="bg-gray-800/50 rounded-lg p-3 border border-gray-700 text-xs animate-fade-in">
                <div class="flex justify-between items-start">
                    <div>
                        <p class="font-bold text-gray-200 truncate max-w-[180px]" [appTooltip]="fileName()">{{ fileName() }}</p>
                        @if (streamerName()) {
                            <p class="text-indigo-400 mt-1">{{ streamerName() }}</p>
                            <p class="text-gray-500 truncate">{{ videoGame() }}</p>
                        }
                    </div>
                    <label class="cursor-pointer text-gray-500 hover:text-white" appTooltip="Заменить файл">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                        <input type="file" class="hidden" accept=".json,.csv" (change)="onFileSelected($event)" />
                    </label>
                </div>
            </div>
            }
        } @else {
            <!-- URL Input Mode -->
             <div class="space-y-2 animate-fade-in">
                <div class="relative">
                    <input type="text" 
                        [value]="twitchUrl()" 
                        (input)="twitchUrl.set($any($event.target).value)"
                        (keydown.enter)="loadFromUrl()"
                        placeholder="https://www.twitch.tv/videos/..." 
                        class="w-full bg-gray-900/50 border border-gray-700 rounded-lg py-2 px-3 text-xs text-gray-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none placeholder-gray-600">
                    
                    @if(twitchUrl()) {
                        <button (click)="twitchUrl.set('')" class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
                             <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" /></svg>
                        </button>
                    }
                </div>
                
                <button (click)="loadFromUrl()"
                        [disabled]="!isValidTwitchUrl() || globalIsLoading()"
                        class="w-full py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-semibold shadow-lg shadow-indigo-900/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2">
                    @if(isLoading() && loadingMessage().includes('Twitch')) {
                        <svg class="animate-spin h-3 w-3" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                        <span>Загрузка чата...</span>
                    } @else {
                        <span>Скачать и анализировать</span>
                    }
                </button>
                <p class="text-xs text-gray-500 text-center leading-tight">
                    Автоматически скачивает чат с серверов Twitch. Может занять время для длинных стримов.
                </p>
             </div>
        }

        <!-- CSV Form Modal/Inline (Legacy) -->
        @if (isCsvMode()) {
           <div class="mt-3 p-3 bg-gray-800/80 rounded-lg border border-gray-600 animate-fade-in text-xs space-y-2">
             <input type="text" [value]="csvVodId()" (input)="csvVodId.set($any($event.target).value)" placeholder="ID видео или ссылка" class="w-full bg-gray-900 border border-gray-600 rounded px-2 py-1 text-gray-200 focus:border-indigo-500 outline-none">
             <input type="text" [value]="csvStreamerName()" (input)="csvStreamerName.set($any($event.target).value)" placeholder="Имя стримера" class="w-full bg-gray-900 border border-gray-600 rounded px-2 py-1 text-gray-200 focus:border-indigo-500 outline-none">
             <button (click)="handleCsvSubmit()" [disabled]="!csvVodId() || globalIsLoading()" class="w-full bg-green-700 hover:bg-green-600 text-white font-semibold py-1 rounded transition-colors disabled:opacity-50">Обработать</button>
           </div>
        }
        }
      </section>

      <!-- Step 2: Controls -->
      @if (isProcessed()) {
        <section class="animate-fade-in">
          <div class="flex items-center gap-2 mb-2 text-sm font-semibold text-gray-400 uppercase tracking-wider">
            <span class="flex items-center justify-center w-5 h-5 rounded-full bg-indigo-900/50 text-indigo-400 text-xs">2</span>
            Анализ
          </div>

          <div class="bg-gray-800/30 p-3 rounded-lg border border-gray-700/50 space-y-4">
             <!-- Sensitivity -->
             <div>
                <div class="flex justify-between items-center mb-1">
                    <label class="text-sm text-gray-300">Чувствительность</label>
                    <span class="text-sm font-mono text-indigo-400 font-bold">{{ peakMessageThreshold() }}</span>
                </div>
                <input type="range" [min]="MIN_MESSAGES_FOR_PEAK" [max]="maxActivity()" step="1" [value]="peakMessageThreshold()" (input)="onThresholdChange($event)" (pointerdown)="sliderGestureStart('threshold', $event)" (pointerup)="sliderGestureEnd($event)" (pointercancel)="sliderGestureEnd($event)" [attr.data-tour]="ANCHORS.THRESHOLD_SLIDER" class="w-full h-1.5 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-500">
                <p class="text-xs text-indigo-300 mt-2 font-medium">Найдено: {{ potentialHighlightsCount() }}@if (activeRegion()) {<span class="text-amber-300"> · в области {{ potentialHighlightsInRegionCount() }}</span>}</p>
             </div>

             <!-- AI Actions -->
             <div class="space-y-2">
                <div class="relative">
                <button (click)="analyzeHighlightsWithAI()"
                        [disabled]="isAnalyzingQuick() || analyzableQuickCount() === 0 || (isLoading() && !isChatDownloading()) || aiQuickExhausted() || aiBlockedByMissingChat()"
                        [attr.data-tour]="ANCHORS.BTN_QUICK_AI"
                        [appTooltip]="aiBlockedByMissingChat() ? aiQuickTooltip() : (aiQuickExhausted() ? aiQuickTooltip() : (parallelDownloadActive() ? 'Дождитесь окончания параллельной загрузки чата.' : (isChatDownloading() && analyzableQuickCount() > 0 ? 'Анализ доступен по уже скачанному. Последний кластер дождётся окончания загрузки.' : aiQuickTooltip())))"
                        [class]="quickRetryInfo() ? 'w-full py-2 bg-amber-700 hover:bg-amber-600 text-white rounded text-xs font-semibold shadow-lg shadow-amber-900/30 ring-2 ring-amber-400/40 disabled:opacity-80 transition-all flex items-center justify-center gap-2' : 'w-full py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded text-xs font-semibold shadow-lg shadow-indigo-900/20 disabled:opacity-50 transition-all flex items-center justify-center gap-2'">
                    @if (quickRetryInfo(); as r) {
                       <svg class="h-3 w-3" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L1 21h22L12 2zm0 6l7.53 13H4.47L12 8zm-1 4v4h2v-4h-2zm0 5v2h2v-2h-2z"/></svg>
                       <span>Gemini перегружен · повтор через {{ r.remainingSec }}с ({{ r.attempt }}/{{ r.maxAttempts }})</span>
                    } @else if (isAnalyzingQuick()) {
                       <svg class="animate-spin h-3 w-3" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                       <span>Анализирую пики...</span>
                    } @else if (isLoading() && !isChatDownloading()) {
                       <svg class="animate-spin h-3 w-3" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                       <span>Ожидание...</span>
                    } @else {
                       <span>Анализ пиков ({{ analyzableQuickCount() }})</span>
                       @if (aiQuickBadge(); as b) {
                          <span class="text-[10px] px-1.5 py-0.5 rounded font-mono"
                                [class.bg-amber-500]="aiQuickQuotaLow()"
                                [class.bg-opacity-30]="aiQuickQuotaLow()"
                                [class.bg-indigo-900]="!aiQuickQuotaLow()"
                                [class.bg-opacity-50]="!aiQuickQuotaLow()">{{ b }}</span>
                       }
                    }
                </button>
                @if (activeRegion()) {
                   <app-region-scope-badge [text]="regionScanBadgeText()"></app-region-scope-badge>
                }
                </div>

                <div class="relative">
                <button (click)="runDeepAnalysis()"
                        [disabled]="isAnalyzingDeep() || isLoading() || isLive() || aiDeepExhausted() || aiBlockedByMissingChat()"
                        [attr.data-tour]="ANCHORS.BTN_DEEP_AI"
                        [appTooltip]="aiBlockedByMissingChat() ? aiDeepTooltip() : (aiDeepExhausted() ? aiDeepTooltip() : (isLive() ? 'Глубокий анализ доступен после окончания стрима — нужны полные данные для распределения моментов по всему VOD' : aiDeepTooltip()))"
                        [class]="deepRetryInfo() ? 'w-full py-2 bg-amber-900/50 hover:bg-amber-800/50 border border-amber-600 text-amber-100 rounded text-xs font-semibold ring-2 ring-amber-400/40 disabled:opacity-80 transition-all flex items-center justify-center gap-2' : 'w-full py-2 bg-purple-900/50 hover:bg-purple-800/50 border border-purple-700 text-purple-200 rounded text-xs font-semibold disabled:opacity-50 transition-all flex items-center justify-center gap-2'">
                    @if (deepRetryInfo(); as r) {
                       <svg class="h-3 w-3" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L1 21h22L12 2zm0 6l7.53 13H4.47L12 8zm-1 4v4h2v-4h-2zm0 5v2h2v-2h-2z"/></svg>
                       <span>Gemini перегружен · повтор через {{ r.remainingSec }}с ({{ r.attempt }}/{{ r.maxAttempts }})</span>
                    } @else if (isAnalyzingDeep()) {
                       <svg class="animate-spin h-3 w-3" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                       <span>Deep Dive...</span>
                    } @else if (isLive()) {
                       <span class="inline-block w-2 h-2 rounded-full bg-red-500 animate-pulse" aria-hidden="true"></span>
                       <span>Доступно после окончания стрима</span>
                    } @else if (isLoading()) {
                       <svg class="animate-spin h-3 w-3" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                       <span>Ожидание...</span>
                    } @else {
                        <span>Глубокий поиск</span>
                        @if (aiDeepBadge(); as b) {
                           <span class="text-[10px] px-1.5 py-0.5 rounded font-mono"
                                 [class.bg-amber-500]="aiDeepQuotaLow()"
                                 [class.bg-opacity-30]="aiDeepQuotaLow()"
                                 [class.bg-purple-900]="!aiDeepQuotaLow()"
                                 [class.bg-opacity-60]="!aiDeepQuotaLow()">{{ b }}</span>
                        }
                    }
                </button>
                @if (activeRegion()) {
                   <app-region-scope-badge [text]="regionScanBadgeText()"></app-region-scope-badge>
                }
                </div>
             </div>

             <!-- Filters (Collapsible) -->
             <div class="pt-3 border-t border-gray-700/50">
                <button (click)="isFiltersExpanded.set(!isFiltersExpanded())" 
                        class="flex items-center justify-between w-full text-xs font-semibold text-gray-400 uppercase tracking-wider hover:text-gray-200 transition-colors">
                   <span>Фильтры</span>
                   <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 transition-transform duration-200" [class.rotate-180]="isFiltersExpanded()" viewBox="0 0 20 20" fill="currentColor">
                     <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                   </svg>
                </button>

                @if (isFiltersExpanded()) {
                   <div class="mt-3 space-y-3 animate-fade-in">
                      <!-- Filters re-filter the raw chat: without it (cloud copy, chat not downloaded on
                           this device) the heatmap would collapse — and the change couldn't be undone. -->
                      @if (needsChatRedownload()) {
                         <p class="text-[11px] text-amber-300">Фильтры недоступны, пока чат не скачан.</p>
                      }
                      <!-- Hide Subs -->
                      <label class="flex items-center gap-2 cursor-pointer select-none group">
                         <input type="checkbox" 
                                [checked]="filterSettings().hideSubs" 
                                [disabled]="needsChatRedownload()"
                                (change)="toggleHideSubs()"
                                class="disabled:opacity-40 rounded bg-gray-700 border-gray-600 text-indigo-500 focus:ring-offset-gray-900 focus:ring-1">
                         <span class="text-xs text-gray-300 group-hover:text-white transition-colors">Скрыть сабки/подарки</span>
                      </label>
 
                      <!-- Ignored Users -->
                      <div>
                         <div class="text-[10px] text-gray-500 mb-1.5 uppercase font-semibold">Игнор пользователей</div>
                         <div class="flex gap-1 mb-2">
                            <input #userInput type="text" placeholder="Никнейм..." [disabled]="needsChatRedownload()"
                                   class="disabled:opacity-40 flex-1 bg-gray-900/50 border border-gray-700 rounded px-2 py-1.5 text-xs text-gray-200 focus:border-indigo-500 outline-none placeholder-gray-600"
                                   (keydown.enter)="addIgnoredUser(userInput.value); userInput.value=''">
                            <button (click)="addIgnoredUser(userInput.value); userInput.value=''" [disabled]="needsChatRedownload()"
                                    class="disabled:opacity-40 px-2 py-1 bg-gray-800 border border-gray-700 rounded text-gray-400 hover:text-white hover:bg-gray-700 transition-colors">
                              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clip-rule="evenodd" /></svg>
                            </button>
                         </div>
                         <!-- Suggested Spammers -->
                         @if (topChatters().length > 0) {
                            <div class="flex flex-wrap gap-1 mb-2">
                               @for (chatter of topChatters(); track chatter.name) {
                                  <button (click)="addIgnoredUser(chatter.name)" [disabled]="needsChatRedownload()" class="disabled:opacity-40 text-[10px] px-1.5 py-0.5 rounded bg-gray-800 border border-gray-700 text-gray-400 hover:text-indigo-300 hover:border-indigo-500/50 transition-all" [appTooltip]="'Добавить в игнор (' + chatter.count + ' сообщений)'">
                                    {{ chatter.name }}
                                  </button>
                               }
                            </div>
                         }
                         <!-- Active List -->
                         @if (filterSettings().ignoredUsers.length) {
                            <div class="flex flex-wrap gap-1.5">
                                @for (user of filterSettings().ignoredUsers; track user) {
                                    <span class="inline-flex items-center gap-1 px-1.5 py-0.5 bg-red-900/20 border border-red-800/50 rounded text-[10px] text-red-300">
                                        {{ user }}
                                        <button (click)="removeIgnoredUser(user)" [disabled]="needsChatRedownload()" class="disabled:opacity-40 hover:text-white focus:outline-none">
                                           <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" /></svg>
                                        </button>
                                    </span>
                                }
                            </div>
                         }
                      </div>
 
                      <!-- Keywords -->
                      <div>
                         <div class="text-[10px] text-gray-500 mb-1.5 uppercase font-semibold">Бан-слова</div>
                         <div class="flex gap-1 mb-2">
                            <input #keywordInput type="text" placeholder="Слово..." [disabled]="needsChatRedownload()"
                                   class="disabled:opacity-40 flex-1 bg-gray-900/50 border border-gray-700 rounded px-2 py-1.5 text-xs text-gray-200 focus:border-indigo-500 outline-none placeholder-gray-600"
                                   (keydown.enter)="addKeyword(keywordInput.value); keywordInput.value=''">
                            <button (click)="addKeyword(keywordInput.value); keywordInput.value=''" [disabled]="needsChatRedownload()"
                                    class="disabled:opacity-40 px-2 py-1 bg-gray-800 border border-gray-700 rounded text-gray-400 hover:text-white hover:bg-gray-700 transition-colors">
                              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clip-rule="evenodd" /></svg>
                            </button>
                         </div>
                         @if (filterSettings().keywords.length) {
                            <div class="flex flex-wrap gap-1.5">
                                @for (word of filterSettings().keywords; track word) {
                                    <span class="inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-700/50 border border-gray-600/50 rounded text-[10px] text-gray-300">
                                        {{ word }}
                                        <button (click)="removeKeyword(word)" [disabled]="needsChatRedownload()" class="disabled:opacity-40 hover:text-white focus:outline-none">
                                           <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" /></svg>
                                        </button>
                                    </span>
                                }
                            </div>
                         }
                      </div>
                   </div>
                }
             </div>

          </div>
        </section>

        <!-- Step 3: Export -->
        <section class="animate-fade-in">
          <div class="flex items-center gap-2 mb-2 text-sm font-semibold text-gray-400 uppercase tracking-wider">
            <span class="flex items-center justify-center w-5 h-5 rounded-full bg-indigo-900/50 text-indigo-400 text-xs">3</span>
            Экспорт ({{ totalEstimatedSizeMb() }} MB)
            @if (activeRegion()) {
              <app-region-scope-badge [inline]="true" [text]="regionExportBadgeText()"></app-region-scope-badge>
            }
          </div>
          <div class="space-y-2">
             <!-- Top Row: Txt / Batch Script. TXT hidden in demo (would export fake demo data). -->
             @if (!demoService.isDemoMode()) {
             <div class="grid grid-cols-2 gap-2">
                <button (click)="exportClipsTxt()" class="px-2 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded text-xs text-gray-300 transition-colors" appTooltip="Скачать отчет TXT">
                  TXT Отчет
                </button>
                <button (click)="exportDownloadScript('windows')" class="px-2 py-2 bg-blue-900/30 hover:bg-blue-800/40 border border-blue-800 rounded text-xs text-blue-300 transition-colors" appTooltip="Скачать .bat скрипт (Бесплатно & Быстро)">
                  Win .BAT
                </button>
             </div>
             }
             
             <!-- Cloud Batch Download -->
             <button (click)="downloadBatchZip()" [disabled]="!isFirebaseReady() || isBatchDownloading()" class="w-full py-2 bg-green-700/80 hover:bg-green-600 text-white rounded text-xs font-semibold shadow-lg shadow-green-900/20 disabled:opacity-50 transition-all flex items-center justify-center gap-2 border border-green-600/50">
                @if (isBatchDownloading()) {
                  @if (batchDownloadQueued()) {
                    <svg class="animate-pulse h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    <span>В очереди... серверы загружены</span>
                  } @else {
                    <svg class="animate-spin h-3 w-3" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                    <span>Создание архива... {{ batchDownloadProgress() }}%</span>
                  }
                } @else {
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                    <span>Скачать всё архивом (ZIP)</span>
                }
             </button>

             @if (zipOversizedClips(); as oversized) {
               @if (oversized.length > 0) {
                 <div class="p-2.5 bg-amber-900/25 border border-amber-800/50 rounded text-xs text-amber-200 space-y-1.5">
                   <div class="flex items-start gap-1.5">
                     <svg class="h-3.5 w-3.5 shrink-0 mt-0.5 text-amber-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                     <div class="font-semibold leading-tight">{{ oversized.length === 1 ? 'Клип слишком большой для ZIP' : oversized.length + ' клипов слишком большие для ZIP' }} (>2 GB):</div>
                   </div>
                   <ul class="space-y-0.5 font-mono text-amber-100/90 pl-5">
                     @for (h of oversized; track h.startTime) {
                       <li>{{ formatTime(h.clipStartTime) }} — {{ h.estimatedSizeMb }} MB</li>
                     }
                   </ul>
                   <div class="text-amber-300/85 pl-5">Используй <strong>Win .BAT</strong> сценарий или сократи диапазон клипа в редакторе.</div>
                 </div>
               }
             }
          </div>
        </section>
      }

      @if (error()) {
        <div class="p-3 bg-red-900/50 border border-red-800 rounded text-red-300 text-xs flex items-start gap-2">
          <div class="flex-1 min-w-0 break-words"><strong>Ошибка:</strong> {{ error() }}</div>
          <button (click)="dismissError()" appTooltip="Закрыть" class="text-red-300 hover:text-white text-base leading-none px-1 shrink-0">&times;</button>
        </div>
      }
      @if (infoMessage()) {
        <div class="p-3 bg-indigo-900/40 border border-indigo-800/60 rounded text-indigo-200 text-xs flex items-start gap-2">
          <div class="flex-1 min-w-0 break-words">{{ infoMessage() }}</div>
          <button (click)="dismissInfo()" appTooltip="Закрыть" class="text-indigo-200 hover:text-white text-base leading-none px-1 shrink-0">&times;</button>
        </div>
      }
      
    </div>
    } @else {
      <!-- Spacer pushes status footer to the bottom in collapsed mode -->
      <div class="flex-1"></div>
    }

    <!-- Status Footer -->
    @if (!isSidebarCollapsed()) {
      <div class="bg-gray-900 border-t border-gray-800 flex-shrink-0">
         @if (progressInfo(); as p) {
           <div class="px-4 pt-3 font-mono">
              <div class="flex items-center justify-between text-xs text-gray-400 mb-1.5">
                 <span class="truncate flex items-center gap-1.5 min-w-0">
                    @if (isLive()) {
                       <span class="inline-block w-2 h-2 rounded-full bg-red-500 animate-pulse shrink-0" aria-hidden="true"></span>
                    }
                    <span class="truncate" [class.text-red-300]="isLive()">{{ p.label }}</span>
                 </span>
                 <span class="ml-2 shrink-0 tabular-nums" [class.text-red-300]="isLive()">{{ progressDetails() }}</span>
              </div>
              <div class="h-1.5 bg-gray-800 rounded-full overflow-hidden">
                 @if (p.percent !== undefined && !isLive()) {
                    <div class="h-full bg-indigo-500 transition-all duration-200"
                         [style.width.%]="p.percent"></div>
                 } @else if (isLive()) {
                    <div class="h-full bg-red-500/60 progress-indeterminate"></div>
                 } @else {
                    <div class="h-full progress-indeterminate"></div>
                 }
              </div>
           </div>
         }
         <div class="px-4 py-3 text-sm text-gray-400 font-mono flex items-center justify-between gap-2">
            <div class="min-w-0 truncate">
              @if(globalIsLoading()) {
                 <span class="text-indigo-400 animate-pulse">{{ globalLoadingMessage() }}</span>
              } @else {
                 <span>Готово к работе.</span>
              }
            </div>

            <!-- Firebase Status Indicator -->
            <div class="flex items-center gap-1.5 shrink-0" [appTooltip]="isFirebaseReady() ? 'Cloud Services Active' : 'Cloud Services Offline'">
               <div class="w-2 h-2 rounded-full" [class.bg-green-500]="isFirebaseReady()" [class.bg-gray-600]="!isFirebaseReady()" [class.animate-pulse]="isFirebaseReady()"></div>
               <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" [class.text-indigo-400]="isFirebaseReady()"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" /></svg>
            </div>
         </div>
         <div class="px-4 pb-2 text-[10px] text-gray-600 font-mono flex items-center gap-2 flex-wrap">
            <a href="/privacy" target="_blank" rel="noopener" class="hover:text-indigo-400 transition-colors">Политика</a>
            <span class="text-gray-700">·</span>
            <a href="/terms" target="_blank" rel="noopener" class="hover:text-indigo-400 transition-colors">Соглашение</a>
            <span class="text-gray-700">·</span>
            <a href="/dmca" target="_blank" rel="noopener" class="hover:text-indigo-400 transition-colors">DMCA</a>
            <span class="text-gray-700">·</span>
            <a href="mailto:support@peakster.su" class="hover:text-indigo-400 transition-colors">support@peakster.su</a>
         </div>
      </div>
    } @else {
      <div class="p-2 bg-gray-900 border-t border-gray-800 flex-shrink-0 flex flex-col items-center justify-center gap-1"
           [appTooltip]="progressInfo() ? progressInfo()!.label + ' · ' + progressDetails() : (isFirebaseReady() ? 'Cloud Services Active' : 'Cloud Services Offline')">
         @if (progressInfo()) {
            <div class="text-[10px] font-mono text-indigo-300 tabular-nums leading-none">{{ progressPercentLabel() }}</div>
         }
         <div class="w-2 h-2 rounded-full" [class.bg-green-500]="isFirebaseReady()" [class.bg-gray-600]="!isFirebaseReady()" [class.animate-pulse]="isFirebaseReady()"></div>
      </div>
    }
  </aside>

  <!-- Main Content Area -->
  <main class="flex-1 flex flex-col min-w-0 bg-gray-950 relative">

    @if (needsChatRedownload() && isProcessed()) {
      <div class="bg-amber-950/40 border-b border-amber-800/40 px-4 py-2 flex items-center gap-3 text-xs animate-fade-in">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-amber-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
        <div class="flex-1 text-amber-100">
          Чат для этого VOD не загружен на этом устройстве. Анализ и моменты сохранены, но для оверлея и экспорта нужен сам чат.
        </div>
        <button (click)="redownloadChatNow()"
                class="px-3 py-1 bg-amber-600 hover:bg-amber-500 text-white rounded text-xs font-medium transition-colors shrink-0">
          Скачать чат
        </button>
      </div>
    }

    <!-- Split View. Below md: single scrolling column (player → heatmap →
         editor → highlights), pb-16 clears the fixed mobile control bar. At
         md+: identical to the original two-column grid — min-h-0/overflow
         are md:-gated back to today's exact values, so desktop is unaffected. -->
    <div class="flex-1 grid grid-cols-1 lg:grid-cols-[78fr_22fr] md:min-h-0 overflow-y-auto md:overflow-visible relative z-10 pb-16 md:pb-0">

      <!-- Center: Player & Editor -->
      <div class="flex flex-col md:min-h-0 bg-black/20 border-b md:border-b-0 md:border-r border-gray-800 relative">
        <!-- Placeholder centered on the player column only — when nested in <main>,
             the absolute inset-0 covered the right sidebar area too, biasing the
             text visually. -->
        @if (!isProcessed()) {
          <div class="absolute inset-0 flex flex-col items-center justify-center text-gray-600 z-0 pointer-events-none select-none">
            <div class="w-64 h-64 rounded-full bg-indigo-500/5 blur-3xl absolute"></div>
            <h2 class="text-4xl font-extrabold text-gray-800 relative z-10">PEAKSTER</h2>
            <p class="text-gray-700 mt-2 relative z-10">Загрузите файл или ссылку, чтобы начать работу</p>
          </div>
        }
        @if (isProcessed() && videoId()) {
            <!-- Player Container — flex-row when side-chat layout, single column for overlay layout. -->
            <div class="flex-1 flex min-h-[300px]" [attr.data-tour]="ANCHORS.PLAYER">
              <!-- #player-frame = the iframe's rect (it fills this box) and the demo poster's:
                   tooltips / popovers treat it as a keep-out zone (playerKeepOut). -->
              <div id="player-frame" class="flex-1 bg-black relative flex items-center justify-center overflow-hidden">
                 @if (demoService.isDemoMode()) {
                    <!-- Demo placeholder (no real Twitch iframe; videoId is fake) -->
                    <div class="absolute inset-0 z-10 flex items-center justify-center bg-gray-950">
                      <img src="assets/demo/demo-vod-poster.svg" alt="Демо-стрим"
                           class="absolute inset-0 w-full h-full object-cover opacity-90">
                      <div class="relative z-10 px-4 py-2 bg-black/60 rounded text-xs text-gray-300 border border-gray-700">
                        ⏱ {{ formatTime(playerTime()) }} / {{ formatTime(videoDurationSec() || 7200) }}
                      </div>
                    </div>
                 } @else {
                    <div id="twitch-player" class="absolute inset-0 w-full h-full z-10"></div>
                 }
                 @if (effectiveChatLayout() === 'overlay' && !isOverlayShifted()) {
                   <app-chat-overlay
                      [comments]="chatComments()"
                      [currentTime]="playerTime()"
                      [visible]="isChatOverlayEnabled()">
                   </app-chat-overlay>
                 }
              </div>
              @if (effectiveChatLayout() === 'side' && isChatOverlayEnabled()) {
                <!-- Responsive width: 25% of player-area, clamped to readable range.
                     Without max-w → too wide on 4K (eats player). Without min-w →
                     unreadable below ~240px on narrow viewports. shrink-1 (default)
                     so on very narrow viewports iframe gets first dibs above min-w. -->
                <div class="w-1/5 min-w-[240px] max-w-[320px]">
                  <app-chat-side
                     [comments]="chatComments()"
                     [currentTime]="playerTime()">
                  </app-chat-side>
                </div>
              }
            </div>

            <!-- Mobile chat bottom-sheet — effectiveChatLayout() only resolves to
                 'sheet' below the mobile breakpoint (see computed above), so this
                 never mounts on desktop regardless of isChatSheetOpen. -->
            @if (effectiveChatLayout() === 'sheet' && isChatOverlayEnabled() && isChatSheetOpen()) {
              <div class="md:hidden fixed inset-x-0 bottom-16 z-40 h-[55dvh] bg-gray-900 border-t border-gray-700 rounded-t-2xl shadow-2xl shadow-black/60 flex flex-col">
                <div class="flex items-center justify-between p-3 border-b border-gray-800 shrink-0">
                  <span class="text-sm font-semibold text-gray-300">Чат</span>
                  <button (click)="isChatSheetOpen.set(false)" appTooltip="Закрыть чат" class="p-1.5 text-gray-400 hover:text-white">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
                <div class="flex-1 min-h-0">
                  <app-chat-side
                     [comments]="chatComments()"
                     [currentTime]="playerTime()">
                  </app-chat-side>
                </div>
              </div>
            }

            <!-- Activity Heatmap = the timeline (between player and the controls row). It replaced
                 the separate clip-editor track: on top a TIME STRIP (ruler, the selected clip with
                 its edge handles, the playhead knob — click = seek, drag = scrub), under it the
                 bars with the playhead line and the clip tint. Left gutter: legend «?», «область
                 интереса», zoom to clip. Nothing here may paint over the player above: readouts
                 live INSIDE the heatmap box, every popover is placed with the player as a keep-out
                 rect (Chrome pauses an obscured iframe). Under the bars: the zoom bar. -->
            @if (isProcessed()) {
              <div class="flex-shrink-0 flex bg-gray-900/80 border-t border-b border-gray-800" [ngClass]="speechLaneActive() ? 'h-[233px]' : 'h-[217px]'">
                <div class="w-7 shrink-0 flex flex-col items-center justify-center gap-1.5 border-r border-gray-800">
                  <button type="button" #legendBtn
                          (mouseenter)="onLegendHover(legendBtn, true)"
                          (mouseleave)="onLegendHover(legendBtn, false)"
                          (click)="toggleLegend(legendBtn, $event)"
                          aria-label="Легенда хитмапы"
                          [ngClass]="legendPopover() ? 'bg-gray-700 text-white' : 'text-gray-500 hover:text-gray-200 hover:bg-gray-800'"
                          class="w-5 h-5 rounded-full text-[11px] font-bold leading-none flex items-center justify-center transition-colors">?</button>
                  <button type="button" (click)="toggleRegion()"
                          [attr.aria-pressed]="regionEnabled()"
                          [appTooltip]="regionEnabled() ? 'Выключить область интереса (Alt+P)' : 'Область интереса: список и анализ только по выбранному участку. Края тянутся мышью, Alt+I / Alt+O — начало / конец по плееру (Alt+P)'"
                          [ngClass]="regionEnabled() ? 'bg-white/15 text-white ring-1 ring-white/40' : 'text-gray-500 hover:text-gray-200 hover:bg-gray-800'"
                          class="w-5 h-5 rounded flex items-center justify-center transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M7 3H4v14h3M13 3h3v14h-3" /></svg>
                  </button>
                  <button type="button" (click)="toggleClipZoom()"
                          [disabled]="!currentHighlight()"
                          [attr.aria-pressed]="clipZoomActive()"
                          aria-label="Приблизить к клипу"
                          [appTooltip]="clipZoomActive() ? 'Вернуть прежний масштаб (Z)' : 'Приблизить к клипу: ±30 с вокруг выбранного момента — для точной правки краёв (Z или двойной клик по клипу на шкале)'"
                          [ngClass]="clipZoomActive() ? 'bg-indigo-500/25 text-indigo-200 ring-1 ring-indigo-400/50' : 'text-gray-500 hover:text-gray-200 hover:bg-gray-800 disabled:opacity-30 disabled:pointer-events-none'"
                          class="w-5 h-5 rounded flex items-center justify-center transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="8.5" cy="8.5" r="5" /><path stroke-linecap="round" d="M12.5 12.5L17 17" /><path stroke-linecap="round" d="M6.5 8.5h4" /></svg>
                  </button>
                </div>
                <div class="flex-1 min-w-0 flex flex-col">
                <div class="relative flex-1 min-h-0">
                <!-- Scroller wraps the content; padding lives here so the inner content's
                     width corresponds 1:1 to the bars (bin grid: time t at t/15*step px).
                     px-[16px], not px-4: rem padding grew with the interface scale while
                     HEATMAP_PAD_LEFT stayed 16 (the whole-stream view scrolled at 110-125%).
                     Its native scrollbar is hidden (index.css) — the zoom bar below is the
                     scrollbar. -->
                <div #heatmapScroller class="absolute inset-0 overflow-x-auto touch-pan-x px-[16px]" id="heatmap-container" [attr.data-tour]="ANCHORS.HEATMAP"
                     (scroll)="onHeatmapScroll()"
                     (wheel)="onHeatmapWheel($event)"
                     (pointerdown)="onHeatmapPointerDown($event)"
                     (pointermove)="onHeatmapPointerMove($event)"
                     (pointerup)="onHeatmapPointerEnd($event)"
                     (pointercancel)="onHeatmapPointerEnd($event)"
                     (pointerleave)="onHeatmapPointerLeave($event)">
                  <!-- w-max forces content to grow to bars' total width instead of inheriting
                       100% of the scroller. «isolate» makes it a stacking context so the -z-10
                       ruler grid paints behind the bars but above the box. Layers below the
                       48px strip (grid, tint, chapter lines, region) start at top-[48px]. -->
                  <div #heatmapContent class="relative isolate h-full flex items-end gap-px w-max" [ngClass]="speechLaneActive() ? 'pb-5' : 'pb-1'">
                    <!-- Ruler grid (underlay). Labels + tick marks live in the strip. Ticks are
                         only built around the visible window and tracked by time. -->
                    <div class="absolute inset-x-0 top-[48px] bottom-0 -z-10 pointer-events-none overflow-hidden" aria-hidden="true">
                      @for (t of rulerTicks(); track t.t) {
                        <div class="absolute top-0 bottom-0 border-l" [ngClass]="t.major ? 'border-gray-500/40' : 'border-gray-700/30'" [style.left.px]="t.x"></div>
                      }
                    </div>
                    @for (col of heatmapColumns(); track col.startTime) {
                      <!-- One bar per 15s bin; zoomed out below STEP_LOD one bar per group of
                           bins (height = busiest bin, see heatmap-zoom.ts). transition-all would
                           animate width over 150ms during a zoom; the bars'd be mid-transition
                           while we restore scrollLeft to the anchored position, and the user'd
                           see the content drift L/R until the transition settled. Limit
                           transitions to height/color/opacity so threshold/selection changes
                           still animate smoothly but width snaps instantly. No per-bar
                           [appTooltip]: the hover readout below replaces it (a tooltip above a
                           tall bar landed on the player). -->
                      <div class="flex-shrink-0 transition-[height,opacity,background-color,border-color] duration-150 hover:opacity-100 opacity-80 cursor-pointer"
                           [style.width.px]="col.n * heatmapStep() - 1"
                           [style.minWidth.px]="col.n * heatmapStep() - 1"
                           [style.height.px]="getBarHeight(col.count)"
                           [class]="getColumnColor(col)"
                           [class.border-b-2]="isCurrentHighlightColumn(col)"
                           [class.border-indigo-300]="isCurrentHighlightColumn(col)"
                           (click)="onHeatmapBinClick(col.bin, $index, $event)">
                      </div>
                    }
                    <!-- The selected clip over the bars (its handles are in the strip). -->
                    @if (clipView(); as cv) {
                      <div class="absolute top-[48px] bottom-0 bg-indigo-400/10 border-x border-indigo-300/60 pointer-events-none"
                           [style.left.px]="cv.startPx"
                           [style.width.px]="cv.bandW"></div>
                    }
                    <!-- Category changes (the zoom bar shows the categories themselves). -->
                    @for (x of chapterChangeXs(); track x) {
                      <div class="absolute top-[48px] bottom-0 border-l border-dashed border-white/40 pointer-events-none" [style.left.px]="x"></div>
                    }
                    <!-- «Речь» lane (admin-only, experimental). Lives in the extra bottom padding
                         (pb-5), never over the bars, so bin clicks keep working. Segments = 8px
                         category-colored bands, AI-search hits = thin row above, text-search hits =
                         teal ticks. px positions from the bin grid (speechLane computed) — no
                         transitions on left/width (Ctrl+wheel zoom re-anchors scrollLeft in sync). -->
                    @if (speechLaneActive()) {
                      @for (x of speechTicks(); track x) {
                        <div class="absolute bottom-0 h-0.5 bg-teal-400 pointer-events-none" [style.left.px]="x" [style.width.px]="binBarWidth()"></div>
                      }
                      @for (b of speechLane(); track b.h.source + '-' + b.h.startTime) {
                        <div class="absolute rounded-sm cursor-pointer pointer-events-auto"
                             [ngClass]="b.cls"
                             [style.left.px]="b.left"
                             [style.width.px]="b.width"
                             [style.bottom.px]="b.bottom"
                             [style.height.px]="b.height"
                             [appTooltip]="b.tip"
                             (click)="onSpeechBandClick(b.h)"></div>
                      }
                    }
                    <!-- «Область интереса» — below the strip only, so its full-height handles never
                         meet the clip handles. Dimming comes after bars/lane in DOM order so it
                         paints over them; pointer-events-none keeps bins outside clickable (seek).
                         Edges are drag handles: pointer capture routes move/up to the handle
                         itself — no window listeners (they'd run app-wide change detection on
                         every mouse move). -->
                    @if (regionView(); as rv) {
                      <div class="absolute top-[48px] bottom-0 left-0 bg-gray-950/60 pointer-events-none" [style.width.px]="rv.inPx"></div>
                      <div class="absolute top-[48px] bottom-0 bg-gray-950/60 pointer-events-none" [style.left.px]="rv.outPx" [style.width.px]="rv.tailPx"></div>
                      <div class="absolute top-[48px] bottom-0 -translate-x-1/2 flex justify-center cursor-ew-resize touch-none group"
                           [ngClass]="viewport.prefersCoarsePointer ? 'w-5' : 'w-3'"
                           [style.left.px]="rv.inPx"
                           (pointerdown)="startRegionDrag('in', $event)"
                           (pointermove)="onRegionDrag($event)"
                           (pointerup)="endRegionDrag($event)"
                           (pointercancel)="endRegionDrag($event)">
                        <div class="relative w-0.5 h-full bg-white/70 group-hover:bg-white">
                          <div class="absolute top-0 left-0 w-2 h-0.5 bg-white/70 group-hover:bg-white"></div>
                          <div class="absolute bottom-0 left-0 w-2 h-0.5 bg-white/70 group-hover:bg-white"></div>
                          <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1.5 h-5 rounded-sm bg-white shadow"></div>
                        </div>
                      </div>
                      <div class="absolute top-[48px] bottom-0 -translate-x-1/2 flex justify-center cursor-ew-resize touch-none group"
                           [ngClass]="viewport.prefersCoarsePointer ? 'w-5' : 'w-3'"
                           [style.left.px]="rv.outPx"
                           (pointerdown)="startRegionDrag('out', $event)"
                           (pointermove)="onRegionDrag($event)"
                           (pointerup)="endRegionDrag($event)"
                           (pointercancel)="endRegionDrag($event)">
                        <div class="relative w-0.5 h-full bg-white/70 group-hover:bg-white">
                          <div class="absolute top-0 right-0 w-2 h-0.5 bg-white/70 group-hover:bg-white"></div>
                          <div class="absolute bottom-0 right-0 w-2 h-0.5 bg-white/70 group-hover:bg-white"></div>
                          <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1.5 h-5 rounded-sm bg-white shadow"></div>
                        </div>
                      </div>
                    }
                    <!-- Edge-safe overlay: spans the scroller's 16px paddings and clips there, so a
                         knob / handle / pin at 0 or at the end is neither cut off nor widens the
                         scroll overflow (a label sticking out past the last bar once made the
                         whole-stream view scroll by 5px). Its inner wrappers are in content px. -->
                    <div class="absolute inset-y-0 -left-[16px] -right-[16px] overflow-hidden pointer-events-none">
                      <!-- Time strip. Mouse: click = seek, press-drag = scrub (one seek on release),
                           double-click the clip = zoom to it. Touch: tap = seek, swipe = pan
                           (touch-pan-x, inherited). Clip handles have their own pointer capture. -->
                      <div data-heatmap-strip
                           class="absolute top-0 inset-x-0 h-[48px] bg-gray-950/50 border-b border-gray-800 pointer-events-auto select-none cursor-pointer"
                           role="slider" aria-label="Шкала времени: клик — перейти, перетаскивание — найти место"
                           aria-valuemin="0" [attr.aria-valuemax]="heatmapEndSec()" [attr.aria-valuenow]="playheadWholeSec()" [attr.aria-valuetext]="formatTime(playheadWholeSec())"
                           (pointerdown)="onStripPointerDown($event)"
                           (pointermove)="onStripPointerMove($event)"
                           (pointerup)="onStripPointerUp($event)"
                           (pointercancel)="onStripPointerCancel($event)"
                           (lostpointercapture)="onStripPointerCancel($event)"
                           (pointerleave)="onStripPointerLeave($event)"
                           (dblclick)="onStripDblClick($event)">
                        <div class="absolute inset-y-0 left-[16px] right-[16px]">
                          @for (t of rulerTicks(); track t.t) {
                            <div class="absolute top-0 bottom-0" [style.left.px]="t.x">
                              <div class="absolute bottom-0 left-0 border-l" [ngClass]="t.major ? 'h-3 border-gray-400/80' : 'h-2 border-gray-600'"></div>
                              @if (t.label) {
                                <span class="absolute top-[5px] left-1.5 text-[12px] leading-none font-mono whitespace-nowrap" [ngClass]="t.major ? 'text-gray-200 font-semibold' : 'text-gray-400'">{{ t.label }}</span>
                              }
                            </div>
                          }
                          @if (clipView(); as cv) {
                            <div class="absolute top-[22px] h-[14px] rounded bg-indigo-500 shadow-[0_0_8px_rgba(99,102,241,0.6)] pointer-events-none"
                                 [style.left.px]="cv.startPx"
                                 [style.width.px]="cv.bandW"></div>
                            <!-- Handle hit zones open outward (clipHandleZones): a 2px clip still has
                                 two grabbable sides. They start at 18px — the strip's top row is the
                                 playhead knob's (a press there scrubs, it must not grab the clip start
                                 the playhead sits on after a selection) — and reach 12px below the
                                 strip for a finger. Press without moving = go to that edge. -->
                            <div data-clip-handle class="absolute top-[18px] h-[42px] cursor-ew-resize touch-none group" aria-hidden="true"
                                 [style.left.px]="cv.zones.start.left"
                                 [style.width.px]="cv.zones.start.width"
                                 (pointerdown)="startClipDrag('start', $event)"
                                 (pointermove)="onClipDrag($event)"
                                 (pointerup)="endClipDrag($event)"
                                 (pointercancel)="cancelClipDrag()">
                              <div class="absolute top-[2px] h-[18px] w-[4px] -ml-[2px] rounded-full shadow"
                                   [ngClass]="clipDragEdge() === 'start' ? 'bg-white' : 'bg-indigo-200 group-hover:bg-white'"
                                   [style.left.px]="cv.startGrip"></div>
                            </div>
                            <div data-clip-handle class="absolute top-[18px] h-[42px] cursor-ew-resize touch-none group" aria-hidden="true"
                                 [style.left.px]="cv.zones.end.left"
                                 [style.width.px]="cv.zones.end.width"
                                 (pointerdown)="startClipDrag('end', $event)"
                                 (pointermove)="onClipDrag($event)"
                                 (pointerup)="endClipDrag($event)"
                                 (pointercancel)="cancelClipDrag()">
                              <div class="absolute top-[2px] h-[18px] w-[4px] -ml-[2px] rounded-full shadow"
                                   [ngClass]="clipDragEdge() === 'end' ? 'bg-white' : 'bg-indigo-200 group-hover:bg-white'"
                                   [style.left.px]="cv.endGrip"></div>
                            </div>
                          }
                        </div>
                      </div>
                      <div class="absolute inset-y-0 left-[16px] right-[16px]">
                        <!-- StreamMarkers pins — downward triangles hanging from the strip, apex at
                             the bin. px from the bin grid (was % of videoDurationSec — drifted when
                             chat bins don't span the VOD). Click routes through onHeatmapPinClick
                             (seek + select, like a click on the marker's card). -->
                        @for (p of markerPinsView(); track $index) {
                          <div class="absolute top-[48px] cursor-pointer pointer-events-auto"
                               [style.left.px]="p.x"
                               style="transform: translateX(-50%);"
                               [appTooltip]="(p.h.markerSourceName || 'METKA') + ': ' + (p.h.markerTag || '—')"
                               (click)="onHeatmapPinClick(p.h)">
                            <div class="origin-top transition-transform duration-150 hover:scale-125"
                                 style="width: 0; height: 0; border-left: 7px solid transparent; border-right: 7px solid transparent; border-top: 10px solid #22d3ee;">
                            </div>
                          </div>
                        }
                        @for (p of clipPinsView(); track $index) {
                          <div class="absolute top-[58px] cursor-pointer pointer-events-auto"
                               [style.left.px]="p.x"
                               style="transform: translateX(-50%);"
                               [appTooltip]="(p.h.clipCreatorName || '—') + ' · ' + (p.h.clipViewCount || 0) + ' просм · ' + (p.h.description || '')"
                               (click)="onHeatmapPinClick(p.h)">
                            <div class="origin-top transition-transform duration-150 hover:scale-125"
                                 style="width: 0; height: 0; border-left: 7px solid transparent; border-right: 7px solid transparent; border-top: 10px solid #a855f7;">
                            </div>
                          </div>
                        }
                        <!-- One readout slot, always inside the box (never above it — player):
                             clip drag > region drag > scrub > strip hover > bar hover. -->
                        @if (heatmapReadout(); as rd) {
                          @if (rd.kind === 'bar') {
                            <div class="absolute top-[48px] bottom-0 w-px bg-white/50" [style.left.px]="rd.x"></div>
                          } @else if (rd.kind === 'strip') {
                            <div class="absolute top-0 bottom-0 w-px bg-white/40" [style.left.px]="rd.x"></div>
                          } @else if (rd.kind === 'scrub') {
                            <div class="absolute top-0 bottom-0 w-px bg-red-300" [style.left.px]="rd.x"></div>
                          }
                          <div class="absolute top-[50px] px-1.5 py-0.5 rounded text-[11px] font-mono whitespace-nowrap"
                               [ngClass]="rd.cls"
                               [style.left.px]="rd.x"
                               [style.transform]="rd.flip ? 'translateX(calc(-100% - 4px))' : 'translateX(4px)'">{{ rd.label }}</div>
                        }
                        <!-- Playhead: line through the bars + knob in the strip. transform, no
                             transition (zoom re-anchors scrollLeft synchronously). Clamped to the
                             last bar; past it (live, quiet tail) the knob goes dim. The knob ignores
                             the pointer — pressing it is a strip press (scrub): the clip handles
                             start below its 18px. -->
                        @if (playheadView(); as ph) {
                          <div class="absolute top-0 bottom-0 left-0 w-0.5 -ml-px bg-red-500"
                               [ngClass]="ph.past ? 'opacity-50' : 'shadow-[0_0_6px_rgba(239,68,68,0.7)]'"
                               [style.transform]="ph.transform">
                            <div class="absolute top-0 left-1/2 -translate-x-1/2 w-[15px] h-[18px] bg-red-500"
                                 style="clip-path: polygon(0 0, 100% 0, 100% 62%, 50% 100%, 0 62%);"></div>
                          </div>
                        }
                      </div>
                    </div>
                  </div>
                </div>
                <!-- Playhead off-screen → a chevron at that edge of the strip; click shows it. -->
                @if (playheadOffscreen(); as side) {
                  <button type="button" (click)="revealPlayhead()"
                          aria-label="Показать плейхед"
                          appTooltip="Плейхед за краем — показать"
                          [ngClass]="side === 'left' ? 'left-0' : 'right-0'"
                          class="absolute top-0 h-[48px] w-[16px] z-10 flex items-center justify-center bg-gray-950/90 text-red-400 hover:text-red-300">
                    @if (side === 'left') {
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" class="w-4 h-4" fill="currentColor" aria-hidden="true"><path d="M12.7 4.3a1 1 0 010 1.4L8.42 10l4.3 4.3a1 1 0 01-1.42 1.4l-5-5a1 1 0 010-1.4l5-5a1 1 0 011.42 0z" /></svg>
                    } @else {
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" class="w-4 h-4" fill="currentColor" aria-hidden="true"><path d="M7.3 15.7a1 1 0 010-1.4L11.58 10 7.3 5.7a1 1 0 011.42-1.4l5 5a1 1 0 010 1.4l-5 5a1 1 0 01-1.42 0z" /></svg>
                    }
                  </button>
                }
                </div>
                <!-- Zoom bar: px-[16px] = the scroller's padding, so at «whole stream» the track
                     lines up with the bars. px units (not rem) like the scroller. -->
                <app-heatmap-zoom-bar class="shrink-0 h-[20px] px-[16px] pt-[2px] pb-[4px]"
                     [endSec]="heatmapEndSec()"
                     [viewStart]="heatmapView().start"
                     [viewEnd]="heatmapView().end"
                     [segments]="zoomBarSegments()"
                     [playheadPct]="zoomBarPlayheadPct()"
                     (panTo)="onZoomBarPan($event)"
                     (edgeDrag)="onZoomBarEdge($event)"
                     (toggleFit)="onZoomBarToggleFit($event)"></app-heatmap-zoom-bar>
                </div>
              </div>
            }

            <!-- Controls row: the selected clip (its edges are edited on the heatmap strip),
                 Pre-Roll, chat. Always shown (Pre-Roll / chat don't depend on a selection) and one
                 line from md up (the clip info clips instead of wrapping), so the player height
                 doesn't jump with the selection. -->
            <div class="flex-shrink-0 bg-gray-900 px-4 py-1.5 flex flex-wrap md:flex-nowrap items-center gap-x-3 gap-y-1.5">
               @if (currentHighlight(); as highlight) {
                  @if (clipView(); as cv) {
                     <div class="flex items-center gap-2 text-xs min-w-0 overflow-hidden whitespace-nowrap">
                        <span class="text-[10px] uppercase tracking-wider text-gray-500 shrink-0">Клип</span>
                        <span class="font-mono text-indigo-300">{{ formatTime(cv.start) }}</span>
                        <span class="text-gray-600">→</span>
                        <span class="font-mono text-indigo-300">{{ formatTime(cv.end) }}</span>
                        <span class="font-mono text-gray-500">{{ cv.duration }}</span>
                        <span class="text-gray-700">·</span>
                        @if (isTranscriptHighlight(highlight)) {
                           <span class="text-teal-300 text-[11px]">речь · {{ highlight.confidence }}{{ highlight.source === 'transcript-search' ? '%' : '' }}</span>
                        } @else if (highlight.source === 'search-ai') {
                           <span class="text-purple-400 text-[11px]">{{ highlight.confidence }}%</span>
                        } @else if (isSearchHighlight(highlight)) {
                           <span class="text-amber-400 text-[11px]">{{ highlight.matchCount }} совп.</span>
                        } @else {
                           <span class="text-gray-400 text-[11px]">{{ highlight.messageCount }} msg</span>
                        }
                     </div>
                  }
               }
               <!-- Pre-roll: inline horizontal layout. Blurred after a mouse / touch change —
                    while a range input has focus the hotkey handler ignores every key. -->
               <div class="flex items-center gap-2 bg-gray-800/50 px-3 py-1.5 rounded border border-gray-700/50 flex-1 min-w-[120px]">
                  <span class="text-[10px] text-gray-500 uppercase tracking-wider shrink-0">Pre-Roll</span>
                  <input type="range" min="0" max="30" step="1" [value]="rewindOffsetSeconds()" (input)="onRewindOffsetChange($event)" (pointerdown)="sliderGestureStart('preroll', $event)" (pointerup)="sliderGestureEnd($event)" (pointercancel)="sliderGestureEnd($event)" class="flex-1 h-1.5 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-500 min-w-0">
                  <span class="font-mono text-indigo-300 text-xs shrink-0 w-8 text-right">{{ rewindOffsetSeconds() }}s</span>
               </div>
               <!-- Chat widget: on/off toggle + layout switch (overlay/side).
                    Left half toggles visibility. Right half is a 2-position pill that
                    sets explicit preference; the active half is whatever effective
                    layout resolves to (covers both explicit choice AND auto default).
                    ⚠ in «Поверх» appears only when the current browser enforces
                    obscured-iframe block (Chrome family) — explains why play() will
                    fail with overlay-over-player. -->
               <div class="flex items-stretch rounded bg-gray-800 border border-gray-700 transition-colors shrink-0 overflow-hidden text-xs" [class.border-indigo-900]="isChatOverlayEnabled()">
                  <button (click)="toggleChatOverlay()" class="flex items-center gap-2 py-1.5 px-3 hover:bg-gray-700 transition-colors" [class.text-indigo-400]="isChatOverlayEnabled()">
                     <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" /></svg>
                     <span>{{ isChatOverlayEnabled() ? 'Скрыть чат' : 'Показать чат' }}</span>
                  </button>
                  <!-- Layout segments visible regardless of chat on/off so the
                       widget width stays stable (juzer noted the click-jump). -->
                  <div class="w-px bg-gray-700"></div>
                  <button (click)="setChatLayout('overlay')"
                          appTooltip="Чат поверх плеера. В Chrome SDK Twitch блокирует play() — используйте Firefox/Safari или «Сбоку»."
                          [ngClass]="effectiveChatLayout() === 'overlay' ? 'flex items-center gap-1 py-1.5 px-3 hover:bg-gray-700 transition-colors bg-indigo-900/40 text-indigo-300' : 'flex items-center gap-1 py-1.5 px-3 hover:bg-gray-700 transition-colors'">
                     <span>Поверх</span>
                     @if (effectiveChatLayout() === 'overlay' && obscuredEnforcementActive) {
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                           <path fill-rule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
                        </svg>
                     }
                  </button>
                  <div class="w-px bg-gray-700"></div>
                  <button (click)="setChatLayout('side')"
                          appTooltip="Чат сбоку от плеера. Работает во всех браузерах."
                          [ngClass]="effectiveChatLayout() === 'side' ? 'py-1.5 px-3 hover:bg-gray-700 transition-colors bg-indigo-900/40 text-indigo-300' : 'py-1.5 px-3 hover:bg-gray-700 transition-colors'">
                     Сбоку
                  </button>
               </div>
            </div>
        }
      </div>

      <!-- Right: Highlights List -->
      <div id="highlights-panel" class="flex flex-col md:min-h-0 bg-gray-900/30">
        @if (isProcessed()) {
            <!-- Filter Tabs -->
            <div role="tablist" aria-label="Фильтр моментов" class="flex border-b border-gray-800 bg-gray-900/80 backdrop-blur-sm sticky top-0 z-10 text-xs font-medium">
                <button role="tab" [attr.aria-selected]="activeTab() === 'all'" (click)="setActiveTab('all')" [attr.data-tour]="ANCHORS.SIDEBAR_TAB_ALL" [class]="activeTab() === 'all' ? 'text-indigo-400 border-b-2 border-b-indigo-500 bg-indigo-500/10' : 'text-indigo-400/50 hover:text-indigo-300 hover:bg-indigo-500/5'" class="flex-1 py-3 transition-colors border-r border-r-gray-800/70">
                    Все ({{ allHighlights().length }})
                </button>
                 <button role="tab" [attr.aria-selected]="activeTab() === 'pinned'" (click)="setActiveTab('pinned')" [class]="activeTab() === 'pinned' ? 'text-yellow-400 border-b-2 border-b-yellow-500 bg-yellow-500/10' : 'text-yellow-500/50 hover:text-yellow-300 hover:bg-yellow-500/5'" class="flex-1 py-3 transition-colors border-r border-r-gray-800/70">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 inline mb-0.5 mr-1" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path d="M5 4a2 2 0 012-2h6a2 2 0 012 2v14l-5-2.5L5 18V4z" /></svg>
                    Избранное ({{ pinnedHighlights().length }})
                </button>
                 <button role="tab" [attr.aria-selected]="activeTab() === 'search'" (click)="setActiveTab('search')" [attr.data-tour]="ANCHORS.SIDEBAR_TAB_SEARCH" [class]="activeTab() === 'search' ? 'text-amber-400 border-b-2 border-b-amber-500 bg-amber-500/10' : 'text-amber-500/50 hover:text-amber-300 hover:bg-amber-500/5'" class="flex-1 py-3 transition-colors border-r border-r-gray-800/70">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 inline mb-0.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                    Поиск ({{ searchHighlights().length }})
                </button>
                 <button role="tab" [attr.aria-selected]="activeTab() === 'clips'" (click)="setActiveTab('clips')" [class]="activeTab() === 'clips' ? 'text-purple-400 border-b-2 border-b-purple-500 bg-purple-500/10' : 'text-purple-500/50 hover:text-purple-300 hover:bg-purple-500/5'" class="flex-1 py-3 transition-colors border-r border-r-gray-800/70">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 inline mb-0.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                    Клипы ({{ clipsHighlights().length }}@if (isLoadingClips()) {<span class="text-purple-300">…</span>})
                </button>
                 @if (isAdmin()) {
                 <button role="tab" [attr.aria-selected]="activeTab() === 'speech'" (click)="setActiveTab('speech')" appTooltip="Речь стримера: транскрипт и ИИ (эксперимент, видно только админу)" [class]="activeTab() === 'speech' ? 'text-teal-300 border-b-2 border-b-teal-500 bg-teal-500/10' : 'text-teal-500/50 hover:text-teal-300 hover:bg-teal-500/5'" class="flex-1 py-3 transition-colors border-r border-r-gray-800/70">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 inline mb-0.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" /></svg>
                    Речь ({{ speechTabCount() }})
                 </button>
                 }
                 <button role="tab" [attr.aria-selected]="activeTab() === 'deleted'" (click)="setActiveTab('deleted')" [class]="activeTab() === 'deleted' ? 'text-red-400 border-b-2 border-b-red-500 bg-red-500/10' : 'text-red-500/50 hover:text-red-300 hover:bg-red-500/5'" class="flex-1 py-3 transition-colors">
                    Корзина ({{ deletedHighlights().length }})
                </button>
            </div>

            <!-- «Область интереса» notice — every tab except Избранное / Корзина is region-filtered. -->
            @if (activeRegion(); as region) {
              @if (activeTab() !== 'pinned' && activeTab() !== 'deleted') {
                <div class="flex items-center gap-2 px-3 py-1.5 border-b border-amber-500/20 bg-amber-500/10 text-[11px] text-amber-200">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" class="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M7 3H4v14h3M13 3h3v14h-3" /></svg>
                  <span class="flex-1 min-w-0">Область <span class="font-mono">{{ formatRegionLabel(region) }}</span> ({{ formatRegionLength(region) }}): показаны моменты внутри неё, Избранное — всегда.</span>
                  <button type="button" (click)="toggleRegion()" class="shrink-0 px-1.5 py-0.5 rounded bg-amber-500/20 hover:bg-amber-500/30 text-amber-100">Выключить</button>
                </div>
              }
            }

            <!-- Search Panel (only on search tab) -->
            @if (activeTab() === 'search') {
              <div class="p-2 border-b border-gray-800 bg-gray-900/60 space-y-2">
                 <!-- Mode toggle: keyword (stemmer/syntax) vs AI (free-text, Gemini-powered) -->
                 <div class="flex items-stretch rounded bg-gray-800 border border-gray-700 overflow-hidden text-xs w-fit">
                    <button type="button" (click)="setSearchMode('keyword')"
                            [ngClass]="searchMode() === 'keyword' ? 'flex items-center gap-1 py-1 px-2.5 hover:bg-gray-700 transition-colors bg-amber-900/40 text-amber-300' : 'flex items-center gap-1 py-1 px-2.5 hover:bg-gray-700 transition-colors text-gray-400'">
                       Ключевые слова
                    </button>
                    <div class="w-px bg-gray-700"></div>
                    <button type="button" (click)="setSearchMode('ai')"
                            appTooltip="Опишите момент своими словами — ИИ прочитает чат и найдёт похожие места с оценкой уверенности"
                            [ngClass]="searchMode() === 'ai' ? 'flex items-center gap-1 py-1 px-2.5 hover:bg-gray-700 transition-colors bg-purple-900/40 text-purple-300' : 'flex items-center gap-1 py-1 px-2.5 hover:bg-gray-700 transition-colors text-gray-400'">
                       <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zM12 2a1 1 0 011 1v1h1a1 1 0 110 2h-1v1a1 1 0 11-2 0v-1h-1a1 1 0 110-2h1v-1a1 1 0 011-1z" clip-rule="evenodd" /></svg>
                       ИИ
                    </button>
                 </div>
                 <div class="flex gap-1">
                    <input type="text"
                           [value]="searchInputValue()"
                           (input)="onSearchInput($event)"
                           (keydown.enter)="onSearchSubmit()"
                           [placeholder]="searchMode() === 'ai' ? 'опишите момент — например «стример громко закричал от испуга»' : 'мина, граната  ·  «фраза»  ·  -бот'"
                           [attr.maxlength]="searchMode() === 'ai' ? 300 : null"
                           [appTooltip]="searchMode() === 'ai' ? 'ИИ ищет по смыслу, не по точным словам — опишите момент своими словами.' : 'Запятая — ИЛИ (мина, граната). Пробел — И (мина граната). Кавычки — точная фраза. Минус — исключить.'"
                           [attr.data-tour]="ANCHORS.SEARCH_INPUT"
                           [class]="'flex-1 bg-gray-800 text-white text-xs px-2 py-1.5 rounded border focus:outline-none placeholder:text-gray-600 ' + (searchMode() === 'ai' ? 'border-gray-700 focus:border-purple-500' : 'border-gray-700 focus:border-amber-500')" />
                    @if (searchMode() === 'ai') {
                       <div class="relative flex">
                       <button (click)="onSearchSubmit()"
                               [disabled]="isAnalyzingSearch() || !searchInputValue().trim() || aiSearchExhausted() || aiBlockedByMissingChat() || isLive() || parallelDownloadActive()"
                               [attr.data-tour]="ANCHORS.SEARCH_BUTTON"
                               [appTooltip]="aiBlockedByMissingChat() ? aiSearchTooltip() : (aiSearchExhausted() ? aiSearchTooltip() : (isLive() ? 'ИИ-поиск недоступен во время прямого эфира — нужен полный чат.' : (parallelDownloadActive() ? 'Дождитесь окончания параллельной загрузки чата.' : aiSearchTooltip())))"
                               [class]="searchRetryInfo() ? 'px-3 py-1.5 bg-amber-700 hover:bg-amber-600 text-white text-xs font-bold rounded transition-colors flex items-center gap-1.5 disabled:opacity-80' : 'px-3 py-1.5 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 disabled:text-gray-500 text-white text-xs font-bold rounded transition-colors flex items-center gap-1.5'">
                          @if (searchRetryInfo(); as r) {
                             <svg class="h-3 w-3" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L1 21h22L12 2zm0 6l7.53 13H4.47L12 8zm-1 4v4h2v-4h-2zm0 5v2h2v-2h-2z"/></svg>
                             <span>{{ r.remainingSec }}с ({{ r.attempt }}/{{ r.maxAttempts }})</span>
                          } @else if (isAnalyzingSearch()) {
                             <svg class="animate-spin h-3 w-3" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                             <span>Ищу...</span>
                          } @else {
                             <span>Искать</span>
                             @if (aiSearchBadge(); as b) {
                                <span class="text-[10px] px-1 py-0.5 rounded font-mono"
                                      [class.bg-amber-500]="aiSearchQuotaLow()"
                                      [class.bg-opacity-30]="aiSearchQuotaLow()"
                                      [class.bg-purple-900]="!aiSearchQuotaLow()"
                                      [class.bg-opacity-50]="!aiSearchQuotaLow()">{{ b }}</span>
                             }
                          }
                       </button>
                       @if (activeRegion()) {
                          <app-region-scope-badge [text]="regionScanBadgeText()"></app-region-scope-badge>
                       }
                       </div>
                    } @else {
                       <div class="relative flex">
                       <button (click)="onSearchSubmit()"
                               [disabled]="!searchInputValue().trim()"
                               [attr.data-tour]="ANCHORS.SEARCH_BUTTON"
                               class="px-3 py-1.5 bg-amber-600 hover:bg-amber-500 disabled:bg-gray-700 disabled:text-gray-500 text-white text-xs font-bold rounded transition-colors">
                          Найти
                       </button>
                       @if (activeRegion()) {
                          <app-region-scope-badge [text]="regionScanBadgeText()"></app-region-scope-badge>
                       }
                       </div>
                    }
                    @if (searchMode() === 'keyword') {
                       <div class="relative">
                          <button type="button"
                                  (click)="toggleSearchSyntaxHelp($event)"
                                  [class.bg-amber-600]="showSearchSyntaxHelp()"
                                  [class.text-white]="showSearchSyntaxHelp()"
                                  class="px-2 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white text-xs rounded border border-gray-700 transition-colors"
                                  appTooltip="Синтаксис поиска">
                             ?
                          </button>
                          @if (showSearchSyntaxHelp()) {
                             <div class="absolute right-0 top-full mt-1 z-40 w-80 bg-gray-900 border border-amber-600/40 rounded-lg shadow-2xl shadow-black/60 p-3 text-xs text-gray-300"
                                  (click)="$event.stopPropagation()">
                                <div class="flex items-center justify-between mb-2 pb-2 border-b border-gray-800">
                                   <span class="font-bold text-amber-300 text-xs">Синтаксис поиска</span>
                                   <button (click)="closeSearchSyntaxHelp()" class="text-gray-500 hover:text-gray-200" appTooltip="Закрыть (Esc)">
                                      <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                                   </button>
                                </div>
                                <table class="w-full text-left">
                                   <tbody class="divide-y divide-gray-800">
                                      <tr>
                                         <td class="py-1.5 pr-2 font-mono text-amber-300 whitespace-nowrap align-top">мина</td>
                                         <td class="py-1.5 text-gray-400">Найдёт также <span class="text-gray-200">мине, мину, мины</span> (через стеммер)</td>
                                      </tr>
                                      <tr>
                                         <td class="py-1.5 pr-2 font-mono text-amber-300 whitespace-nowrap align-top">мина, граната</td>
                                         <td class="py-1.5 text-gray-400"><span class="text-gray-200">ИЛИ</span> — любое из слов</td>
                                      </tr>
                                      <tr>
                                         <td class="py-1.5 pr-2 font-mono text-amber-300 whitespace-nowrap align-top">мина граната</td>
                                         <td class="py-1.5 text-gray-400"><span class="text-gray-200">И</span> — оба в одном сообщении</td>
                                      </tr>
                                      <tr>
                                         <td class="py-1.5 pr-2 font-mono text-amber-300 whitespace-nowrap align-top">"точная фраза"</td>
                                         <td class="py-1.5 text-gray-400">Подстрока без стеммера</td>
                                      </tr>
                                      <tr>
                                         <td class="py-1.5 pr-2 font-mono text-amber-300 whitespace-nowrap align-top">-бот</td>
                                         <td class="py-1.5 text-gray-400">Исключить (со стеммером: также бота, боту, боты)</td>
                                      </tr>
                                      <tr>
                                         <td class="py-1.5 pr-2 font-mono text-amber-300 whitespace-nowrap align-top">-"мин"</td>
                                         <td class="py-1.5 text-gray-400">Точное исключение: буква в букву, без стеммера</td>
                                      </tr>
                                      <tr>
                                         <td class="py-1.5 pr-2 font-mono text-amber-300 whitespace-nowrap align-top">точное совпадение</td>
                                         <td class="py-1.5 text-gray-400">Чекбокс: отключает морфологию для всего запроса</td>
                                      </tr>
                                   </tbody>
                                </table>
                                <div class="mt-2 pt-2 border-t border-gray-800 text-gray-400 leading-relaxed space-y-1">
                                   <div>Пример: <span class="font-mono text-amber-400">мина, граната бот -я</span> — (мина ИЛИ граната) И «бот» И НЕ «я».</div>
                                   <div>Вычесть букву в букву: <span class="font-mono text-amber-400">мина -"мин"</span> — формы мины, но НЕ голое «мин».</div>
                                </div>
                             </div>
                          }
                       </div>
                    }
                 </div>
                 <div class="flex items-center justify-between text-xs text-gray-400">
                    @if (searchMode() === 'keyword') {
                       <label class="flex items-center gap-1.5 cursor-pointer hover:text-gray-300 select-none">
                          <input type="checkbox" [checked]="searchExactMatch()" (change)="toggleSearchExactMatch()" class="h-3 w-3 rounded bg-gray-700 border-gray-600 text-amber-500 focus:ring-0" />
                          точное совпадение
                       </label>
                       @if (searchQuery()) {
                          <div class="flex items-center gap-2">
                             <span class="text-amber-400 font-mono">{{ totalSearchMatches() }} совп. в {{ searchHighlights().length }} моментах</span>
                             <button (click)="clearSearchResults()" class="hover:text-red-400" appTooltip="Удалить результаты (кроме закреплённых)">✕</button>
                          </div>
                       }
                    } @else {
                       <span></span>
                       @if (aiSearchHighlightsCount() > 0) {
                          <div class="flex items-center gap-2">
                             <span class="text-purple-400 font-mono">Найдено: {{ aiSearchHighlightsCount() }}</span>
                             <button (click)="clearSearchResults()" class="hover:text-red-400" appTooltip="Удалить результаты (кроме закреплённых)">✕</button>
                          </div>
                       }
                    }
                 </div>
                 @if (searchHistory().length > 0 && (searchMode() === 'ai' ? aiSearchHighlightsCount() === 0 : !searchQuery())) {
                    <div class="flex flex-wrap gap-1 pt-1">
                       <span class="text-xs text-gray-400 uppercase tracking-wider mr-1 self-center">История:</span>
                       @for (h of searchHistory(); track h) {
                          <button (click)="runSearchFromHistory(h)"
                                  [class]="searchMode() === 'ai' ? 'px-2 py-0.5 text-xs bg-gray-800 hover:bg-purple-600 hover:text-white text-gray-300 rounded transition-colors' : 'px-2 py-0.5 text-xs bg-gray-800 hover:bg-amber-600 hover:text-white text-gray-300 rounded transition-colors'">
                             {{ h }}
                          </button>
                       }
                    </div>
                 }
              </div>
            }

            <!-- «Речь» panel (admin-only, experimental): transcript status, mode pill, Gemini controls. -->
            @if (activeTab() === 'speech' && isAdmin()) {
              <div class="p-2 border-b border-gray-800 bg-gray-900/60 space-y-2 text-xs">
                 <div class="flex items-center justify-between gap-2 text-[11px] min-w-0">
                    @if (transcript.status() === 'ready' && transcript.transcript(); as t) {
                       <span class="text-teal-300 font-mono truncate" [appTooltip]="'Whisper ' + t.model + ' · ' + t.lines.length + ' строк · от ' + t.createdAt">
                          {{ t.model }} · {{ formatHoursShort(t.durationSec) }} · {{ t.lines.length }} строк
                       </span>
                    } @else if (transcript.status() === 'loading') {
                       <span class="text-gray-400">Загружаю транскрипт…</span>
                    } @else if (transcript.status() === 'missing') {
                       <span class="text-gray-400">Транскрипта нет</span>
                    } @else if (transcript.status() === 'error') {
                       <span class="text-red-400 truncate">{{ transcript.errorMessage() }}</span>
                    } @else {
                       <span></span>
                    }
                    <div class="flex items-center gap-1.5 shrink-0">
                       <span class="px-1.5 py-0.5 rounded bg-purple-900/40 text-purple-300 border border-purple-700/40 text-[9px] uppercase tracking-wider" appTooltip="Экспериментальная функция — видна только администратору">эксперимент</span>
                       <button (click)="transcript.reload()" [disabled]="transcript.status() === 'loading'" class="px-1.5 py-0.5 rounded text-gray-400 hover:text-white hover:bg-gray-700 disabled:opacity-40" appTooltip="Перезагрузить транскрипт">⟳</button>
                    </div>
                 </div>
                 @if (transcriptDurationWarning(); as w) {
                    <div class="text-[11px] text-amber-400">{{ w }}</div>
                 }
                 <div class="flex items-stretch rounded bg-gray-800 border border-gray-700 overflow-hidden w-fit">
                    <button type="button" (click)="setSpeechMode('segments')"
                            [ngClass]="speechMode() === 'segments' ? 'py-1 px-2.5 transition-colors bg-teal-900/50 text-teal-200' : 'py-1 px-2.5 transition-colors text-gray-400 hover:bg-gray-700'">Участки</button>
                    <div class="w-px bg-gray-700"></div>
                    <button type="button" (click)="setSpeechMode('text')"
                            [ngClass]="speechMode() === 'text' ? 'py-1 px-2.5 transition-colors bg-teal-900/50 text-teal-200' : 'py-1 px-2.5 transition-colors text-gray-400 hover:bg-gray-700'">Текст</button>
                    <div class="w-px bg-gray-700"></div>
                    <button type="button" (click)="setSpeechMode('ai-search')"
                            [ngClass]="speechMode() === 'ai-search' ? 'py-1 px-2.5 transition-colors bg-fuchsia-900/50 text-fuchsia-200' : 'py-1 px-2.5 transition-colors text-gray-400 hover:bg-gray-700'">ИИ-поиск</button>
                 </div>
                 @if (speechMode() === 'segments') {
                    <div class="flex gap-1">
                       <select (change)="onSpeechModelChange('seg', $event)" [disabled]="!!transcript.segRun()?.running"
                               appTooltip="Pro — точнее, Flash — быстрее и дешевле"
                               class="bg-gray-800 text-gray-200 border border-gray-700 rounded px-1.5 text-xs focus:outline-none focus:border-teal-500">
                          <option value="pro" [selected]="transcript.segModel() === 'pro'">Pro</option>
                          <option value="flash" [selected]="transcript.segModel() === 'flash'">Flash</option>
                       </select>
                       <button type="button" (click)="transcript.useChat.set(!transcript.useChat())"
                               [disabled]="!!transcript.segRun()?.running || !transcript.chatAvailable()"
                               [appTooltip]="speechChatTooltip()"
                               [ngClass]="speechChatToggleClass()">+ чат</button>
                       @if (transcript.segRun()?.running) {
                          <button (click)="transcript.cancelSegments()" appTooltip="Отменить анализ"
                                  class="flex-1 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-xs font-bold rounded flex items-center justify-center gap-1.5">
                             <svg class="animate-spin h-3 w-3 shrink-0" viewBox="0 0 24 24" aria-hidden="true"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                             <span>Анализ {{ transcript.segRun()?.done }}/{{ transcript.segRun()?.total }} · Отмена</span>
                          </button>
                       } @else {
                          <div class="relative flex-1 flex">
                          <button (click)="transcript.runSegments()" [disabled]="transcript.status() !== 'ready'"
                                  class="flex-1 px-3 py-1.5 bg-teal-600 hover:bg-teal-500 disabled:bg-gray-700 disabled:text-gray-500 text-white text-xs font-bold rounded transition-colors">
                             {{ transcript.segments().length ? 'Найти заново' : 'Найти участки' }}
                          </button>
                          @if (activeRegion()) {
                             <app-region-scope-badge [text]="regionScanBadgeText()"></app-region-scope-badge>
                          }
                          </div>
                       }
                    </div>
                    @if (transcript.segRun(); as r) {
                       @if (!r.running) {
                          <div class="flex items-center justify-between gap-2 text-[11px]">
                             <span [class]="r.error ? 'text-amber-400' : 'text-gray-400'">{{ r.error || ('Найдено участков: ' + r.results) }}</span>
                             <span class="flex items-center gap-2 shrink-0">
                                @if (transcript.segCanRetry()) {
                                   <button (click)="transcript.runSegments(true)" class="px-2 py-0.5 rounded bg-amber-800/50 hover:bg-amber-700/60 text-amber-100">Повторить неудачные</button>
                                }
                                <span class="font-mono text-gray-500" appTooltip="Токенов Gemini за прогон">{{ formatTokens(r.tokens) }} ток.</span>
                             </span>
                          </div>
                       }
                    }
                    @if (transcript.segments().length > 0) {
                       <div class="flex flex-wrap items-center gap-1">
                          @for (cat of TRANSCRIPT_CATEGORY_KEYS; track cat) {
                             <button (click)="transcript.toggleCategory(cat)" [ngClass]="speechChipClass(cat)">
                                <span class="inline-block w-2 h-2 rounded-sm" [ngClass]="TRANSCRIPT_CATEGORIES[cat].band"></span>
                                {{ TRANSCRIPT_CATEGORIES[cat].label }} {{ transcript.categoryCounts()[cat] }}
                             </button>
                          }
                          <button (click)="clearSpeechResults('transcript-ai')" class="ml-auto px-1 text-gray-500 hover:text-red-400" appTooltip="Удалить участки (кроме закреплённых и добавленных)">✕</button>
                       </div>
                    }
                 }
                 @if (speechMode() === 'ai-search') {
                    <div class="flex gap-1">
                       <input type="text" [value]="transcript.aiQueryInput()" (input)="onSpeechAiInput($event)" (keydown.enter)="transcript.runAiSearch()" maxlength="300"
                              placeholder="что искать в речи — например «рассказ про ледокол»"
                              class="flex-1 min-w-0 bg-gray-800 text-white text-xs px-2 py-1.5 rounded border border-gray-700 focus:border-fuchsia-500 focus:outline-none placeholder:text-gray-600" />
                       <select (change)="onSpeechModelChange('search', $event)" [disabled]="!!transcript.searchRun()?.running"
                               appTooltip="Flash — быстрее и дешевле, Pro — точнее"
                               class="bg-gray-800 text-gray-200 border border-gray-700 rounded px-1.5 text-xs focus:outline-none focus:border-fuchsia-500">
                          <option value="flash" [selected]="transcript.searchModel() === 'flash'">Flash</option>
                          <option value="pro" [selected]="transcript.searchModel() === 'pro'">Pro</option>
                       </select>
                       <button type="button" (click)="transcript.useChat.set(!transcript.useChat())"
                               [disabled]="!!transcript.searchRun()?.running || !transcript.chatAvailable()"
                               [appTooltip]="speechChatTooltip()"
                               [ngClass]="speechChatToggleClass()">+ чат</button>
                       @if (transcript.searchRun()?.running) {
                          <button (click)="transcript.cancelAiSearch()" appTooltip="Отменить поиск"
                                  class="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-xs font-bold rounded flex items-center gap-1.5">
                             <svg class="animate-spin h-3 w-3 shrink-0" viewBox="0 0 24 24" aria-hidden="true"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                             <span>{{ (transcript.searchRun()?.total ?? 0) > 1 ? transcript.searchRun()?.done + '/' + transcript.searchRun()?.total : 'Поиск' }}</span>
                          </button>
                       } @else {
                          <div class="relative flex">
                          <button (click)="transcript.runAiSearch()" [disabled]="transcript.status() !== 'ready' || !transcript.aiQueryInput().trim()"
                                  class="px-3 py-1.5 bg-fuchsia-600 hover:bg-fuchsia-500 disabled:bg-gray-700 disabled:text-gray-500 text-white text-xs font-bold rounded transition-colors">
                             Искать
                          </button>
                          @if (activeRegion()) {
                             <app-region-scope-badge [text]="regionScanBadgeText()"></app-region-scope-badge>
                          }
                          </div>
                       }
                    </div>
                    @if (transcript.searchRun(); as r) {
                       @if (!r.running) {
                          <div class="flex items-center justify-between gap-2 text-[11px]">
                             <span [class]="r.error ? 'text-amber-400' : 'text-gray-400'">{{ r.error || ('Найдено: ' + r.results) }}</span>
                             <span class="flex items-center gap-2 shrink-0">
                                @if (transcript.searchCanRetry()) {
                                   <button (click)="transcript.runAiSearch(true)" class="px-2 py-0.5 rounded bg-amber-800/50 hover:bg-amber-700/60 text-amber-100">Повторить неудачные</button>
                                }
                                <span class="font-mono text-gray-500" appTooltip="Токенов Gemini за прогон">{{ formatTokens(r.tokens) }} ток.</span>
                             </span>
                          </div>
                       }
                    }
                    @if (transcript.searchHits().length > 0) {
                       <div class="flex items-center justify-end text-[11px]">
                          <button (click)="clearSpeechResults('transcript-search')" class="px-1 text-gray-500 hover:text-red-400" appTooltip="Удалить результаты (кроме закреплённых и добавленных)">✕ очистить</button>
                       </div>
                    }
                 }
              </div>
            }

            <!-- StreamMarkers source filter chips (only when there are paired sources) -->
            @if (markersService.markerSources().length > 0) {
              <div class="flex items-start gap-1.5 px-2 py-2 border-b border-gray-800 flex-wrap">
                <span class="text-[10px] text-gray-500 uppercase tracking-wider mt-0.5 mr-1 shrink-0">Источники:</span>
                @for (source of markersService.markerSources(); track source.sourceId) {
                  <button (click)="markersService.toggleSourceHidden(source.sourceId)"
                          [class]="markersService.hiddenSourceIds().has(source.sourceId)
                            ? 'px-2 py-0.5 rounded text-[10px] bg-gray-800/60 text-gray-500 line-through border border-gray-700/40 hover:text-gray-300 transition-colors'
                            : 'px-2 py-0.5 rounded text-[10px] bg-cyan-900/40 text-cyan-200 border border-cyan-700/50 hover:bg-cyan-800/50 transition-colors'">
                    {{ source.name }}
                  </button>
                }
              </div>
            }

            <!-- List Actions. The sort controls hide while «Речь» shows the transcript panel instead
                 of cards; ↶/↷ and the undo toast stay (Ctrl+Z works there too). -->
            <div class="flex justify-between items-center gap-2 p-2 border-b border-gray-800 text-xs">
                <!-- Undo / redo toast takes the sort controls' place for 2.5s — same cell (grid,
                     no positioning near the player), so nothing below moves. -->
                <div class="grid flex-1 min-w-0">
                    @if (!(activeTab() === 'speech' && speechMode() === 'text')) {
                    <div class="col-start-1 row-start-1 flex gap-3" [class.invisible]="!!historyToast()">
                        <button (click)="setSortOrder('time')" [class.text-white]="sortOrder() === 'time'" class="text-gray-400 hover:text-gray-200 transition-colors">По времени</button>
                        <span class="text-gray-700">|</span>
                        <button (click)="setSortOrder('count')" [class.text-white]="sortOrder() === 'count'" class="text-gray-400 hover:text-gray-200 transition-colors">
                           @if (activeTab() === 'search') { По совпадениям }
                           @else if (activeTab() === 'clips') { По просмотрам }
                           @else if (activeTab() === 'speech') { По оценке }
                           @else { По активности }
                        </button>
                    </div>
                    }
                    <div role="status" aria-live="polite" class="col-start-1 row-start-1 min-w-0 truncate text-gray-200 pointer-events-none">{{ historyToast() }}</div>
                </div>
                <div class="flex items-center gap-2 shrink-0">
                    <div class="flex items-center">
                        <button type="button" (click)="undo()" [disabled]="undoDisabled()" [appTooltip]="undoTooltip()" aria-label="Отменить"
                                class="p-1 rounded text-gray-400 hover:text-white hover:bg-gray-800 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-400 disabled:cursor-default transition-colors">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" /></svg>
                        </button>
                        <button type="button" (click)="redo()" [disabled]="redoDisabled()" [appTooltip]="redoTooltip()" aria-label="Повторить"
                                class="p-1 rounded text-gray-400 hover:text-white hover:bg-gray-800 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-400 disabled:cursor-default transition-colors">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M15 15l6-6m0 0l-6-6m6 6H9a6 6 0 000 12h3" /></svg>
                        </button>
                    </div>
                    @if(canMerge() && activeTab() === 'all') {
                        <button (click)="mergeSelectedHighlights()" class="text-purple-400 hover:text-purple-300 font-bold animate-pulse">
                            Объединить ({{ selectedToMerge().size }})
                        </button>
                    }
                </div>
            </div>

            <!-- Merge banner — ~10s after a merge, while it is still the step Ctrl+Z would undo
                 («Отменить» = that undo, through the shared history). -->
            @if (mergeBanner()) {
              <div class="flex items-center justify-between gap-2 px-3 py-2 bg-purple-900/30 border-b border-purple-700/40 text-xs animate-fade-in">
                <span class="text-purple-200">{{ mergeBannerText() }}</span>
                <button (click)="undoFromMergeBanner()"
                        [appTooltip]="mergeUndoTooltip()"
                        class="px-2 py-0.5 rounded bg-purple-700/50 hover:bg-purple-600 text-purple-100 font-medium transition-colors">
                   Отменить
                </button>
              </div>
            }

            <!-- Scrollable List. Below md: no independent scroll region — the
                 outer split-view grid is the single page scroller (see grid
                 comment above); at md+ unchanged (own scroll, two even columns). -->
            @if (activeTab() === 'speech' && speechMode() === 'text' && isAdmin()) {
              @if (transcript.status() === 'ready') {
                <app-transcript-panel [activeLine]="transcriptActiveLine()" [range]="transcriptRange()" (seek)="onTranscriptSeek($event)"></app-transcript-panel>
              } @else {
                <div class="text-center p-8 text-gray-500 italic text-sm">
                  @if (transcript.status() === 'loading') { Загружаю транскрипт… }
                  @else if (transcript.status() === 'error') { {{ transcript.errorMessage() }} }
                  @else {
                    Транскрипта для этого VOD нет.<br>
                    <span class="text-[11px] not-italic">Запустите на ПК: <code class="select-all font-mono text-teal-300 bg-gray-800 px-1.5 py-0.5 rounded">node tools/transcribe/transcribe-vod.mjs {{ videoId() }}</code></span>
                  }
                </div>
              }
            } @else {
            <div class="flex-1 p-2 space-y-2 scrollbar-thin scrollbar-thumb-gray-800 md:overflow-y-auto" [attr.data-tour]="ANCHORS.HIGHLIGHTS_LIST">
               @for (highlight of visibleHighlights(); track highlight.source + '-' + highlight.startTime; let isFirst = $first) {
                   <div
                      class="relative group rounded border border-transparent hover:border-gray-700 hover:bg-gray-800 transition-all cursor-pointer p-2 flex gap-3 border-l-4"
                      [ngClass]="getHighlightCardClasses(highlight)"
                      [attr.id]="highlightCardId(highlight)"
                      [attr.data-tour]="cardDataTour(highlight, isFirst)"
                      (click)="selectHighlight(highlight.startTime)">

                      <!-- Merge Checkbox -->
                       @if(activeTab() === 'all' && !isSearchHighlight(highlight) && !isTranscriptHighlight(highlight)) {
                        <div class="flex items-center" (click)="$event.stopPropagation()">
                            <input type="checkbox"
                                  [checked]="selectedToMerge().has(highlight.startTime)"
                                  (change)="toggleMergeSelection(highlight.startTime)"
                                  class="rounded bg-gray-700 border-gray-600 text-indigo-600 focus:ring-0 cursor-pointer h-3 w-3">
                        </div>
                      }

                      <div class="flex-1 min-w-0">
                          <!-- Top metadata row: timecode + sentiment/msg (or search-query badge) on left; AI/queue on right -->
                          <div class="flex justify-between items-start mb-1 gap-2">
                              <div class="flex items-center gap-2 min-w-0 flex-wrap">
                                 <span class="font-mono text-indigo-300 font-bold text-xs">{{ highlight.timestamp }}</span>
                                 @if (isTranscriptHighlight(highlight)) {
                                    <span class="px-1.5 py-0.5 rounded text-[10px] font-bold border flex items-center gap-1 truncate"
                                          [ngClass]="transcriptBadgeClass(highlight)"
                                          [appTooltip]="highlight.source === 'transcript-search' ? 'ИИ-поиск по речи стримера' : 'Участок из речи стримера (ИИ)'">
                                       <svg xmlns="http://www.w3.org/2000/svg" class="h-2.5 w-2.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" /></svg>
                                       <span class="truncate">{{ highlight.source === 'transcript-search' ? highlight.searchQuery : transcriptCategoryLabel(highlight) }}</span>
                                       <span class="opacity-80">· {{ highlight.confidence }}{{ highlight.source === 'transcript-search' ? '%' : '' }}</span>
                                    </span>
                                    @if (highlight.transcriptTag) {
                                       <span class="text-[10px] text-gray-300 italic truncate max-w-[150px]" [appTooltip]="highlight.transcriptTag">{{ highlight.transcriptTag }}</span>
                                    }
                                    <span class="text-[10px] text-gray-500">{{ formatSpan(highlight.endTime - highlight.startTime) }}</span>
                                    @if (highlight.transcriptWithChat) {
                                       <span class="text-[10px] text-sky-300/80" appTooltip="Найдено по речи вместе с чатом">+ чат</span>
                                    }
                                 } @else if (highlight.source === 'search-ai') {
                                    <button type="button"
                                       (click)="toggleSamplesPopover(highlight.startTime, $event)"
                                       [attr.data-tour]="isFirst ? ANCHORS.SEARCH_RESULT_BADGE : null"
                                       class="px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-600/30 hover:bg-purple-600/50 text-purple-300 border border-purple-600/40 flex items-center gap-1 truncate cursor-pointer transition-colors"
                                       [appTooltip]="'Клик: показать сообщения по запросу «' + highlight.searchQuery + '»'">
                                       <svg xmlns="http://www.w3.org/2000/svg" class="h-2.5 w-2.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zM12 2a1 1 0 011 1v1h1a1 1 0 110 2h-1v1a1 1 0 11-2 0v-1h-1a1 1 0 110-2h1v-1a1 1 0 011-1z" clip-rule="evenodd" /></svg>
                                       <span class="truncate">{{ highlight.searchQuery }}</span>
                                       <span class="text-purple-200">· {{ highlight.confidence }}%</span>
                                    </button>
                                 } @else if (isSearchHighlight(highlight)) {
                                    <button type="button"
                                       (click)="toggleSamplesPopover(highlight.startTime, $event)"
                                       [attr.data-tour]="isFirst ? ANCHORS.SEARCH_RESULT_BADGE : null"
                                       class="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-600/30 hover:bg-amber-600/50 text-amber-300 border border-amber-600/40 flex items-center gap-1 truncate cursor-pointer transition-colors"
                                       [appTooltip]="'Клик: показать сообщения по запросу «' + highlight.searchQuery + '»'">
                                       <svg xmlns="http://www.w3.org/2000/svg" class="h-2.5 w-2.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                                       <span class="truncate">{{ highlight.searchQuery }}</span>
                                       <span class="text-amber-200">· {{ highlight.matchCount }}</span>
                                    </button>
                                 } @else {
                                    <span [class]="'px-1.5 py-0.5 rounded text-[10px] font-medium ' + getSentimentClasses(highlight.sentiment)">{{ getSentimentRussian(highlight.sentiment) }}</span>
                                    <span class="text-[10px] text-gray-500">{{ highlight.messageCount }} {{ highlight.source === 'user-clip' ? 'просм' : 'msg' }}</span>
                                    @if (highlight.source === 'user-clip' && highlight.clipCreatorName) {
                                       <span class="text-[10px] text-purple-400/60 truncate max-w-[100px]">· {{ highlight.clipCreatorName }}</span>
                                    }
                                 }
                              </div>
                              <div class="flex gap-1.5 items-center shrink-0">
                                  @if (highlight.source === 'stream-marker') {
                                    <span [appTooltip]="'Метка от ' + (highlight.markerSourceName || '—')" class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-cyan-900/50 text-cyan-200 border border-cyan-600/50 text-[10px] font-bold tracking-wider max-w-[140px]">
                                      <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" /></svg>
                                      <span class="truncate">{{ highlight.markerSourceName || 'METKA' }}</span>
                                    </span>
                                  }
                                  @if (highlight.source === 'ai-deep-dive') {
                                    <span appTooltip="Найдено глубоким AI-анализом" class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-purple-900/50 text-purple-200 border border-purple-600/50 text-[10px] font-bold tracking-wider">
                                      <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zM12 2a1 1 0 011 1v1h1a1 1 0 110 2h-1v1a1 1 0 11-2 0v-1h-1a1 1 0 110-2h1v-1a1 1 0 011-1z" clip-rule="evenodd" /></svg>
                                      <span>AI</span>
                                    </span>
                                  }
                                  @if (highlight.source === 'user-clip') {
                                    <a [href]="highlight.clipUrl" target="_blank" rel="noopener"
                                       (click)="$event.stopPropagation()"
                                       [appTooltip]="'Открыть оригинал на Twitch · ' + (highlight.clipViewCount || 0) + ' просмотров'"
                                       class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-purple-900/50 hover:bg-purple-800/60 text-purple-200 border border-purple-600/50 text-[10px] font-bold tracking-wider transition-colors">
                                      <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                                      <span>КЛИП</span>
                                    </a>
                                  }
                                  @if (isHighlightInQueue(highlight.startTime)) {
                                     <span class="text-green-400 animate-spin" appTooltip="Processing in Cloud"><svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg></span>
                                  }
                              </div>
                          </div>
                          @if (isTranscriptHighlight(highlight)) {
                             @if (highlight.transcriptTitle) {
                                <p class="text-sm text-gray-100 font-semibold leading-snug mb-0.5">{{ highlight.transcriptTitle }}</p>
                             }
                             <p [class]="'text-xs text-gray-300 leading-snug mb-1 ' + (currentHighlight()?.startTime === highlight.startTime ? '' : 'line-clamp-2')">{{ highlight.description }}</p>
                             @if (highlight.transcriptQuote && currentHighlight()?.startTime === highlight.startTime) {
                                <p class="text-xs text-teal-200/80 italic leading-snug mb-1 border-l-2 border-teal-700/60 pl-2">«{{ highlight.transcriptQuote }}»</p>
                             }
                          } @else if (isSearchHighlight(highlight) && highlight.sampleMessages?.length) {
                             <p [class]="'text-xs text-gray-400 leading-snug mb-1 ' + (currentHighlight()?.startTime === highlight.startTime ? '' : 'line-clamp-2')">
                                @if (renderSample(highlight.sampleMessages[0], highlight.matchedKeywords); as rs) {
                                   @if (rs.author) {
                                      <span class="text-indigo-400 font-semibold">{{ rs.author }}:</span>
                                      <span>&nbsp;</span>
                                   }
                                   @for (seg of rs.segments; track $index) {
                                      @if (seg.isMatch) {
                                         <mark class="bg-amber-500/40 text-white rounded px-0.5">{{ seg.text }}</mark>
                                      } @else {
                                         <span>{{ seg.text }}</span>
                                      }
                                   }
                                }
                             </p>
                          } @else {
                             <p [class]="'text-sm text-gray-300 leading-snug mb-1 ' + (currentHighlight()?.startTime === highlight.startTime ? '' : 'line-clamp-2')">
                                {{ highlight.description }}
                             </p>
                          }
                          <!-- Subsumed badge: this moment's startTime falls inside a pinned highlight's
                               extended clip range, so it's already covered by the parent in Избранном. -->
                          @if (containingPinnedByStartTime().get(highlight.startTime); as parent) {
                             <div class="text-[10px] text-gray-500 italic mb-1 flex items-center gap-1"
                                  [appTooltip]="'Этот момент попадает в расширенный диапазон закреплённого момента: ' + (parent.description || '')">
                                <svg class="h-3 w-3 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                                   <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
                                </svg>
                                <span>В избранном: {{ parent.timestamp }}</span>
                             </div>
                          }
                          <!-- Bottom action row (V2: always visible, primary green Download with size in label) -->
                          <div class="flex gap-1.5 mt-2" (click)="$event.stopPropagation()">
                             @if (activeTab() !== 'deleted') {
                                <button (click)="downloadClip(highlight, $event)"
                                        [disabled]="!isFirebaseReady() || isHighlightInQueue(highlight.startTime) || clipExhausted()"
                                        [attr.data-tour]="isFirst ? ANCHORS.BTN_DOWNLOAD_CLIP : null"
                                        class="flex-1 py-1.5 px-2 rounded text-xs font-semibold flex items-center justify-center gap-1.5 bg-green-900/30 hover:bg-green-800/50 text-green-200 border border-green-700/40 hover:border-green-600/50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors min-w-0"
                                        [appTooltip]="clipTooltip()">
                                   @if (isHighlightInQueue(highlight.startTime)) {
                                      <svg class="animate-spin h-3 w-3 shrink-0" viewBox="0 0 24 24" aria-hidden="true"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                      <span class="truncate">В очереди...</span>
                                   } @else {
                                      <svg class="h-3.5 w-3.5 shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                                      <span class="truncate">Скачать{{ highlight.estimatedSizeMb ? ' · ' + highlight.estimatedSizeMb + ' MB' : '' }}</span>
                                      @if (clipBadge(); as b) {
                                         <span class="text-[10px] px-1 rounded font-mono shrink-0"
                                               [class.bg-amber-500]="clipQuotaLow()"
                                               [class.bg-opacity-30]="clipQuotaLow()"
                                               [class.bg-green-900]="!clipQuotaLow()"
                                               [class.bg-opacity-60]="!clipQuotaLow()">{{ b }}</span>
                                      }
                                   }
                                </button>
                                @if ((isSearchHighlight(highlight) && activeTab() === 'search') || (highlight.source === 'user-clip' && activeTab() === 'clips') || (isTranscriptHighlight(highlight) && activeTab() === 'speech')) {
                                   <button (click)="togglePromoteHighlight(highlight.startTime, $event)"
                                           [appTooltip]="highlight.isPromoted ? 'Убрать из общего списка (.)' : 'Добавить в общий список (.)'"
                                           [class]="highlight.isPromoted
                                              ? 'flex-1 py-1.5 px-2 rounded text-xs font-semibold flex items-center justify-center gap-1.5 bg-amber-900/40 hover:bg-amber-800/50 text-amber-200 border border-amber-700/50 hover:border-amber-600/50 transition-colors min-w-0'
                                              : 'flex-1 py-1.5 px-2 rounded text-xs font-semibold flex items-center justify-center gap-1.5 bg-gray-800/60 hover:bg-amber-900/40 hover:text-amber-200 hover:border-amber-700/50 text-gray-300 border border-gray-700/50 transition-colors min-w-0'">
                                      @if (highlight.isPromoted) {
                                         <svg class="h-3.5 w-3.5 shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3" aria-hidden="true">
                                            <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                                         </svg>
                                         <span class="truncate">Добавлено</span>
                                      } @else {
                                         <svg class="h-3.5 w-3.5 shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
                                            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
                                         </svg>
                                         <span class="truncate">Добавить</span>
                                      }
                                   </button>
                                }
                                <button (click)="togglePinHighlight(highlight.startTime, $event)"
                                        [attr.data-tour]="isFirst ? ANCHORS.CARD_BTN_PIN : null"
                                        [class]="highlight.isPinned
                                           ? 'px-2.5 py-1.5 rounded flex items-center justify-center transition-colors text-yellow-300 bg-yellow-900/40 border border-yellow-600/50'
                                           : 'px-2.5 py-1.5 rounded flex items-center justify-center transition-colors text-gray-400 bg-gray-800/50 hover:bg-gray-700 hover:text-yellow-400 border border-gray-700/50'"
                                        [appTooltip]="highlight.isPinned ? 'Убрать из избранного' : 'Закрепить в избранном'">
                                   <svg class="h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path d="M5 4a2 2 0 012-2h6a2 2 0 012 2v14l-5-2.5L5 18V4z" /></svg>
                                </button>
                                <button (click)="softDeleteHighlight(highlight.startTime, $event)"
                                        [attr.data-tour]="isFirst ? ANCHORS.CARD_BTN_TRASH : null"
                                        class="px-2.5 py-1.5 rounded bg-gray-800/50 hover:bg-gray-700 text-gray-400 hover:text-red-400 border border-gray-700/50 flex items-center justify-center transition-colors"
                                        appTooltip="Удалить (можно восстановить из корзины)">
                                   <svg class="h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                </button>
                             } @else {
                                <button (click)="restoreHighlight(highlight.startTime, $event)"
                                        class="flex-1 py-1.5 rounded text-xs font-semibold flex items-center justify-center gap-1.5 bg-gray-700 hover:bg-green-700/60 hover:text-green-100 text-gray-200 transition-colors"
                                        appTooltip="Восстановить из корзины">
                                   <svg class="h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" d="M15.707 15.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 010 1.414zm-6 0a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 011.414 1.414L5.414 10l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd" /></svg>
                                   <span>Восстановить</span>
                                </button>
                             }
                          </div>

                          <!-- Promote moved into action row above. Marker block stays for ВСЕ tab. -->
                          @if (isSearchHighlight(highlight) && activeTab() !== 'search' && highlight.isPromoted) {
                             <!-- In ВСЕ tab, mark promoted search results visibly -->
                             <div class="mt-1.5 text-[10px] text-amber-400/80 flex items-center gap-1">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" /></svg>
                                Из поиска
                             </div>
                          }
                          @if (highlight.source === 'user-clip' && activeTab() !== 'clips' && highlight.isPromoted) {
                             <div class="mt-1.5 text-[10px] text-purple-400/80 flex items-center gap-1">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" /></svg>
                                Из клипов
                             </div>
                          }
                          @if (isTranscriptHighlight(highlight) && activeTab() !== 'speech' && highlight.isPromoted) {
                             <div class="mt-1.5 text-[10px] text-teal-400/80 flex items-center gap-1">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" /></svg>
                                Из речи
                             </div>
                          }
                      </div>
                   </div>
               }
               @if (sortedHighlights().length === 0) {
                 @if (activeTab() === 'search') {
                   @if (searchQuery()) {
                     <div class="text-center p-8 text-gray-500 italic text-sm">
                        По запросу «{{ searchQuery() }}» ничего не найдено.<br>
                        <span class="text-[10px] not-italic">Попробуйте другое слово или включите «точное совпадение».</span>
                     </div>
                   } @else {
                     <div class="text-center p-8 text-gray-500 italic text-sm">
                        Введите слово и нажмите «Найти».<br>
                        <span class="text-[10px] not-italic">Одно слово «мина» уже ловит мине/мину/мины (при выключенном «точном совпадении»).</span>
                        <br>
                        <span class="text-[10px] not-italic">Запятая = ИЛИ: «мина, граната» найдёт оба корня.</span>
                     </div>
                   }
                 } @else if (activeTab() === 'clips') {
                   @if (isLoadingClips()) {
                     <div class="text-center p-8 text-purple-400/70 italic text-sm flex items-center justify-center gap-2">
                        <svg class="animate-spin h-4 w-4" viewBox="0 0 24 24" aria-hidden="true"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                        Загружаем клипы…
                     </div>
                   } @else {
                     <div class="text-center p-8 text-gray-500 italic text-sm">
                        У этого VOD нет пользовательских клипов.<br>
                        <span class="text-[10px] not-italic">Twitch /clips отдаёт только клипы с привязкой к ВОДу. Sub-only стримы и live-pending клипы пропускаются.</span>
                     </div>
                   }
                 } @else if (activeTab() === 'speech') {
                   <div class="text-center p-6 text-gray-500 italic text-sm">
                     @if (transcript.status() === 'missing') {
                       Транскрипта для этого VOD нет.<br>
                       <span class="text-[11px] not-italic">Запустите на ПК: <code class="select-all font-mono text-teal-300 bg-gray-800 px-1.5 py-0.5 rounded">node tools/transcribe/transcribe-vod.mjs {{ videoId() }}</code></span>
                     } @else if (transcript.status() === 'loading') {
                       Загружаю транскрипт…
                     } @else if (speechMode() === 'ai-search') {
                       Опишите момент — ИИ найдёт его в речи стримера.
                     } @else {
                       Нажмите «Найти участки» — ИИ прочитает речь стримера и отметит интересное на дорожке под тепловой картой.
                     }
                   </div>
                 } @else {
                   <div class="text-center p-8 text-gray-500 italic text-sm">Нет моментов</div>
                 }
               }
            </div>
            }
        }
      </div>
    </div>

    <!-- Mobile-only control bar ("пульт управления") — on-screen equivalents
         for keyboard-only shortcuts that have no other touch path (play/pause,
         seek, set clip start/end), plus chat-sheet + jump-to-highlights. Never
         mounts on desktop (isMobileLayout() false), zero render cost there.
         Hidden while the sidebar drawer is open — same z-30 as the drawer's
         own backdrop, but painted after it in DOM order (it lives inside
         <main>, later than <aside>/backdrop), so on a viewport narrower than
         the drawer's w-80 it would otherwise peek out from the strip the
         drawer doesn't cover and swallow taps meant to dismiss the drawer. -->
    @if (viewport.isMobileLayout() && isProcessed() && isSidebarCollapsed()) {
      <app-mobile-control-bar
          class="md:hidden fixed inset-x-0 bottom-0 z-30"
          [isPlaying]="!isPaused()"
          [isChatOpen]="isChatSheetOpen()"
          (playPauseToggle)="togglePlayPause()"
          (seekBack)="seekRelative(-10)"
          (seekForward)="seekRelative(10)"
          (setClipStart)="setClipStartToPlayhead()"
          (setClipEnd)="setClipEndToPlayhead()"
          (toggleChat)="toggleMobileChatSheet()"
          (jumpToHighlights)="jumpToHighlights()"
          [canUndo]="!undoDisabled()"
          [canRedo]="!redoDisabled()"
          [toast]="historyToast()"
          (undo)="undo()"
          (redo)="redo()">
      </app-mobile-control-bar>
    }
  </main>
</div>

<!-- Modals -->
@if (isLibraryOpen()) {
  <app-channel-library-overlay
      (close)="closeLibrary()"
      (loadVideo)="onVideoSelectedFromLibrary($event)">
  </app-channel-library-overlay>
}

@if (showHelpModal()) {
  <app-help-modal (close)="closeHelpModal()" (openPathHelp)="openPathHelp()" (restartTour)="restartTourFromHelp()"></app-help-modal>
}

@if (showMarkerSourcesModal()) {
  <app-marker-sources-modal (close)="closeMarkerSourcesModal()"></app-marker-sources-modal>
}

@if (isAdmin() && isAdminPanelOpen()) {
  <app-admin-panel (close)="closeAdminPanel()"></app-admin-panel>
}

<!-- Demo CTA: shown when a demo user tries to do something requiring auth (download a clip, etc) -->
@if (demoService.signInPromptVisible()) {
  <div class="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
       style="z-index: 10500;"
       (click)="demoService.dismissSignInPrompt()">
    <div class="bg-gray-900 border border-indigo-500/40 rounded-2xl max-w-md w-full p-6 shadow-2xl shadow-indigo-900/30 relative"
         (click)="$event.stopPropagation()">
      <button (click)="demoService.dismissSignInPrompt()"
              class="absolute top-3 right-3 text-gray-500 hover:text-gray-200 p-1">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
      </button>

      <div class="w-14 h-14 mx-auto mb-4 bg-gray-800 border border-indigo-500/30 rounded-2xl flex items-center justify-center">
        <svg class="w-7 h-7 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 11c0-1.105.895-2 2-2s2 .895 2 2v2m-7 0h10a2 2 0 012 2v5a2 2 0 01-2 2H7a2 2 0 01-2-2v-5a2 2 0 012-2zM9 11V8a3 3 0 016 0v3"/></svg>
      </div>

      <h2 class="text-xl font-bold text-center text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400 mb-2">
        Чтобы скачать клип, нужен вход
      </h2>
      <p class="text-sm text-gray-400 text-center mb-5 leading-relaxed">
        Вы в демо-режиме. Чтобы скачивать клипы и анализировать свои стримы — войдите через Twitch.
      </p>

      <button (click)="loginWithTwitch()"
              class="w-full py-3 px-4 bg-[#9146FF] hover:bg-[#7b32e6] text-white font-bold rounded-lg transition-transform hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-3 shadow-lg shadow-[#9146FF]/20">
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/></svg>
        Войти через Twitch
      </button>

      <button (click)="demoService.dismissSignInPrompt()"
              class="w-full mt-2 py-2 px-4 text-gray-400 hover:text-gray-200 text-sm transition-colors">
        Продолжить демо
      </button>
    </div>
  </div>
}

@if (isPlayerFocused()) {
  <div class="fixed bottom-4 right-4 z-50 bg-black/80 border border-yellow-600 text-yellow-500 px-3 py-2 rounded text-xs pointer-events-none">
     Фокус на плеере (Нажмите ESC чтобы выйти)
  </div>
}

@if (showPathHelp()) {
  <app-path-help-modal (close)="closePathHelp()"></app-path-help-modal>
}

<!-- Heatmap legend popover («?» in the heatmap gutter). Wide and short on purpose: placed
     with the player as a keep-out rect (placeFloating), it usually opens over the heatmap
     itself, where the room under the player is ~110px. Rendered hidden first, measured,
     then positioned (openLegend). -->
@if (legendPopover(); as lp) {
  <div #legendPopoverEl
       class="fixed z-50 w-[720px] max-w-[calc(100vw-8px)] px-3 py-1.5 rounded-lg bg-gray-900 border border-gray-700 shadow-2xl shadow-black/60 text-[11px] leading-4 text-gray-300"
       [style.top.px]="lp.top"
       [style.left.px]="lp.left"
       [style.visibility]="lp.ready ? 'visible' : 'hidden'"
       (mouseenter)="onLegendPopoverHover(true)"
       (mouseleave)="onLegendPopoverHover(false)"
       (click)="$event.stopPropagation()">
    <div class="grid grid-cols-5 gap-x-3 gap-y-0.5">
      <span class="flex items-center gap-1.5"><span class="inline-block w-2.5 h-2.5 rounded-sm bg-gray-700 shrink-0"></span>сообщения за 15 с</span>
      <span class="flex items-center gap-1.5"><span class="inline-block w-2.5 h-2.5 rounded-sm bg-yellow-500/80 shrink-0"></span>пик (выше порога)</span>
      <span class="flex items-center gap-1.5"><span class="inline-block w-2.5 h-2.5 rounded-sm bg-indigo-400 shrink-0"></span>выбранный момент</span>
      <span class="flex items-center gap-1.5"><span class="inline-block w-2.5 h-2.5 rounded-sm bg-amber-400 shrink-0"></span>совпадения поиска</span>
      <span class="flex items-center gap-1.5"><span class="inline-block shrink-0" style="width: 0; height: 0; border-left: 5px solid transparent; border-right: 5px solid transparent; border-top: 7px solid #22d3ee;"></span>метка StreamMarkers</span>
      <span class="flex items-center gap-1.5"><span class="inline-block shrink-0" style="width: 0; height: 0; border-left: 5px solid transparent; border-right: 5px solid transparent; border-top: 7px solid #a855f7;"></span>клип зрителя</span>
      <span class="flex items-center gap-1.5"><span class="inline-block w-2.5 h-2.5 rounded-sm bg-gray-950 ring-1 ring-white/70 shrink-0"></span>вне области</span>
      <span class="flex items-center gap-1.5"><span class="inline-block w-0.5 h-3 bg-red-500 shrink-0 ml-1 mr-1"></span>плеер сейчас</span>
      <span class="flex items-center gap-1.5"><span class="inline-block w-3 h-1.5 rounded-sm bg-indigo-500 shrink-0"></span>клип (края тянутся)</span>
      @if (isAdmin()) {
        <span class="flex items-center gap-1.5"><span class="inline-block w-3 h-1.5 rounded-sm bg-teal-400 shrink-0"></span>речь</span>
      }
    </div>
    <div class="mt-1 pt-1 border-t border-gray-800 flex flex-wrap gap-x-3 text-gray-400">
      <span><span class="text-gray-200">Колесо</span> — прокрутка</span>
      <span><span class="text-gray-200">Ctrl+колесо</span> — масштаб</span>
      <span><span class="text-gray-200">Клик по шкале</span> — перейти, <span class="text-gray-200">протянуть</span> — найти место</span>
      <span><span class="text-gray-200">Клик по столбику</span> — момент</span>
      <span><span class="text-gray-200">Z</span> / двойной клик по клипу — приблизить к клипу</span>
      <span><span class="text-gray-200">Alt+I / Alt+O</span> — края области по плееру</span>
      <span><span class="text-gray-200">Alt+P</span> — область вкл/выкл</span>
      <span><span class="text-gray-200">Ctrl+Z / Ctrl+Y</span> — отменить / повторить</span>
      <span>Шкала лог/лин — меню профиля</span>
    </div>
  </div>
}

<!-- Search samples popover — messages that triggered a search-* highlight, with matched words marked. -->
@if (samplesPopover(); as popover) {
  @if (popoverHighlight(); as h) {
    <div [class]="'fixed z-50 bg-gray-900 rounded-lg shadow-2xl shadow-black/60 overflow-hidden flex flex-col animate-fade-in border ' + (h.source === 'search-ai' ? 'border-purple-600/40' : 'border-amber-600/40')"
         [attr.data-tour]="ANCHORS.SEARCH_SAMPLES_POPOVER"
         [style.top.px]="popover.top"
         [style.left.px]="popover.left"
         [style.width.px]="popover.width"
         (click)="$event.stopPropagation()">
       <div class="flex items-center justify-between p-2 border-b border-gray-800 bg-gray-900/95">
          <div [class]="'flex items-center gap-1.5 text-xs font-bold min-w-0 ' + (h.source === 'search-ai' ? 'text-purple-300' : 'text-amber-300')">
             @if (h.source === 'search-ai') {
                <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zM12 2a1 1 0 011 1v1h1a1 1 0 110 2h-1v1a1 1 0 11-2 0v-1h-1a1 1 0 110-2h1v-1a1 1 0 011-1z" clip-rule="evenodd" /></svg>
             } @else {
                <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
             }
             <span class="truncate">«{{ h.searchQuery }}»</span>
             @if (h.source === 'search-ai') {
                <span class="text-gray-500 font-normal flex-shrink-0">· {{ h.confidence }}% уверенности</span>
             } @else {
                <span class="text-gray-500 font-normal flex-shrink-0">· {{ h.matchCount }} {{ h.matchCount === 1 ? 'совпадение' : 'совп.' }}</span>
             }
          </div>
          <button (click)="closeSamplesPopover()" class="text-gray-500 hover:text-gray-200 flex-shrink-0 ml-2" appTooltip="Закрыть (Esc)">
             <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
       </div>
       <div class="overflow-y-auto max-h-[50vh] p-2 space-y-1.5 scrollbar-thin scrollbar-thumb-gray-700">
          @for (sample of h.sampleMessages; track $index) {
             <div class="text-xs leading-relaxed">
                @if (renderSample(sample, h.matchedKeywords); as rs) {
                   @if (rs.author) {
                      <span class="text-indigo-400 font-semibold">{{ rs.author }}:</span>
                      <span>&nbsp;</span>
                   }
                   @for (seg of rs.segments; track $index) {
                      @if (seg.isMatch) {
                         <mark class="bg-amber-500/50 text-white rounded px-0.5">{{ seg.text }}</mark>
                      } @else {
                         <span class="text-gray-300">{{ seg.text }}</span>
                      }
                   }
                }
             </div>
          }
          @if (!h.sampleMessages?.length) {
             <div class="text-gray-500 italic text-xs p-2">Нет примеров сообщений.</div>
          }
       </div>
    </div>
  }
}

@if (showProModal()) {
  <div class="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center animate-fade-in p-4">
    <div class="bg-gray-900 border border-purple-500/50 rounded-xl shadow-2xl max-w-md w-full overflow-hidden flex flex-col relative z-10">

      <div class="p-6 text-center space-y-4">
        <div class="w-16 h-16 mx-auto bg-purple-900/30 text-purple-400 rounded-full flex items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
          </svg>
        </div>
        <h2 class="text-2xl font-bold text-white">{{ proModalReason() ? 'Лимит исчерпан' : 'Про-функция' }}</h2>
        @if (proModalReason(); as reason) {
          <p class="text-gray-400 text-sm leading-relaxed">{{ reason }} С <strong>PRO</strong> подпиской дневные лимиты выше.</p>
        } @else {
          <p class="text-gray-400 text-sm leading-relaxed">Экспорт пакетов через <strong>сценарии (.bat)</strong> и загрузка <strong>ZIP архивов</strong> через облако доступны только для пользователей с <strong>PRO</strong> подпиской.</p>
        }
      </div>

      <div class="p-4 bg-gray-800/50 border-t border-gray-800 flex justify-between gap-3">
        <button (click)="closeProModal()" class="px-4 py-2 border border-gray-600 hover:bg-gray-700 text-gray-300 rounded transition-colors text-sm font-medium">Закрыть</button>
        <button onclick="window.open('/pricing', '_blank')" class="px-6 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white rounded shadow-lg shadow-purple-900/30 transition-transform active:scale-95 text-sm font-bold">
          Перейти на PRO
        </button>
      </div>
    </div>
  </div>
}

@if (syncConflict(); as conflict) {
  <div class="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center animate-fade-in p-4">
    <div class="bg-gray-900 border border-amber-500/40 rounded-xl shadow-2xl max-w-md w-full overflow-hidden flex flex-col relative z-10">
      <div class="p-6 text-center space-y-4">
        <div class="w-16 h-16 mx-auto bg-amber-900/30 text-amber-400 rounded-full flex items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4a2 2 0 00-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z" /></svg>
        </div>
        <h2 class="text-xl font-bold text-white">Конфликт синхронизации</h2>
        <p class="text-gray-200 text-sm font-medium break-words">{{ syncConflictTitle(conflict) }}</p>
        <p class="text-gray-400 text-sm leading-relaxed">
          Этот стрим изменили на другом устройстве{{ syncConflictRemoteTime(conflict) ? ' (' + syncConflictRemoteTime(conflict) + ')' : '' }},
          а на этом есть правки, которых нет в облаке. Какую версию оставить?
          Вторая будет заменена.
        </p>
      </div>
      <div class="p-4 bg-gray-800/50 border-t border-gray-800 flex justify-between gap-2">
        <button (click)="resolveSyncConflictKeepLocal()" class="px-4 py-2 border border-gray-600 hover:bg-gray-700 text-gray-300 rounded transition-colors text-sm font-medium">С этого устройства</button>
        <button (click)="resolveSyncConflictUseServer()" class="px-6 py-2 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white rounded shadow-lg shadow-amber-900/30 transition-transform active:scale-95 text-sm font-bold">Из облака</button>
      </div>
    </div>
  </div>
}

@if (showSyncMigrationPrompt()) {
  <div class="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center animate-fade-in p-4">
    <div class="bg-gray-900 border border-indigo-500/40 rounded-xl shadow-2xl max-w-md w-full overflow-hidden flex flex-col relative z-10">

      <div class="p-6 text-center space-y-4">
        <div class="w-16 h-16 mx-auto bg-indigo-900/30 text-indigo-400 rounded-full flex items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
          </svg>
        </div>
        <h2 class="text-xl font-bold text-white">Синхронизация с облаком</h2>
        <p class="text-gray-400 text-sm leading-relaxed">
          В вашей библиотеке локально хранится
          <strong class="text-gray-200">{{ syncLocalChannelsCount() }} каналов</strong>
          и <strong class="text-gray-200">{{ syncLocalProjectsCount() }} проектов</strong>.
          Загрузить их в облако для синхронизации между устройствами?
        </p>

        @if (syncMigrationProgress(); as progress) {
          @if (syncMigrationRunning() || progress.phase === 'done' || progress.phase === 'error') {
            <div class="space-y-2">
              <p class="text-xs text-gray-400">{{ progress.message }}</p>
              @if (progress.total > 0 && progress.phase !== 'done' && progress.phase !== 'error') {
                <div class="w-full bg-gray-800 rounded-full h-2">
                  <div class="bg-indigo-500 h-2 rounded-full transition-all" [style.width.%]="progress.total === 0 ? 0 : (progress.current / progress.total * 100)"></div>
                </div>
                <p class="text-[10px] text-gray-500">{{ progress.current }} / {{ progress.total }}</p>
              }
              @if (progress.phase === 'error') {
                <p class="text-xs text-red-400">Не удалось завершить миграцию. Попробуйте ещё раз позже.</p>
              }
            </div>
          }
        }
      </div>

      <div class="p-4 bg-gray-800/50 border-t border-gray-800 flex justify-between gap-2 flex-wrap">
        <button (click)="dismissSyncMigrationForever()"
                [disabled]="syncMigrationRunning()"
                class="px-3 py-2 text-gray-500 hover:text-gray-300 text-xs transition-colors disabled:opacity-40">Никогда</button>
        <div class="flex gap-2">
          <button (click)="dismissSyncMigrationLater()"
                  [disabled]="syncMigrationRunning()"
                  class="px-4 py-2 border border-gray-600 hover:bg-gray-700 text-gray-300 rounded transition-colors text-sm font-medium disabled:opacity-40">Не сейчас</button>
          <button (click)="startSyncMigration()"
                  [disabled]="syncMigrationRunning()"
                  class="px-6 py-2 bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-500 hover:to-blue-500 text-white rounded shadow-lg shadow-indigo-900/30 transition-transform active:scale-95 text-sm font-bold disabled:opacity-50">
            @if (syncMigrationRunning()) { <span>Загрузка...</span> } @else { <span>Загрузить</span> }
          </button>
        </div>
      </div>
    </div>
  </div>
}

<!-- Onboarding tour overlay (active in demo mode or when user manually restarts) -->
<app-tour-overlay></app-tour-overlay>
  `
})
export class AppComponent implements OnDestroy {
  private document = inject(DOCUMENT) as Document;
  private cdRef = inject(ChangeDetectorRef);

  // Services
  private twitchChatService = inject(TwitchChatService);
  private twitchChatState = inject(TwitchChatStateService);
  private exportService = inject(ExportService);
  private downloadService = inject(DownloadService);
  private firebaseService = inject(FirebaseService);
  private libraryService = inject(LibraryService);
  private syncService = inject(SyncService);
  private syncMigrationService = inject(SyncMigrationService);
  markersService = inject(MarkersService);
  demoService = inject(DemoService);
  tourService = inject(OnboardingTourService);
  quotaService = inject(QuotaService);
  viewport = inject(ViewportService);
  // «Речь» (admin-only, experimental) — every UI entry point is behind isAdmin().
  transcript = inject(TranscriptService);
  // Ctrl+Z / Ctrl+Y over the user's work on the open VOD (see «Undo / redo» below).
  history = inject(UndoHistoryService);
  readonly TRANSCRIPT_CATEGORIES = TRANSCRIPT_CATEGORIES;
  readonly TRANSCRIPT_CATEGORY_KEYS = TRANSCRIPT_CATEGORY_KEYS;
  readonly ANCHORS = TOUR_ANCHORS;

  // Signals linked to state service
  fileName = this.twitchChatState.fileName;
  isLoading = this.twitchChatState.isLoading;
  loadingMessage = this.twitchChatState.loadingMessage;
  isAnalyzingQuick = this.twitchChatState.isAnalyzingQuick;
  isAnalyzingDeep = this.twitchChatState.isAnalyzingDeep;
  isAnalyzingSearch = this.twitchChatState.isAnalyzingSearch;
  quickRetryInfo = this.twitchChatState.quickRetryInfo;
  deepRetryInfo = this.twitchChatState.deepRetryInfo;
  searchRetryInfo = this.twitchChatState.searchRetryInfo;
  globalIsLoading = this.twitchChatState.globalIsLoading;
  globalLoadingMessage = this.twitchChatState.globalLoadingMessage;
  error = this.twitchChatState.error;
  infoMessage = this.twitchChatState.infoMessage;
  progressInfo = this.twitchChatState.progressInfo;
  // 1Hz tick that drives the elapsed-time display in `progressDetails` while
  // an op is running. Started/stopped by an effect tied to progressInfo, so
  // we don't burn a timer when nothing's in flight.
  private progressTick = signal(0);
  private progressTickInterval: ReturnType<typeof setInterval> | null = null;
  progressDetails = computed(() => {
    const p = this.progressInfo();
    if (!p) return '';
    this.progressTick(); // dependency: forces recompute on every tick
    const elapsedSec = Math.max(0, Math.floor((performance.now() - p.startedAt) / 1000));
    const mm = Math.floor(elapsedSec / 60);
    const ss = elapsedSec % 60;
    const time = `${mm}:${String(ss).padStart(2, '0')}`;
    const parts: string[] = [];
    if (typeof p.percent === 'number') parts.push(`${Math.floor(p.percent)}%`);
    if (p.detail) parts.push(p.detail);
    // Live mode swaps "elapsed since op started" for "time since last poll-tick" —
    // gives the user a meaningful "is the loop alive" signal during a multi-hour
    // live stream, where the total elapsed counter would just keep climbing into
    // the hours without telling them whether anything's still happening.
    const lastPoll = this.twitchChatState.lastLivePollAt();
    if (this.twitchChatState.isLive() && lastPoll !== null) {
      const sinceSec = Math.max(0, Math.floor((performance.now() - lastPoll) / 1000));
      if (sinceSec < 60) parts.push(`обновлено ${sinceSec} с назад`);
      else parts.push(`обновлено ${Math.floor(sinceSec / 60)} мин назад`);
    } else {
      parts.push(time);
    }
    return parts.join(' • ');
  });
  // Compact percent label rendered in the collapsed-sidebar footer above the
  // sync dot. Only the number, no elapsed/detail; "…" when indeterminate so
  // the user still sees that something is in flight.
  progressPercentLabel = computed(() => {
    const p = this.progressInfo();
    if (!p) return '';
    return typeof p.percent === 'number' ? `${Math.floor(p.percent)}%` : '…';
  });
  isProcessed = this.twitchChatState.isProcessed;

  streamerName = this.twitchChatState.streamerName;
  videoTitle = this.twitchChatState.videoTitle;
  videoGame = this.twitchChatState.videoGame;
  videoId = this.twitchChatState.videoId;

  activityBins = this.twitchChatState.activityBins;
  highlights = this.twitchChatState.highlights;
  maxActivity = this.twitchChatState.maxActivity;

  // Cached from player.getDuration() on READY. 0 = unknown (no clamp).
  videoDurationSec = signal<number>(0);

  // Heatmap/timeline bins trimmed to the actual video length. Chat timestamps can
  // run slightly past the VOD end (stray late messages), which otherwise makes the
  // timeline extend past the video's real duration with no boundary.
  trimmedBins = computed(() => {
    const bins = this.activityBins();
    const duration = this.videoDurationSec();
    if (duration <= 0) return bins;
    return bins.filter(b => b.startTime < duration);
  });

  peakMessageThreshold = this.twitchChatState.peakMessageThreshold;
  rewindOffsetSeconds = this.twitchChatState.rewindOffsetSeconds;
  sortOrder = this.twitchChatState.sortOrder;
  activeTab = this.twitchChatState.activeTab;
  currentHighlightIndex = this.twitchChatState.currentHighlightIndex;
  selectedToMerge = this.twitchChatState.selectedToMerge;
  isChatOverlayEnabled = this.twitchChatState.isChatOverlayEnabled;
  chatLayoutPreference = this.twitchChatState.chatLayoutPreference;
  // Browser feature-detect: does this browser populate
  // IntersectionObserverEntry.isVisible when trackVisibility:true is passed?
  // Chrome family does (and Twitch's bundle uses it to enforce the overlay
  // block); Firefox/Safari don't (Twitch falls back to inside-iframe
  // checkVisibility which can't see our overlay → playback works fine).
  readonly obscuredEnforcementActive: boolean = (() => {
    try {
      // trackVisibility/delay are IO V2 extensions — not in TS lib types, cast.
      new IntersectionObserver(() => {}, { threshold: 0, trackVisibility: true, delay: 100 } as IntersectionObserverInit);
      return 'isVisible' in IntersectionObserverEntry.prototype;
    } catch { return false; }
  })();
  // Resolved layout = explicit preference overrides auto-detect. Mobile always
  // wins with 'sheet' — the 'side' column (w-1/5 min-w-[240px]) has no
  // stacking fallback and squeezes the player to a sliver on a phone-width
  // viewport, and Chrome's obscuredEnforcementActive (which also fires on
  // Chrome for Android) would otherwise force exactly that 'side' branch by
  // default. Desktop's stored chatLayoutPreference is untouched — it's simply
  // outranked below the mobile breakpoint and resumes unchanged above it.
  effectiveChatLayout = computed<'overlay' | 'side' | 'sheet'>(() => {
    if (this.viewport.isMobileLayout()) return 'sheet';
    const pref = this.chatLayoutPreference();
    if (pref === 'overlay') return 'overlay';
    if (pref === 'side') return 'side';
    return this.obscuredEnforcementActive ? 'side' : 'overlay';
  });

  // Mobile chat bottom-sheet — transient view state (not persisted), same
  // idiom as isFiltersExpanded/isAvatarMenuOpen. Only meaningful when
  // effectiveChatLayout() === 'sheet'.
  isChatSheetOpen = signal(false);
  interfaceScale = this.twitchChatState.interfaceScale;
  heatmapScale = this.twitchChatState.heatmapScale;
  filterSettings = this.twitchChatState.filterSettings;

  isCsvMode = this.twitchChatState.isCsvMode;
  csvVodId = this.twitchChatState.csvVodId;
  csvStreamerName = this.twitchChatState.csvStreamerName;
  csvVideoTitle = this.twitchChatState.csvVideoTitle;
  csvVideoGame = this.twitchChatState.csvVideoGame;

  // Computed from service
  currentHighlight = this.twitchChatState.currentHighlight;
  allHighlights = this.twitchChatState.allHighlights;
  pinnedHighlights = this.twitchChatState.pinnedHighlights;
  deletedHighlights = this.twitchChatState.deletedHighlights;
  clipsHighlights = this.twitchChatState.clipsHighlights;
  isLoadingClips = this.twitchChatState.isLoadingClips;
  sortedHighlights = this.twitchChatState.sortedHighlights;
  unanalyzedCount = this.twitchChatState.unanalyzedCount;
  analyzableQuickCount = this.twitchChatState.analyzableQuickCount;
  isChatDownloading = this.twitchChatState.isChatDownloading;
  parallelDownloadActive = this.twitchChatState.parallelDownloadActive;
  isLive = this.twitchChatState.isLive;
  lastLivePollAt = this.twitchChatState.lastLivePollAt;
  containingPinnedByStartTime = this.twitchChatState.containingPinnedByStartTime;
  canMerge = this.twitchChatState.canMerge;

  // --- Undo / redo (services/undo-history.service.ts) ---
  private static readonly IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
  canUndo = this.history.canUndo;
  canRedo = this.history.canRedo;
  // Anything modal over the editor (or no editor yet) — undo would change the project behind it.
  historyBlocked = computed(() =>
    !this.isProcessed()
    || this.showHelpModal() || this.showPathHelp() || this.isLibraryOpen()
    || (this.isAdmin() && this.isAdminPanelOpen()) || this.showMarkerSourcesModal()
    || this.showProModal() || !!this.syncConflict() || this.showSyncMigrationPrompt()
    || this.demoService.signInPromptVisible() || this.tourService.isActive()
    || this.isAuthLoading() || (!this.currentUser() && !this.demoService.isDemoMode())
    || (!!this.currentUser() && this.isConsentBlocking()));
  undoDisabled = computed(() => !this.canUndo() || this.historyBlocked());
  redoDisabled = computed(() => !this.canRedo() || this.historyBlocked());
  undoTooltip = computed(() => {
    const label = this.history.undoLabel();
    return label ? `Отменить: ${label} (${AppComponent.IS_MAC ? '⌘Z' : 'Ctrl+Z'})` : 'Нечего отменять';
  });
  redoTooltip = computed(() => {
    const label = this.history.redoLabel();
    return label ? `Повторить: ${label} (${AppComponent.IS_MAC ? '⇧⌘Z' : 'Ctrl+Y'})` : 'Нечего повторять';
  });
  // «Отменено: …» — shown for 2.5s in the list actions row (in place of the sort controls, so
  // nothing moves) and in the mobile control bar.
  historyToast = signal<string | null>(null);
  private historyToastTimer: ReturnType<typeof setTimeout> | null = null;
  // «Объединено N моментов · Отменить» — while that merge is still the step Ctrl+Z would undo.
  private mergeBannerState = signal<{ entryId: number; count: number } | null>(null);
  private mergeBannerTimer: ReturnType<typeof setTimeout> | null = null;
  mergeBanner = computed(() => {
    const b = this.mergeBannerState();
    return b && this.history.undoTopId() === b.entryId ? b : null;
  });
  mergeBannerText = computed(() => {
    const b = this.mergeBanner();
    return b ? `Объединено ${b.count} ${AppComponent.pluralMoments(b.count)}` : '';
  });
  mergeUndoTooltip = computed(() => `Вернуть исходные моменты (${AppComponent.IS_MAC ? '⌘Z' : 'Ctrl+Z'})`);

  static pluralMoments(n: number): string {
    const m10 = n % 10;
    const m100 = n % 100;
    if (m10 === 1 && m100 !== 11) return 'момент';
    if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return 'момента';
    return 'моментов';
  }

  private static shorten(text: string, max = 30): string {
    return text.length > max ? `${text.slice(0, max - 1)}…` : text;
  }

  /** The moment a startTime-keyed action acts on (same first-match lookup as the service). */
  private momentAt(startTime: number): Highlight | null {
    return this.highlights().find(h => h.startTime === startTime) ?? null;
  }

  private momentLabel(h: Highlight | null | undefined): string {
    return h ? h.timestamp || this.formatTime(h.startTime) : '';
  }

  undo() { this.runHistory('undo'); }
  redo() { this.runHistory('redo'); }

  // Shared by the hotkeys, the ↶/↷ buttons, the mobile bar and the merge banner.
  private runHistory(dir: 'undo' | 'redo'): void {
    this.tooltipService.hide(); // a shown «Отменить: …» tooltip would name a step already undone
    // An uncommitted drag is the thing to undo — drop it, like Esc (and its stale base must not
    // be committed over the undo on release).
    if (this.clipDrag) { this.cancelClipDrag(); return; }
    if (this.regionDrag) { this.cancelRegionDrag(); return; }
    if (this.stripPress) { this.onStripPointerCancel(); return; }
    if (this.historyBlocked()) return;
    const r = dir === 'undo'
      ? this.history.undo({ visible: () => this.visibleHighlights() })
      : this.history.redo({ visible: () => this.visibleHighlights() });
    if (!r) return;
    if ('message' in r) { this.showHistoryToast(r.message); return; }
    if (this.mergeBannerState()?.entryId === r.entryId) this.mergeBannerState.set(null);
    this.showHistoryToast(`${dir === 'undo' ? 'Отменено' : 'Повторено'}: ${r.label}`);
    // No seek (the playhead stays). Bring the moment's clip into view only while paused —
    // while playing, scrolling away from the playhead would stop the heatmap following it.
    const h = this.currentHighlight();
    if (h && this.isPlayerPaused) this.ensureClipVisible(h);
  }

  private showHistoryToast(text: string): void {
    this.historyToast.set(text);
    if (this.historyToastTimer) clearTimeout(this.historyToastTimer);
    this.historyToastTimer = setTimeout(() => {
      this.historyToastTimer = null;
      this.historyToast.set(null);
    }, 2500);
  }

  // Highlights with hidden marker-sources filtered out (chip toggles).
  // Tab-scoped: filter applies only on ВСЕ tab. Other tabs (СОХРАНЕННЫЕ /
  // УДАЛЁННЫЕ / ПОИСК / КЛИПЫ) reflect explicit user decisions (pin, promote,
  // trash) and shouldn't be silently masked by source visibility toggles.
  visibleHighlights = computed(() => {
    const sorted = this.sortedHighlights();
    if (this.activeTab() !== 'all') return sorted;
    const hidden = this.markersService.hiddenSourceIds();
    if (hidden.size === 0) return sorted;
    return sorted.filter(h =>
      h.source !== 'stream-marker' || !h.markerSourceId || !hidden.has(h.markerSourceId)
    );
  });

  // Stream-marker highlights for heatmap-pin overlay (filtered by source-chip toggles).
  streamMarkerPins = computed(() => {
    const hidden = this.markersService.hiddenSourceIds();
    return this.allHighlights().filter(h =>
      h.source === 'stream-marker'
      && !h.isDeleted
      && (!h.markerSourceId || !hidden.has(h.markerSourceId))
    );
  });

  // User-clip highlights for heatmap-pin overlay (purple triangles). Pulled from
  // the full highlights list so deleted clips don't pin and Promoted-status doesn't
  // matter — pins always reflect available community clips on this VOD.
  userClipPins = computed(() => {
    const region = this.activeRegion();
    return this.twitchChatState.highlights().filter(h =>
      h.source === 'user-clip' && !h.isDeleted && isVisibleInRegion(h, region)
    );
  });

  // Pins in px on the bin grid like everything else on the heatmap (they used % of
  // videoDurationSec — drifted when chat bins don't span the VOD), clamped to the last bar.
  markerPinsView = computed(() => this.pinsOnGrid(this.streamMarkerPins()));
  clipPinsView = computed(() => this.pinsOnGrid(this.userClipPins()));
  private pinsOnGrid(list: Highlight[]): Array<{ h: Highlight; x: number }> {
    const end = this.heatmapEndSec();
    if (!(end > 0)) return [];
    const unit = this.heatmapStep() / 15;
    return list.map(h => ({ h, x: Math.min(Math.max(0, h.startTime), end) * unit }));
  }

  // --- «Речь» (admin-only, experimental) ---
  speechMode = this.twitchChatState.speechMode;
  speechCategoryFilter = this.twitchChatState.speechCategoryFilter;
  speechTabCount = computed(() => this.transcript.segments().length + this.transcript.searchHits().length);

  // Line under the playhead for the transcript panel. Reads the per-frame playerTime ONLY
  // while the text panel is on screen; the panel gets an index (changes every few seconds),
  // never the time itself — see transcript-panel.component.ts perf note.
  transcriptActiveLine = computed(() => {
    if (!this.isAdmin() || this.activeTab() !== 'speech' || this.speechMode() !== 'text') return -1;
    return this.transcript.lineIndexAt(this.playerTime());
  });
  transcriptRange = computed(() => {
    const h = this.currentHighlight();
    return h && isTranscriptSource(h.source) ? { start: h.startTime, end: h.endTime } : null;
  });

  // Heatmap «Речь» lane. Positions in px from the bin grid — bar k sits at k*heatmapStep —
  // not % of videoDurationSec (that drifts when chat bins don't span the whole VOD).
  // Reacts to heatmapStep so zoom moves bands with the bars; no CSS transition on
  // left/width (zoom re-anchors scrollLeft synchronously, a transition would drift).
  speechLane = computed(() => {
    if (!this.isAdmin()) return [];
    const maxT = this.trimmedBins().length * 15;
    if (maxT <= 0) return [];
    const unit = this.heatmapStep() / 15;
    const cats = this.speechCategoryFilter();
    const cur = this.currentHighlight();
    const out: Array<{ h: Highlight; left: number; width: number; bottom: number; height: number; cls: string; tip: string }> = [];
    for (const h of this.twitchChatState.highlights()) {
      if (h.isDeleted || !isTranscriptSource(h.source)) continue;
      const isSearch = h.source === 'transcript-search';
      if (!isSearch && h.transcriptCategory && !cats.has(h.transcriptCategory)) continue;
      const start = Math.min(h.startTime, maxT);
      const end = Math.min(h.endTime, maxT);
      if (end <= start) continue;
      const style = isSearch ? TRANSCRIPT_SEARCH_STYLE : (TRANSCRIPT_CATEGORIES[h.transcriptCategory as TranscriptCategory] ?? TRANSCRIPT_SEARCH_STYLE);
      const selected = cur?.startTime === h.startTime;
      out.push({
        h,
        left: start * unit,
        width: Math.max(3, (end - start) * unit),
        bottom: isSearch ? 14 : 4,
        height: isSearch ? 3 : 8,
        cls: style.band + (selected ? ' ring-2 ring-white' : ' opacity-80 hover:opacity-100'),
        tip: isSearch
          ? `ИИ-поиск «${h.searchQuery ?? ''}» · ${h.confidence ?? 0}% · ${h.description}`
          : `${TRANSCRIPT_CATEGORIES[h.transcriptCategory as TranscriptCategory]?.label ?? 'Речь'}${h.transcriptTag ? ' · ' + h.transcriptTag : ''} · ${h.confidence ?? 0} · ${h.transcriptTitle || h.description}`,
      });
    }
    return out;
  });
  // Text-search hits over speech → thin ticks under the bars, one per 15s bin (capped).
  speechTicks = computed(() => {
    if (!this.isAdmin()) return [];
    const m = this.transcript.textMatches();
    const doc = this.transcript.transcript();
    if (!m || m.invalid || !doc) return [];
    const maxT = this.trimmedBins().length * 15;
    const unit = this.heatmapStep() / 15;
    const bins = new Set<number>();
    for (const i of m.lines) {
      const t = doc.lines[i][0];
      if (t < maxT) bins.add(Math.floor(t / 15) * 15);
      if (bins.size >= 400) break;
    }
    return [...bins].map(b => b * unit);
  });
  speechLaneActive = computed(() => this.speechLane().length > 0 || this.speechTicks().length > 0);
  transcriptDurationWarning = computed(() => {
    const d = this.transcript.transcript()?.durationSec ?? 0;
    const v = this.videoDurationSec();
    if (!d || v <= 0 || Math.abs(d - v) <= 60) return null;
    return `Длина транскрипта (${this.formatTime(d)}) не совпадает с VOD (${this.formatTime(v)}) — таймкоды могут быть смещены.`;
  });

  potentialPeakBinTimes = this.twitchChatService.potentialPeakBinTimes;
  potentialHighlightsCount = this.twitchChatService.potentialHighlightsCount;

  // Using filtered comments for Overlay
  chatComments = this.twitchChatState.filteredComments;

  // Firebase state
  isFirebaseReady = this.firebaseService.isInitialized;
  isBatchDownloading = this.downloadService.isBatchDownloading;
  batchDownloadProgress = this.downloadService.batchDownloadProgress;
  batchDownloadQueued = this.downloadService.batchDownloadQueued;

  // Firebase Auth State
  currentUser = this.firebaseService.currentUser;
  userProfile = this.firebaseService.userProfile;
  isAuthLoading = this.firebaseService.isAuthLoading;
  useEmulators = this.firebaseService.useEmulators;

  // Consent flow (added 2026-05-02). consentGiven gates the OAuth button on
  // the main login screen; needsConsentRefresh/isConsentBlocking surface the
  // post-login banner / blocking modal for legacy users without a record.
  consentGiven = signal(false);
  needsConsentRefresh = this.firebaseService.needsConsentRefresh;
  isConsentBlocking = this.firebaseService.isConsentBlocking;
  isRecordingConsent = signal(false);

  // Surfaced if the post-click navigation to auth.peakster.su never completes
  // within 6s — covers QUIC/DPI failures on the first redirect hop. Cannot
  // detect failures on twitch.tv (we're already off-site by then).
  loginRedirectStuck = signal(false);
  private loginRedirectTimer: number | null = null;

  // Cloud sync indicator (Pro-only)
  syncStatus = this.syncService.syncStatus;
  syncLastSyncedAt = this.syncService.lastSyncedAt;
  syncPendingChanges = this.syncService.pendingChanges;
  syncHasPulled = this.syncService.hasPulled;
  isSyncEligible = this.firebaseService.isSyncEligible;
  syncIndicatorTooltip = computed(() => {
    const status = this.syncStatus();
    const last = this.syncLastSyncedAt();
    const pending = this.syncPendingChanges();
    if (status === 'offline') return 'Нет соединения — синхронизация приостановлена';
    if (status === 'error') return 'Ошибка синхронизации — данные сохранены локально';
    if (status === 'syncing') return 'Синхронизация...';
    if (pending > 0) return `Ожидает синхронизации: ${pending}`;
    if (last > 0) {
      const ago = Math.max(0, Math.floor((Date.now() - last) / 1000));
      if (ago < 60) return `Синхронизировано ${ago}с назад`;
      if (ago < 3600) return `Синхронизировано ${Math.floor(ago / 60)}м назад`;
      return `Синхронизировано ${Math.floor(ago / 3600)}ч назад`;
    }
    return 'Готово к синхронизации';
  });
  syncIndicatorColor = computed(() => {
    const status = this.syncStatus();
    if (status === 'offline') return 'bg-gray-500';
    if (status === 'error') return 'bg-red-500';
    if (status === 'syncing' || this.syncPendingChanges() > 0) return 'bg-amber-400 animate-pulse';
    return 'bg-emerald-500';
  });

  // --- First-time cloud migration prompt (Pro users only) ---
  showSyncMigrationPrompt = signal<boolean>(false);
  syncMigrationProgress = this.syncMigrationService.progress;
  syncMigrationRunning = this.syncMigrationService.isRunning;
  syncLocalChannelsCount = signal<number>(0);
  syncLocalProjectsCount = signal<number>(0);

  // Conflict-resolution modal shell. The detection itself is deferred to a
  // future iteration (compare server updatedAt vs last-pulled timestamp during
  // push); this is the UI surface for when SyncService.conflictDetected fires.
  syncConflict = this.syncService.conflictDetected;

  // Local state
  showHelpModal = signal(false);
  showPathHelp = signal(false);
  showProModal = signal(false);
  // Optional copy override for the Pro upsell modal — set by quota-exhausted
  // handlers to swap the default «экспорт через .bat / ZIP» message for a
  // «дневной лимит исчерпан» message. Cleared when the modal closes.
  proModalReason = signal<string | null>(null);
  openProModal(reason: string | null = null) {
    this.proModalReason.set(reason);
    this.showProModal.set(true);
  }
  closeProModal() {
    this.showProModal.set(false);
    this.proModalReason.set(null);
  }

  // ── Daily-quota display helpers ─────────────────────────────────────────
  // Each kind exposes (a) badge string «used/limit», (b) tooltip with reset
  // time and Pro hint, (c) exhausted boolean for [disabled] expression. Pro
  // (limit === null) never renders a badge — empty string short-circuits @if.
  private formatResetClock(d: Date | null): string {
    if (!d) return 'полночь UTC';
    const hh = String(d.getUTCHours()).padStart(2, '0');
    const mm = String(d.getUTCMinutes()).padStart(2, '0');
    return `${hh}:${mm} UTC`;
  }
  private buildQuotaTooltip(q: QuotaState | null, label: string): string {
    if (!q) return label;
    if (q.limit === null) return `${label} (безлимит)`;
    const reset = this.formatResetClock(this.quotaService.resetAt());
    if (q.exhausted) {
      const tail = this.firebaseService.isPro() ? '' : ' Перейдите на Pro для большего лимита.';
      return `Дневной лимит исчерпан. Сбросится в ${reset}.${tail}`;
    }
    return `Осталось ${q.remaining} из ${q.limit}. Сбросится в ${reset}.`;
  }

  aiQuickBadge = computed(() => {
    const q = this.quotaService.aiQuickQuota();
    if (!q || q.limit === null) return '';
    return `${q.remaining}/${q.limit}`;
  });
  aiDeepBadge = computed(() => {
    const q = this.quotaService.aiDeepQuota();
    if (!q || q.limit === null) return '';
    return `${q.remaining}/${q.limit}`;
  });
  aiSearchBadge = computed(() => {
    const q = this.quotaService.aiSearchQuota();
    if (!q || q.limit === null) return '';
    return `${q.remaining}/${q.limit}`;
  });
  clipBadge = computed(() => {
    const q = this.quotaService.clipQuota();
    if (!q || q.limit === null) return '';
    return `${q.remaining}/${q.limit}`;
  });

  aiQuickQuotaLow = computed(() => {
    const q = this.quotaService.aiQuickQuota();
    return !!q && q.limit !== null && q.remaining <= Math.max(2, Math.ceil(q.limit * 0.2));
  });
  aiDeepQuotaLow = computed(() => {
    const q = this.quotaService.aiDeepQuota();
    return !!q && q.limit !== null && q.remaining <= Math.max(1, Math.ceil(q.limit * 0.2));
  });
  aiSearchQuotaLow = computed(() => {
    const q = this.quotaService.aiSearchQuota();
    return !!q && q.limit !== null && q.remaining <= Math.max(1, Math.ceil(q.limit * 0.2));
  });
  clipQuotaLow = computed(() => {
    const q = this.quotaService.clipQuota();
    return !!q && q.limit !== null && q.remaining <= Math.max(2, Math.ceil(q.limit * 0.2));
  });

  aiQuickExhausted  = computed(() => !!this.quotaService.aiQuickQuota()?.exhausted);
  aiDeepExhausted   = computed(() => !!this.quotaService.aiDeepQuota()?.exhausted);
  aiSearchExhausted = computed(() => !!this.quotaService.aiSearchQuota()?.exhausted);
  clipExhausted     = computed(() => !!this.quotaService.clipQuota()?.exhausted);

  // Cloud Sync drops chatComments (1MB Firestore doc cap) but keeps
  // activityBins + highlights. Synced VODs opened on fresh device show
  // a "Скачать чат" CTA via needsChatRedownload(). Without chat content
  // AI Quick + Deep would hallucinate moments from spike patterns alone
  // (and consume daily quota for nothing). Block both buttons in this state.
  aiBlockedByMissingChat = computed(() => this.twitchChatState.needsChatRedownload());

  aiQuickTooltip = computed(() => {
    if (this.aiBlockedByMissingChat()) return 'Чат не загружен. Скачайте чат — без него AI-анализ не работает.';
    return this.buildQuotaTooltip(this.quotaService.aiQuickQuota(), 'Анализ пиков');
  });
  aiDeepTooltip = computed(() => {
    if (this.aiBlockedByMissingChat()) return 'Чат не загружен. Скачайте чат — без него AI-анализ не работает.';
    return this.buildQuotaTooltip(this.quotaService.aiDeepQuota(), 'Глубокий поиск');
  });
  aiSearchTooltip = computed(() => {
    if (this.aiBlockedByMissingChat()) return 'Чат не загружен. Скачайте чат — без него ИИ-поиск не работает.';
    return this.buildQuotaTooltip(this.quotaService.aiSearchQuota(), 'ИИ-поиск');
  });
  clipTooltip = computed(() => this.buildQuotaTooltip(this.quotaService.clipQuota(), 'Скачать MP4 через облако'));

  /**
   * On exhausted-button click: Free → Pro upsell modal; Pro → toast/alert
   * (rare — only if admin set a per-Pro override below tier default). Kind is
   * used for the modal copy.
   */
  private handleQuotaExhaustedClick(kind: QuotaKind): void {
    const labelByKind: Record<QuotaKind, string> = {
      aiQuick: 'AI-анализ пиков',
      aiDeep: 'Глубокий AI-поиск',
      aiSearch: 'ИИ-поиск',
      clip: 'Скачивание клипов',
      zip: 'ZIP-архив клипов',
    };
    const reset = this.formatResetClock(this.quotaService.resetAt());
    if (this.firebaseService.isPro()) {
      alert(`${labelByKind[kind]}: дневной лимит исчерпан. Сбросится в ${reset}.`);
      return;
    }
    this.openProModal(`${labelByKind[kind]}: дневной лимит исчерпан. Сбросится в ${reset}.`);
  }
  isPlayerFocused = signal(false);
  playerTime = signal(0);
  // Chat overlay is removed from DOM when shifted so Twitch's visibility check passes
  // (the check rejects play() if any element's bbox overlaps the iframe's bbox).
  isOverlayShifted = signal(false);
  private overlayRestoreTimeout: any = null;
  // Twitch SDK rejects play() for ~1s after a pause. Defer play if called too soon.
  private lastPauseTime = 0;
  private readonly MIN_PAUSE_PLAY_DELAY_MS = 1000;
  // At most one deferred play is queued; later K presses cancel/re-queue it.
  private pendingPlayTimeout: any = null;
  // True between user's pause keypress and the Twitch PAUSE event (avoids double pause calls).
  private isPausingNow = false;
  // Timestamp of most recent user-initiated play() call. Used to distinguish a real user play
  // from a seek-buffer PLAY event, which must not change the paused/playing state.
  private userPlayIntentTime = 0;
  topChatters = computed(() => this.twitchChatService.getTopChatters());
  isFiltersExpanded = signal(false);
  isLibraryOpen = signal(false);

  // Sidebar redesign state (Peakster v0.3.5 polish pass)
  // Auto-collapse breakpoint: below this viewport width, sidebar starts collapsed.
  private static readonly SIDEBAR_AUTO_COLLAPSE_PX = 1280;
  // Once user clicks the toggle, auto-resize stops fighting their preference (per session).
  private hasManuallyToggledSidebar = false;
  isSidebarCollapsed = signal<boolean>(this.computeInitialSidebarCollapsed());
  isSourceSectionCollapsed = signal<boolean>(false);
  isAvatarMenuOpen = signal<boolean>(false);
  showMarkerSourcesModal = signal<boolean>(false);

  // Admin panel state. isAdmin is double-gated: role='admin' from PG (mirrored
  // into Firestore profile) AND not in demo mode. UI hiding is cosmetic — real
  // protection is auth-rf requireAdmin re-reading PG on every /admin/* request.
  isAdmin = computed(() =>
    this.userProfile()?.role === 'admin' && !this.demoService.isDemoMode()
  );
  isAdminPanelOpen = signal<boolean>(false);

  // System banner pushed by admin /admin/broadcast endpoint → Firestore
  // system/banner doc → onSnapshot in firebase.service. Null when no active
  // broadcast or expired.
  systemBanner = this.firebaseService.systemBanner;

  private computeInitialSidebarCollapsed(): boolean {
    if (typeof window === 'undefined') return false;
    const stored = localStorage.getItem('peakster-sidebar-collapsed');
    if (stored !== null) {
      // User has expressed a preference previously — honor it, lock auto-resize for this session.
      this.hasManuallyToggledSidebar = true;
      return stored === 'true';
    }
    // No prior preference → start expanded so onboarding wizard (steps 1/2/3) is visible.
    // Auto-collapse will kick in only after a video is loaded (see effect in constructor).
    return false;
  }

  // New Local State for URL Input
  sourceMode = signal<'file' | 'url'>('file');
  twitchUrl = signal<string>('');

  MIN_MESSAGES_FOR_PEAK = this.twitchChatService.MIN_MESSAGES_FOR_PEAK;

  totalEstimatedSizeMb = computed(() => {
    const total = this.twitchChatState.exportableHighlights()
      .reduce((sum, h) => sum + (h.estimatedSizeMb || 0), 0);
    return Math.round(total);
  });

  // Per-clip ceiling on the ZIP worker is ~2.2 GB output (Cloud Run 8Gi gen2:
  // /tmp tmpfs shares the memory limit; TDC peak ≈ 3.6× output during HLS
  // segment merge → 8 GiB cap). Clips above this hard-fail with SIGBUS mid-
  // download — the ZIP just sits at progress 0. We warn before submit so the
  // user knows to route those highlights through the .BAT script (local TDC
  // has no such ceiling) or trim the range in the editor. Threshold = 2048 MB
  // (slightly below empirical 2.2 GB cap) so the warning surfaces before the
  // actual fail line.
  zipOversizedClips = computed(() =>
    this.twitchChatState.exportableHighlights()
      .filter(h => (h.estimatedSizeMb || 0) > 2048)
  );

  private player: any;
  // Playhead interpolation anchor: between SDK time samples, we interpolate using wallclock
  // delta so the playhead appears smooth at 60fps instead of stepping every 200ms.
  private lastSampleSec = 0;
  private lastSampleMs = 0;
  private prevSdkSample = -1; // tracks the last raw SDK value; we only re-anchor when it changes
  private playheadRafId: number | null = null;

  // Track the last intended seek target so that rapid j/l presses accumulate
  // instead of all resolving against a stale player.getCurrentTime() during buffering.
  private lastSeekTargetSec = 0;
  private lastSeekAtMs = 0;
  private readonly SEEK_ACCUMULATE_WINDOW_MS = 1500;

  // Buffer-stuck detection: timestamp of the FIRST consecutive SDK reading reporting
  // sdkTime ≈ lastSeekTargetSec after a seek. Stays set while SDK keeps reporting target;
  // cleared as soon as SDK advances past target. Once stuck >250ms we pin the playhead
  // (slow distant seeks where Twitch's buffer outlasts SEEK_ACCUMULATE_WINDOW_MS would
  // otherwise let interp race ahead of audio, and the >5s drift guard locks the offset
  // in via monotonic-forward). Fast seeks within buffered region transition past target
  // before the threshold trips, so the smooth UX is preserved.
  private sdkBufferStuckSinceMs: number | null = null;
  private readonly BUFFER_STUCK_THRESHOLD_MS = 250;

  // Drift safety net: if 3+ consecutive SDK samples show |interp - sdk| > 5s, force
  // re-anchor interp to SDK. Backstop for cases where interp ran wild (background tab
  // throttling, long buffer where SDK eventually catches up to a position far behind
  // our wallclock-extrapolated interp). Without this, the monotonic-forward "ignore
  // as stale" branch traps interp forever in the wandering position; only a fresh
  // seek (which resets anchors) recovers. Counter resets to 0 on any sample where
  // |drift| < 5s (we're back in sync, normal monotonic-forward resumes).
  private consecutiveLargeDriftSamples = 0;
  private readonly LARGE_DRIFT_RECOVERY_THRESHOLD = 3;

  // Local play/pause state driven by PLAY/PAUSE events.
  // player.isPaused() from Twitch API updates with delay when the iframe is not focused,
  // which caused K / Space to only pause (second press saw stale "playing" state).
  private isPlayerPaused = true;

  // Set true on Twitch.Player.ENDED. Used to suppress auto-advance to next video:
  // any PLAY event fired while this is true without a fresh userPlayIntentTime is
  // Twitch's 20s-countdown advancing to the next VOD — we immediately re-pause.
  // Cleared by seekAbsolute (user seeked back inside the video = intent to re-watch).
  private didVideoEnd = false;

  // Throttle timestamp for the video-hijack recovery check (rAF runs at 60Hz; we
  // only need to compare video IDs once per second).
  private lastVideoIdCheckMs = 0;

  // --- Adaptive playback rate detection ---
  // Twitch's iframe API does not expose the speed multiplier directly, so we derive it
  // from the ratio of SDK-time delta to wallclock delta between samples and smooth with
  // EMA. Converges over ~1.5s after a speed change. Set the flag to false to disable
  // entirely and always assume 1x.
  private readonly ENABLE_ADAPTIVE_PLAYBACK_RATE = true;
  private readonly PLAYBACK_RATE_EMA_ALPHA = 0.3; // higher = faster reaction, more noise
  private currentPlaybackRate = 1;
  private lastRateSampleSdkSec = -1;
  private lastRateSampleWallMs = 0;

  constructor() {
    // Tooltips must never paint over the Twitch player — in Chrome the SDK's IO v2
    // visibility check pauses an obscured iframe (src/CLAUDE.md «Chat overlay»).
    this.tooltipService.setKeepOutProvider(() => this.playerKeepOut());

    // ── Path A: complete the auth-rf redirect handshake ───────────────────
    // After auth-rf bounces the browser back to peakster.su with a Firebase
    // custom token in the URL fragment (#ct=…), trade it for a Firebase ID
    // token via signInWithCustomToken. The fragment is stripped after
    // consumption so a refresh doesn't replay it. Runs once, gated on
    // Firebase Auth being initialized.
    // «Речь» tab is admin-only — demote / logout / demo mid-session must not leave the
    // sidebar stuck on a tab whose button no longer renders.
    effect(() => {
      if (this.activeTab() === 'speech' && !this.isAdmin()) {
        untracked(() => this.twitchChatState.activeTab.set('all'));
      }
    });

    let rfAuthHandled = false;
    effect(() => {
      if (rfAuthHandled) return;
      if (!this.firebaseService.isInitialized()) return;
      const hash = typeof window !== 'undefined' ? window.location.hash : '';
      if (!hash.startsWith('#ct=')) return;
      rfAuthHandled = true;
      const params = new URLSearchParams(hash.slice(1));
      const ct = params.get('ct');
      if (!ct) return;
      this.firebaseService.completeRfAuth(decodeURIComponent(ct)).catch((err) => {
        console.error('[app] completeRfAuth failed:', err);
        // Non-fatal — user sees the login screen and can retry.
      });
    });

    // Drive the progress-bar's elapsed-time counter at 1 Hz only while an op is
    // active. progressInfo signal toggles the interval on/off; when null, no
    // timer is burning.
    effect(() => {
      const active = this.progressInfo() !== null;
      if (active && this.progressTickInterval === null) {
        this.progressTickInterval = setInterval(() => this.progressTick.update(v => v + 1), 1000);
      } else if (!active && this.progressTickInterval !== null) {
        clearInterval(this.progressTickInterval);
        this.progressTickInterval = null;
      }
    });

    // Effect to handle player creation/destruction when videoId changes.
    // Demo mode: we render an SVG placeholder instead of the Twitch iframe,
    // so skip player init entirely (videoId is fake and would 404).
    effect(() => {
      const vid = this.videoId();
      if (vid && !this.demoService.isDemoMode() && typeof window !== 'undefined') {
        setTimeout(() => this.initTwitchPlayer(String(vid)), 100);
      } else if (!vid) {
        this.destroyPlayer();
      }
    });

    // Demo mode: hydrate fake chat + AI data on first load.
    // Must run after constructor wiring so all signals are reactive.
    effect(() => {
      if (!this.demoService.isDemoMode()) return;
      if (this.isProcessed()) return;
      this.demoService.hydrateAll().then(() => {
        // Cache video duration so heatmap/timeline render correctly without a real player.
        this.videoDurationSec.set(7200);
      });
    });

    // Demo mode: auto-start the tour once demo data is hydrated and visible.
    // First-time only (gated by localStorage flag); user can re-trigger via HelpModal.
    effect(() => {
      if (!this.demoService.isDemoMode()) return;
      if (!this.isProcessed()) return;
      if (this.tourService.isActive()) return;
      if (!this.tourService.shouldAutoStart()) return;
      // Small delay to let the layout settle so first target rect is correct.
      setTimeout(() => this.tourService.start(), 300);
    });

    // Effect to apply UI scaling
    effect(() => {
      const scale = this.interfaceScale();
      if (typeof window !== 'undefined' && this.document && this.document.documentElement) {
        this.document.documentElement.style.fontSize = `${scale}%`;
      }
    });

    // Note: sidebar collapsed state is persisted on toggleSidebar() directly,
    // not via effect — auto-resize changes shouldn't be saved as user preference.

    // Auto-collapse "Источник" section once a video is loaded — Step 1 is done,
    // user is now working with Step 2 (Анализ) / Step 3 (Экспорт). User can re-expand to swap source.
    effect(() => {
      if (this.isProcessed()) {
        this.isSourceSectionCollapsed.set(true);
      }
    });

    // Auto-collapse sidebar on small screens once a video is loaded.
    // Onboarding wizard takes priority over real estate; only after isProcessed do we trade.
    effect(() => {
      if (!this.isProcessed()) return;
      if (this.hasManuallyToggledSidebar) return;
      if (typeof window === 'undefined') return;
      if (window.innerWidth < AppComponent.SIDEBAR_AUTO_COLLAPSE_PX) {
        this.isSidebarCollapsed.set(true);
      }
    });

    // Mobile-only: auto-close the off-canvas drawer once a video is loaded —
    // same "wizard first, then trade for real estate" reasoning as the effect
    // above, just for the drawer variant of the sidebar below the md breakpoint.
    effect(() => {
      if (!this.isProcessed()) return;
      if (this.hasManuallyToggledSidebar) return;
      if (!this.viewport.isMobileLayout()) return;
      this.isSidebarCollapsed.set(true);
    });

    // Auto-scroll the moments list to the currently-selected highlight when
    // selection changes (heatmap click, j/l, ,/. hotkeys). Without this, the
    // list keeps its old scroll position and the newly-selected card can be
    // off-screen — user has to scroll manually to see it. Skip the first
    // emission so a saved currentHighlightIndex from SavedState doesn't
    // hijack the user's scroll on initial load. block:'nearest' makes it a
    // no-op if the card is already in view (e.g. user clicking a visible card).
    // Heatmap page-flip while playing — see followPlayhead. Runs on every playhead frame and on
    // every view change (scroll / zoom / resize); the work itself is a few comparisons.
    effect(() => {
      const t = this.playerTime();
      const view = this.heatmapView();
      untracked(() => this.followPlayhead(t, view));
    });
    // VOD switch: drop timeline-strip drags, scrub and the zoom-to-clip memory.
    effect(() => {
      this.videoId();
      untracked(() => this.resetTimelineUiState());
    });
    // A drag whose handles just unmounted never gets its pointerup (the capture went with the
    // element): the selection changed / vanished under a clip drag, the region was switched off
    // (cloud restore, Alt+P) under a region drag.
    effect(() => {
      const cv = this.clipView();
      const rv = this.regionView();
      untracked(() => {
        if (this.clipDrag && (!cv || cv.key !== this.clipDrag.key)) this.cancelClipDrag();
        if (this.regionDrag && !rv) this.cancelRegionDrag();
      });
    });

    let skipFirstHighlightScroll = true;
    effect(() => {
      this.currentHighlightIndex(); // subscribe to index changes only
      if (skipFirstHighlightScroll) {
        skipFirstHighlightScroll = false;
        return;
      }
      untracked(() => {
        const h = this.currentHighlight();
        if (!h || typeof window === 'undefined' || !this.document) return;
        // rAF defers to next paint so Angular has rendered the new [id] / class
        // (the click handler updates the signal synchronously, and the DOM
        // mutation happens after Angular's tick).
        requestAnimationFrame(() => {
          const el = this.document!.getElementById(this.highlightCardId(h));
          el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        });
      });
    });

    // While the VOD is live, keep videoDurationSec in sync with the growing
    // recording. Two sources, take whichever is larger:
    //   1. player.getDuration() — Twitch SDK's view, lags by seconds-to-minutes
    //      and only refreshes lazily on user scrub (the "domotal do konca →
    //      duration updated" symptom);
    //   2. state.liveDurationSec — authoritative `now - liveStream.started_at`
    //      from /get-vod-status, written by the chat service's poll loop.
    // SDK is good for "how much is actually playable in the iframe"; backend
    // value is good for "where the heatmap/timeline should end". Without this
    // effect the timeline cap stays at the iframe-init duration and any moments
    // past that point are unreachable via timeline scrub.
    effect((onCleanup) => {
      if (!this.isLive()) return;
      if (typeof window === 'undefined') return;
      const tick = () => {
        if (!this.isLive()) return;
        let sdkDur = 0;
        try {
          if (this.player) sdkDur = this.player.getDuration() || 0;
        } catch (e) {}
        const backendDur = this.twitchChatState.liveDurationSec();
        const newMax = Math.max(sdkDur, backendDur);
        if (newMax > this.videoDurationSec()) {
          this.videoDurationSec.set(newMax);
        }
      };
      // Run once immediately so the badge → first poll has a reasonable cap.
      tick();
      const interval = setInterval(tick, 30_000);
      onCleanup(() => clearInterval(interval));
    });

    // First-time cloud-sync migration prompt for Pro users.
    // Shows once after release; auto-completes silently if there's nothing local.
    effect(() => {
      const profile = this.userProfile();
      if (!profile || profile.isPro !== true) return;
      if (profile.migrationCompleted === true) return;

      const channels = this.syncMigrationService.countLocalChannels();
      const projects = this.syncMigrationService.countLocalProjects();
      this.syncLocalChannelsCount.set(channels);
      this.syncLocalProjectsCount.set(projects);

      if (channels === 0 && projects === 0) {
        // Nothing to migrate — mark done so we never bother again
        this.firebaseService.markMigrationCompleted();
      } else {
        this.showSyncMigrationPrompt.set(true);
      }
    });
  }

  async startSyncMigration(): Promise<void> {
    const uid = this.currentUser()?.uid;
    if (!uid) return;
    try {
      await this.syncMigrationService.runFirstTimeMigration(uid);
      // Hide prompt only after success (progress UI shows during run)
      this.showSyncMigrationPrompt.set(false);
    } catch (err: any) {
      console.error('Sync migration failed:', err);
      // Leave prompt visible — error message is in syncMigrationProgress signal
    }
  }

  dismissSyncMigrationLater(): void {
    this.showSyncMigrationPrompt.set(false);
  }

  async dismissSyncMigrationForever(): Promise<void> {
    await this.firebaseService.markMigrationCompleted();
    this.showSyncMigrationPrompt.set(false);
  }

  resolveSyncConflictKeepLocal(): void {
    void this.twitchChatState.resolveSyncConflict('local');
  }

  resolveSyncConflictUseServer(): void {
    void this.twitchChatState.resolveSyncConflict('remote');
  }

  syncConflictTitle(conflict: StateConflict): string {
    return conflict.local.videoTitle || conflict.remote.state.videoTitle || `VOD ${conflict.videoId}`;
  }

  syncConflictRemoteTime(conflict: StateConflict): string {
    const ms = conflict.remote.updatedAtMs;
    if (!ms) return '';
    return new Date(ms).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  }

  // Auth Handlers
  async loginWithTwitch() {
    this.loginRedirectStuck.set(false);
    if (this.loginRedirectTimer !== null) {
      clearTimeout(this.loginRedirectTimer);
    }
    // Redirect-stuck detector is for the auth-rf navigation only — in the
    // emergency popup mode the user may legitimately spend >6s on Twitch.
    if (this.firebaseService.loginMode === 'auth-rf') {
      this.loginRedirectTimer = window.setTimeout(() => {
        this.loginRedirectStuck.set(true);
        this.loginRedirectTimer = null;
      }, 6000);
    }
    try {
      await this.firebaseService.loginWithTwitch(this.consentGiven());
      // If user logged in from the demo, drop the ?demo=1 flag and reload into the
      // real app — otherwise they remain stuck looking at synthetic data.
      if (this.demoService.isDemoMode() && typeof window !== 'undefined') {
        window.location.assign(window.location.pathname);
      }
    } catch (e: any) {
      if (this.loginRedirectTimer !== null) {
        clearTimeout(this.loginRedirectTimer);
        this.loginRedirectTimer = null;
      }
      // Popup mode: user closed the Twitch window — not an error worth an alert.
      if (e?.code === 'auth/popup-closed-by-user' || e?.code === 'auth/cancelled-popup-request') return;
      alert('Ошибка входа: ' + e.message);
    }
  }

  /**
   * Records consent for a logged-in legacy user via the post-login banner or
   * the post-deadline blocking modal. Both paths converge here.
   */
  async acceptConsent() {
    if (this.isRecordingConsent()) return;
    this.isRecordingConsent.set(true);
    try {
      await this.firebaseService.recordConsent();
    } catch (e: any) {
      alert('Не удалось сохранить согласие: ' + (e?.message ?? 'ошибка сети'));
    } finally {
      this.isRecordingConsent.set(false);
    }
  }

  /** From the login screen: redirect into ?demo=1 so the demo + tour kick in. */
  enterDemo() {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    url.searchParams.set('demo', '1');
    // Drop the tour-completed flag so the tour auto-starts even if user previously
    // saw it — they're explicitly asking to see the demo again.
    try { localStorage.removeItem('peakster-tour-completed'); } catch { /* ignore */ }
    window.location.assign(url.toString());
  }

  async loginAnonymousDev() {
    try {
      await this.firebaseService.loginAnonymousDev();
    } catch (e: any) {
      alert('Ошибка анонимного входа: ' + e.message);
    }
  }

  async logout() {
    await this.firebaseService.logout();
  }

  isHighlightInQueue(startTime: number): boolean {
    return this.downloadService.isHighlightInQueue(startTime);
  }

  async downloadClip(highlight: Highlight, event?: Event) {
    if (event) event.stopPropagation();

    // Pre-check daily quota before triggering any UI/queue state. Cached URLs
    // bypass — that's a re-fire of an already-finished job, no new server work.
    if (this.clipExhausted()) {
      this.handleQuotaExhaustedClick('clip');
      return;
    }

    // If this clip was already produced and the signed URL is still valid,
    // re-fire the browser download synchronously inside the click handler.
    // Solves the popup-blocker case: the original auto-fired <a>.click() ran
    // post-fetch (async-after-click), some browsers treat that as a popup
    // and silently block. The retrigger is a fresh user gesture, never blocked.
    // Bonus: skips a redundant backend job + GCS upload for the same range.
    const id = this.videoId();
    if (!id) {
      alert('ID видео не найден. Невозможно скачать.');
      return;
    }
    const cached = this.downloadService.getRecentDownload(highlight, id);
    if (cached) {
      this.downloadService.retriggerDownload(cached.url, cached.filename);
      return;
    }
    // Same ~2.2 GB worker ceiling as the ZIP (zipOversizedClips) — reachable in one click since
    // «Речь» search hits can span a whole raid (up to 2 h).
    if ((highlight.estimatedSizeMb || 0) > 2048
        && !confirm(`Клип ≈ ${(highlight.estimatedSizeMb! / 1024).toFixed(1)} ГБ — сервер может не справиться (предел около 2 ГБ). Надёжнее скачать его через .bat или сократить участок. Всё равно скачать?`)) {
      return;
    }

    try {
      await this.downloadService.downloadClipWithId(highlight, id);
    } catch (e: any) {
      if (e instanceof QuotaExceededError) {
        this.handleQuotaExhaustedClick('clip');
        return;
      }
      alert('Ошибка при запуске загрузки: ' + e.message);
    }
  }

  async downloadBatchZip() {
    if (!this.userProfile()?.isPro && this.userProfile()?.role !== 'admin') {
      this.openProModal(null);
      return;
    }

    const id = this.videoId();
    if (!id) {
      alert('ID видео не найден. Невозможно скачать.');
      return;
    }

    const listToDownload = this.twitchChatState.exportableHighlights();
    if (listToDownload.length === 0) {
      alert('Нет клипов для скачивания.');
      return;
    }

    try {
      await this.downloadService.downloadZipBatch(listToDownload, id);
    } catch (e: any) {
      if (e instanceof QuotaExceededError) {
        this.handleQuotaExhaustedClick('zip');
        return;
      }
      throw e;
    }
  }

  async sendToCloud(highlight: Highlight, event?: Event) {
    // Legacy placeholder, now points to downloadClip if user clicks old button (though I replaced it in UI)
    this.downloadClip(highlight, event);
  }

  // ... methods used in template ...
  setScale(scale: number) {
    this.twitchChatState.interfaceScale.set(scale);
  }

  getScaleClass(scale: number): string {
    return this.interfaceScale() === scale
      ? 'bg-indigo-600 text-white'
      : 'text-gray-400 hover:text-gray-200 hover:bg-gray-700';
  }

  setHeatmapScale(scale: 'log' | 'linear') {
    this.twitchChatState.heatmapScale.set(scale);
  }

  getHeatmapScaleClass(scale: 'log' | 'linear'): string {
    return this.heatmapScale() === scale
      ? 'bg-indigo-600 text-white'
      : 'text-gray-400 hover:text-gray-200 hover:bg-gray-700';
  }

  // DOM id for a highlight card, used by the auto-scroll effect when the
  // currently-selected highlight changes (e.g. via heatmap click, j/l hotkey,
  // ,/. step). Stable across renders because (source, startTime) is unique.
  highlightCardId(h: { source?: string; startTime: number }): string {
    return `hl-${h.source ?? 'peak'}-${h.startTime}`;
  }

  setSourceMode(mode: 'file' | 'url') {
    this.sourceMode.set(mode);
  }

  isValidTwitchUrl(): boolean {
    const url = this.twitchUrl();
    return !!url && (url.includes('twitch.tv/videos/') || /^\d+$/.test(url));
  }

  loadFromUrl() {
    const url = this.twitchUrl();
    if (!this.isValidTwitchUrl()) return;
    this.twitchChatService.loadFromUrl(url);
  }

  onFileSelected(event: Event) {
    // Demo mode: defensive — UI hides the file input but a stale handle could still fire.
    if (this.demoService.isDemoMode()) return;
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.twitchChatService.onFileSelected(input.files[0]);
    }
  }

  handleCsvSubmit() {
    this.twitchChatService.handleCsvSubmit();
  }

  onThresholdChange(event: Event) {
    const val = Number((event.target as HTMLInputElement).value);
    this.history.transactParam('threshold', () => this.twitchChatService.updatePeakMessageThreshold(val),
      { coalesceKey: 'threshold' });
  }

  // Filters
  dismissError() {
    this.twitchChatState.error.set('');
  }

  dismissInfo() {
    this.twitchChatState.infoMessage.set('');
  }

  // A filter change re-filters the raw chat — without it (cloud copy, chat not downloaded on
  // this device) the bins collapse and neither the change nor its undo can be recomputed.
  private changeFilters(label: string, fn: () => void) {
    if (this.needsChatRedownload()) return;
    this.history.transactParam('filters', fn, { label: () => label });
  }

  toggleHideSubs() {
    const hide = !this.filterSettings().hideSubs;
    this.changeFilters(hide ? 'Скрыть сабки' : 'Показать сабки', () => this.twitchChatService.toggleHideSubs(hide));
  }

  addKeyword(val: string) {
    if (val) this.changeFilters(`Бан-слово «${val}»`, () => this.twitchChatService.addFilterKeyword(val));
  }

  removeKeyword(val: string) {
    this.changeFilters(`Убрано бан-слово «${val}»`, () => this.twitchChatService.removeFilterKeyword(val));
  }

  addIgnoredUser(val: string) {
    if (val) this.changeFilters(`Игнор ${val}`, () => this.twitchChatService.addIgnoredUser(val));
  }

  removeIgnoredUser(val: string) {
    this.changeFilters(`Убран из игнора ${val}`, () => this.twitchChatService.removeIgnoredUser(val));
  }

  analyzeHighlightsWithAI() {
    if (this.aiBlockedByMissingChat()) {
      alert('Чат не загружен — без него AI анализирует пустые спайки и тратит квоту впустую. Нажмите «Скачать чат» вверху и повторите.');
      return;
    }
    if (this.aiQuickExhausted()) {
      this.handleQuotaExhaustedClick('aiQuick');
      return;
    }
    this.twitchChatService.analyzeHighlightsWithAI();
  }

  runDeepAnalysis() {
    if (this.aiBlockedByMissingChat()) {
      alert('Чат не загружен — без него AI анализирует пустые спайки и тратит квоту впустую. Нажмите «Скачать чат» вверху и повторите.');
      return;
    }
    if (this.aiDeepExhausted()) {
      this.handleQuotaExhaustedClick('aiDeep');
      return;
    }
    this.twitchChatService.runDeepAnalysis();
  }

  runAiSearch() {
    const q = this.searchInputValue().trim();
    if (!q) return;
    if (this.aiBlockedByMissingChat()) {
      alert('Чат не загружен — без него ИИ-поиск не работает. Нажмите «Скачать чат» вверху и повторите.');
      return;
    }
    if (this.aiSearchExhausted()) {
      this.handleQuotaExhaustedClick('aiSearch');
      return;
    }
    this.twitchChatState.activeTab.set('search');
    this.twitchChatService.runAiSearch(q);
  }

  // Heatmap zoom = px per 15s bin (bar + 1px gap), continuous: Ctrl+wheel / pinch scale
  // it, the zoom bar's edges set it from a time window (utils/heatmap-zoom.ts). Quantized
  // to 1/64 px. The user's choice is `heatmapStepWanted` (default 5 = the old 4px bars;
  // transient view state — kept across VOD switches, not persisted); what's drawn is
  // `heatmapStep` = that clamped to `zoomLimits`: never narrower than «the whole stream
  // fills the width», so a short stream, a wider window or a VOD switch never leaves empty
  // space right of the bars.
  private heatmapStepWanted = signal<number>(STEP_DEFAULT);

  private tooltipService = inject(TooltipService);
  @ViewChild('heatmapContent') private heatmapContentRef?: ElementRef<HTMLElement>;
  @ViewChild('legendPopoverEl') private legendPopoverRef?: ElementRef<HTMLElement>;

  // Scroller geometry for the zoom bar's window: scrollLeft from (scroll), width from a
  // ResizeObserver. Zoom / pan code writes scrollLeft back right away, so the thumb never
  // shows the new step with the old scroll position for a frame.
  private heatmapScrollLeft = signal(0);
  private heatmapViewportW = signal(0);
  private heatmapScroller: HTMLElement | null = null;
  private heatmapScrollerRO: ResizeObserver | null = null;
  @ViewChild('heatmapScroller') private set heatmapScrollerRef(ref: ElementRef<HTMLElement> | undefined) {
    const el = ref?.nativeElement ?? null;
    if (el === this.heatmapScroller) return;
    this.heatmapScrollerRO?.disconnect();
    this.heatmapScrollerRO = null;
    this.heatmapScroller = el;
    if (!el || typeof ResizeObserver === 'undefined') return;
    this.heatmapScrollerRO = new ResizeObserver(() => {
      this.heatmapViewportW.set(el.clientWidth);
      this.heatmapScrollLeft.set(el.scrollLeft);
    });
    this.heatmapScrollerRO.observe(el);
  }

  // Bin grid: bar k sits at k*heatmapStep px inside the content div (bins are dense from
  // 0, one per 15s), so time t is at t/15*heatmapStep — same mapping for the speech lane,
  // ruler, region, readout and category lines. Zoomed out below STEP_LOD the bins are drawn
  // in groups (heatmapColumns), the grid itself stays. heatmapEndSec = right edge of the
  // last bar.
  heatmapEndSec = computed(() => this.trimmedBins().length * 15);
  private heatmapZoomLimits = computed(() => zoomLimits(
    this.trimmedBins().length, this.heatmapViewportW() - 2 * AppComponent.HEATMAP_PAD_LEFT));
  heatmapStep = computed(() => clampStep(this.heatmapStepWanted(), this.heatmapZoomLimits()));
  private heatmapBinsPerColumn = computed(() => binsPerColumn(this.heatmapStep()));
  heatmapColumns = computed(() => buildColumns(this.trimmedBins(), this.heatmapBinsPerColumn()));
  binBarWidth = computed(() => Math.max(1, this.heatmapStep() - AppComponent.HEATMAP_BAR_GAP));
  // Ticks around the visible window only (± one viewport): zoomed in to seconds on a long
  // stream the whole-stream list would be thousands of ticks, rebuilt on every zoom step.
  // Not measured yet → the first 4000px.
  rulerTicks = computed(() => {
    const unit = this.heatmapStep() / 15;
    const w = this.heatmapViewportW();
    const margin = (w > 0 ? w : 4000) / unit;
    const v = this.heatmapView();
    return buildRulerTicks(this.heatmapEndSec(), unit, w > 0 ? v.start - margin : 0, w > 0 ? v.end + margin : margin);
  });
  heatmapView = computed(() => viewWindow(
    this.heatmapScrollLeft(), this.heatmapViewportW(), this.heatmapStep(), this.heatmapEndSec(), AppComponent.HEATMAP_PAD_LEFT));

  // --- Categories (Twitch chapters): painted on the zoom bar track, dashed line on the
  // heatmap at every change, name in the hover readout. Colors by first appearance in this
  // VOD — a category that comes back keeps its color. Literal Tailwind class names. ---
  private static readonly CHAPTER_COLORS = [
    'bg-sky-500/30 text-sky-100',
    'bg-emerald-500/30 text-emerald-100',
    'bg-rose-500/30 text-rose-100',
    'bg-orange-500/30 text-orange-100',
    'bg-violet-500/30 text-violet-100',
    'bg-lime-500/30 text-lime-100',
  ];
  vodChapters = computed<VodChapter[]>(() => {
    const v = this.twitchChatState.vodChapters();
    return v && v.videoId === String(this.twitchChatState.videoId()) ? v.chapters : [];
  });
  zoomBarSegments = computed<ZoomBarSegment[]>(() => {
    const end = this.heatmapEndSec();
    if (!(end > 0)) return [];
    const colors = new Map<string, string>();
    const palette = AppComponent.CHAPTER_COLORS;
    const out: ZoomBarSegment[] = [];
    for (const c of this.vodChapters()) {
      if (c.startSec >= end) break;
      const to = Math.min(c.endSec, end);
      if (!colors.has(c.gameId)) colors.set(c.gameId, palette[colors.size % palette.length]);
      out.push({
        key: `${c.gameId}@${c.startSec}`,
        leftPct: c.startSec / end * 100,
        widthPct: (to - c.startSec) / end * 100,
        cls: colors.get(c.gameId)!,
        label: c.name,
        tip: `${c.name} · ${this.formatTime(c.startSec)} – ${this.formatTime(to)}`,
      });
    }
    return out;
  });
  chapterChangeXs = computed(() => {
    const end = this.heatmapEndSec();
    const unit = this.heatmapStep() / 15;
    return this.vodChapters().slice(1).filter(c => c.startSec > 0 && c.startSec < end).map(c => c.startSec * unit);
  });

  // --- Timeline strip: the top 48px of the heatmap — ruler labels, the selected clip with
  // its edge handles, the playhead knob. It replaced the separate clip-editor track (see
  // src/CLAUDE.md «Heatmap timeline strip»). Everything below the strip (ruler grid, clip
  // tint, region, pins) starts at top-[48px]. ---
  private static readonly HEATMAP_EXACT_CLICK_STEP = 13; // deeper than the old max zoom: bar clicks / readouts get exact
  private static readonly CLIP_SNAP_PX = 6;              // a dragged clip edge sticks to the playhead
  private static readonly DRAG_START_PX = 3;             // press → drag threshold (strip scrub, clip handles)

  // Live value of a clip-edge drag, committed with setClipTimes on pointerup — one autosave
  // instead of one per pointermove (the autosave isn't debounced: snapshot + JSON of the whole
  // chat + IDB). Keyed by the highlight's startTime, not its index: a live-poll rebuild can
  // shift indices mid-drag.
  private clipDraft = signal<{ key: number; start: number; end: number } | null>(null);
  clipDragEdge = signal<'start' | 'end' | null>(null);
  private clipDrag: { edge: 'start' | 'end'; key: number; pointerId: number; x0: number; clientX: number; moved: boolean; base: ClipRange } | null = null;

  // The selected clip as drawn (the draft while dragging) — strip band, handles, tint over the
  // bars, readout, controls row. px on the bin grid, clamped to the last bar. Never reads
  // playerTime (it would rebuild every frame).
  clipView = computed(() => {
    const h = this.currentHighlight();
    if (!h) return null;
    const d = this.clipDraft();
    const own = d !== null && d.key === h.startTime;
    const start = own ? d.start : h.clipStartTime;
    const end = own ? d.end : h.clipEndTime;
    const unit = this.heatmapStep() / 15;
    const lastPx = this.heatmapEndSec() * unit;
    const startPx = Math.min(Math.max(0, start * unit), lastPx);
    const endPx = Math.min(Math.max(startPx, end * unit), lastPx);
    const coarse = this.viewport.prefersCoarsePointer;
    const out = coarse ? 16 : 10;
    const zones = clipHandleZones(startPx, endPx, out, coarse ? 8 : 6);
    return {
      key: h.startTime, start, end, startPx, endPx,
      bandW: Math.max(2, endPx - startPx),
      zones, startGrip: out, endGrip: zones.end.width - out,
      duration: formatClipDuration(end - start),
    };
  });

  // Playhead on the heatmap: transform only, clamped to the last bar (live / a quiet tail →
  // it can be past the bins; the knob dims). Per-frame — keep it out of every @for source.
  playheadView = computed(() => {
    const end = this.heatmapEndSec();
    if (!(end > 0)) return null;
    const t = this.playerTime();
    const x = Math.min(Math.max(0, t), end) * this.heatmapStep() / 15;
    return { transform: `translateX(${x}px)`, past: t > end + 1 };
  });
  playheadWholeSec = computed(() => Math.floor(Math.max(0, this.playerTime())));
  playheadOffscreen = computed<'left' | 'right' | null>(() => {
    const end = this.heatmapEndSec();
    if (!(end > 0) || !(this.heatmapViewportW() > 0)) return null;
    const v = this.heatmapView();
    const t = Math.min(Math.max(0, this.playerTime()), end);
    return t < v.start ? 'left' : t > v.end ? 'right' : null;
  });
  // Tick on the zoom bar track; rounded to 0.05% so the bar isn't re-checked every frame.
  zoomBarPlayheadPct = computed<number | null>(() => {
    const end = this.heatmapEndSec();
    if (!(end > 0)) return null;
    return Math.round(Math.min(Math.max(0, this.playerTime()), end) / end * 2000) / 20;
  });

  // Strip press. Mouse: click = seek, press-drag = scrub (ghost line + time), ONE seek on
  // release — Twitch buffers on every seek and the playhead interpolation guards assume
  // discrete seeks. Touch: tap = seek, a swipe pans natively (→ pointercancel, no seek).
  private stripPress: { pointerId: number; x0: number; y0: number; clientX: number; scrubbing: boolean; cancelled: boolean } | null = null;
  private scrubTime = signal<number | null>(null);
  private stripHoverTime = signal<number | null>(null);

  // The one readout chip (inside the heatmap box — never above it, that's the player):
  // clip drag > region drag > scrub > strip hover (exact time) > bar hover (bin info).
  // Which side of x the chip goes: against the VISIBLE window (content px), not the content
  // end — a scrub / drag readout near the scroller's side (where edge auto-scroll runs) would
  // otherwise render past it and be clipped. `preferLeft` for the clip / region end edge.
  heatmapReadout = computed<{ x: number; label: string; flip: boolean; kind: 'clip' | 'region' | 'scrub' | 'strip' | 'bar'; cls: string } | null>(() => {
    const unit = this.heatmapStep() / 15;
    const lastPx = this.heatmapEndSec() * unit;
    const pad = AppComponent.HEATMAP_PAD_LEFT;
    const w = this.heatmapViewportW();
    const viewL = this.heatmapScrollLeft() - pad;
    const viewR = w > 0 ? Math.min(viewL + w, lastPx + pad) : lastPx + pad;
    const flipFor = (x: number, label: string, preferLeft: boolean) => {
      const cw = label.length * 7 + 16;
      const fitsRight = x + cw + 4 <= viewR;
      const fitsLeft = x - cw - 4 >= viewL;
      return preferLeft ? fitsLeft || !fitsRight : !fitsRight && fitsLeft;
    };
    const edge = this.clipDragEdge();
    const cv = this.clipView();
    if (edge && cv) {
      const x = edge === 'start' ? cv.startPx : cv.endPx;
      const label = `${edge === 'start' ? 'Начало' : 'Конец'} ${formatTimecode(edge === 'start' ? cv.start : cv.end)} · ${cv.duration}`;
      return { x, label, flip: flipFor(x, label, edge === 'end'), kind: 'clip', cls: 'bg-indigo-500 text-white font-semibold shadow' };
    }
    const rd = this.regionDragReadout();
    if (rd) return { ...rd, flip: flipFor(rd.x, rd.label, this.regionDragEdge() === 'out'), kind: 'region', cls: 'bg-white text-gray-900 font-semibold shadow' };
    const scrub = this.scrubTime();
    const at = scrub ?? this.stripHoverTime();
    if (at !== null) {
      const x = at * unit;
      const label = this.heatmapStep() > AppComponent.HEATMAP_EXACT_CLICK_STEP ? formatTimecode(at) : this.formatTime(at);
      const flip = flipFor(x, label, false);
      return scrub !== null
        ? { x, label, flip, kind: 'scrub', cls: 'bg-red-500 text-white font-semibold shadow' }
        : { x, label, flip, kind: 'strip', cls: 'bg-gray-950/90 border border-gray-700 text-gray-200' };
    }
    const hv = this.heatmapHoverInfo();
    return hv ? { ...hv, flip: flipFor(hv.x, hv.label, false), kind: 'bar', cls: 'bg-gray-950/90 border border-gray-700 text-gray-200' } : null;
  });

  // «Zoom to clip» (Z / double-click the clip on the strip / gutter button): the clip ±30s —
  // the old editor track's view — for precise edge editing; again → the previous zoom and
  // position. Remembered in seconds (a scrollLeft means nothing at another step). Any other
  // zoom ends it: «zoomed» = the step is still the one zoom-to-clip set.
  private clipZoom = signal<{ key: number; step: number; prevStep: number; prevStartSec: number } | null>(null);
  clipZoomActive = computed(() => {
    const z = this.clipZoom();
    return !!z && this.heatmapStep() === z.step && this.currentHighlight()?.startTime === z.key;
  });

  /** The player rect (iframe, or the demo poster) — what no tooltip / popover may cover. */
  private playerKeepOut(): FloatRect[] {
    const el = typeof document !== 'undefined' ? document.getElementById('player-frame') : null;
    if (!el) return [];
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0 ? [r] : [];
  }

  // --- Hover readout (replaces per-bar tooltips: one above a tall bar landed on the player) ---
  // Zoomed out, a column = a group of bins: the readout shows its busiest bin (what a
  // click seeks to). The category is named only when the VOD has more than one.
  heatmapHover = signal<number | null>(null); // index into heatmapColumns (= bin index at >= STEP_LOD)
  heatmapHoverInfo = computed(() => {
    const idx = this.heatmapHover();
    if (idx === null) return null;
    const col = this.heatmapColumns()[idx];
    if (!col) return null;
    const step = this.heatmapStep();
    const x = idx * this.heatmapBinsPerColumn() * step + (col.n * step - 1) / 2;
    let label = `${this.formatTime(col.bin.startTime)} · ${col.count} сообщ.`;
    if (this.searchQuery()) label += ` · ${this.columnSearchMatches(col)} совп.`;
    const chapters = this.vodChapters();
    if (chapters.length > 1) {
      const chapter = chapterAt(chapters, col.bin.startTime);
      if (chapter) label += ` · ${chapter.name}`;
    }
    return { x, label, flip: x + label.length * 6 + 20 > this.heatmapEndSec() / 15 * step };
  });

  private updateHeatmapHover(clientX: number): void {
    const el = this.heatmapContentRef?.nativeElement;
    if (!el) return;
    const colPx = this.heatmapBinsPerColumn() * this.heatmapStep();
    const idx = Math.floor((clientX - el.getBoundingClientRect().left) / colPx);
    const next = idx >= 0 && idx < this.heatmapColumns().length ? idx : null;
    if (next !== this.heatmapHover()) this.heatmapHover.set(next);
  }

  onHeatmapPointerLeave(event: PointerEvent): void {
    if (event.pointerType !== 'touch') {
      this.heatmapHover.set(null);
      this.stripHoverTime.set(null);
    }
  }

  // Wheel pan / zoom moves the content under a still cursor — refresh whichever readout it's over.
  private refreshHeatmapHover(event: MouseEvent): void {
    if (AppComponent.isStripEvent(event)) this.stripHoverTime.set(this.heatmapTimeAt(event.clientX));
    else this.updateHeatmapHover(event.clientX);
  }

  private static isStripEvent(event: Event): boolean {
    return !!(event.target as HTMLElement | null)?.closest?.('[data-heatmap-strip]');
  }

  /** Exact time under clientX on the bin grid, clamped to [0, limit] (default: the bars). */
  private heatmapTimeAt(clientX: number, limit = this.heatmapEndSec()): number | null {
    const el = this.heatmapContentRef?.nativeElement;
    if (!el) return null;
    const t = (clientX - el.getBoundingClientRect().left) / this.heatmapStep() * 15;
    return Math.min(Math.max(0, t), limit);
  }

  // A press on the strip / a clip handle preventDefaults pointerdown, which in Chrome also
  // keeps focus where it was — a focused search box would then swallow every hotkey.
  private releaseInputFocus(): void {
    const a = this.document.activeElement;
    if (a instanceof HTMLInputElement || a instanceof HTMLTextAreaElement || a instanceof HTMLSelectElement) this.restoreAppFocus();
  }

  // --- «Область интереса» ---
  regionEnabled = this.twitchChatState.regionEnabled;
  activeRegion = this.twitchChatState.activeRegion;
  potentialHighlightsInRegionCount = this.twitchChatService.potentialHighlightsInRegionCount;
  regionScanBadgeText = computed(() => {
    const r = this.activeRegion();
    return r ? `Включена область интереса ${formatRegion(r)} — будет просканирована только она, а не весь стрим.` : '';
  });
  regionExportBadgeText = computed(() => {
    const r = this.activeRegion();
    return r ? `Включена область интереса ${formatRegion(r)} — экспортируются моменты из области и всё Избранное.` : '';
  });
  formatRegionLabel(r: TimeRegion): string { return formatRegion(r); }
  formatRegionLength(r: TimeRegion): string { return formatRegionDuration(r.outSec - r.inSec); }

  regionView = computed(() => {
    const r = this.activeRegion();
    if (!r) return null;
    const unit = this.heatmapStep() / 15;
    const endPx = this.heatmapEndSec() * unit;
    const inPx = Math.min(r.inSec * unit, endPx);
    const outPx = Math.min(r.outSec * unit, endPx);
    return { inPx, outPx, tailPx: Math.max(0, endPx - outPx) };
  });

  toggleRegion(): void {
    const s = this.twitchChatState;
    this.cancelRegionDrag(); // Alt+P mid-drag unmounts the captured handle — its pointerup never comes
    this.history.transact(() => s.regionEnabled() ? 'Включение области интереса' : 'Выключение области интереса', () => {
      if (s.regionEnabled()) {
        s.regionEnabled.set(false);
        return;
      }
      let r = s.regionRange();
      if (!r) {
        r = this.defaultRegionFromView();
        if (!r) return;
        s.regionRange.set(r);
      }
      s.regionEnabled.set(true);
    });
  }

  // First enable without a saved region: what the heatmap shows right now, 10% in from
  // each side so both handles are visible and grabbable.
  private defaultRegionFromView(): TimeRegion | null {
    const endSec = this.heatmapEndSec();
    let from = 0;
    let to = endSec;
    const scroller = document.getElementById('heatmap-container');
    const content = this.heatmapContentRef?.nativeElement;
    if (scroller && content) {
      const sRect = scroller.getBoundingClientRect();
      const cRect = content.getBoundingClientRect();
      const toSec = (x: number) => (x - cRect.left) / this.heatmapStep() * 15;
      from = Math.max(0, toSec(sRect.left));
      to = Math.min(endSec, toSec(sRect.right));
    }
    const inset = Math.max(0, to - from) * 0.1;
    return clampRegion({ inSec: from + inset, outSec: to - inset }, endSec);
  }

  /** Alt+I / Alt+O — region edge at the playhead; also switches the region on. */
  private setRegionEdgeAtPlayhead(edge: 'in' | 'out'): void {
    const s = this.twitchChatState;
    const r = setRegionEdgeAt(s.regionRange(), edge, this.playerTime(), this.heatmapEndSec());
    if (!r) return;
    this.history.transact(edge === 'in' ? 'Начало области' : 'Конец области', () => {
      s.regionRange.set(r);
      s.regionEnabled.set(true);
    });
  }

  // Edge drag. The live value goes to state.regionDraft (lists re-filter while dragging);
  // pointerup commits it to regionRange — one autosave instead of one per pointermove.
  private regionDrag: { edge: 'in' | 'out'; pointerId: number; clientX: number } | null = null;
  regionDragEdge = signal<'in' | 'out' | null>(null);
  regionDragReadout = computed(() => {
    const edge = this.regionDragEdge();
    const view = this.regionView();
    const r = this.activeRegion();
    if (!edge || !view || !r) return null;
    const x = edge === 'in' ? view.inPx : view.outPx;
    const label = `${edge === 'in' ? 'Начало' : 'Конец'} ${this.formatTime(edge === 'in' ? r.inSec : r.outSec)} · ${formatRegionDuration(r.outSec - r.inSec)}`;
    return { x, label, flip: edge === 'out' || x + 200 > view.outPx + view.tailPx };
  });

  startRegionDrag(edge: 'in' | 'out', event: PointerEvent): void {
    const range = this.twitchChatState.regionRange();
    if (!range || this.regionDrag || this.clipDrag || this.stripPress) return;
    event.preventDefault();
    event.stopPropagation(); // keep the scroller's pinch tracking out of it
    (event.currentTarget as HTMLElement | null)?.setPointerCapture?.(event.pointerId);
    this.regionDrag = { edge, pointerId: event.pointerId, clientX: event.clientX };
    this.regionDragEdge.set(edge);
    this.heatmapHover.set(null);
    this.stripHoverTime.set(null);
    this.twitchChatState.regionDraft.set(range);
    this.startEdgeAutoScroll('region', () => this.regionDrag?.clientX ?? null, () => this.applyRegionDrag());
  }

  onRegionDrag(event: PointerEvent): void {
    const d = this.regionDrag;
    if (!d || event.pointerId !== d.pointerId) return;
    if (event.pointerType === 'mouse' && event.buttons === 0) {
      this.cancelRegionDrag(); // its pointerup went elsewhere (the handle was re-rendered)
      return;
    }
    event.preventDefault();
    d.clientX = event.clientX;
    this.applyRegionDrag();
  }

  endRegionDrag(event: PointerEvent): void {
    const d = this.regionDrag;
    if (!d || event.pointerId !== d.pointerId) return;
    this.stopEdgeAutoScroll('region');
    this.regionDrag = null;
    this.regionDragEdge.set(null);
    const draft = this.twitchChatState.regionDraft();
    if (draft) {
      this.history.transact(d.edge === 'in' ? 'Начало области' : 'Конец области',
        () => this.twitchChatState.regionRange.set(draft));
    }
    this.twitchChatState.regionDraft.set(null);
  }

  // Esc / Alt+P / VOD switch / the handle unmounted: drop the draft, nothing is committed.
  private cancelRegionDrag(): void {
    if (!this.regionDrag) return;
    this.stopEdgeAutoScroll('region');
    this.regionDrag = null;
    this.regionDragEdge.set(null);
    this.twitchChatState.regionDraft.set(null);
  }

  private applyRegionDrag(): void {
    const d = this.regionDrag;
    const el = this.heatmapContentRef?.nativeElement;
    const draft = this.twitchChatState.regionDraft();
    if (!d || !el || !draft) return;
    const t = (d.clientX - el.getBoundingClientRect().left) / this.heatmapStep() * 15;
    const next = moveRegionEdge(draft, d.edge, t, this.heatmapEndSec());
    if (next.inSec !== draft.inSec || next.outSec !== draft.outSec) this.twitchChatState.regionDraft.set(next);
  }

  // Holding a drag near the scroller's side scrolls the heatmap so the edge / scrub can travel
  // past the visible part of a long stream. One loop for region, clip-edge and scrub drags.
  private edgeScroll: { owner: 'region' | 'clip' | 'scrub'; raf: number; clientX: () => number | null; apply: () => void } | null = null;
  private startEdgeAutoScroll(owner: 'region' | 'clip' | 'scrub', clientX: () => number | null, apply: () => void): void {
    this.stopEdgeAutoScroll();
    const EDGE_PX = 32;
    const SPEED_PX = 8;
    const tick = () => {
      const s = this.edgeScroll;
      if (!s) return;
      const x = s.clientX();
      const scroller = this.heatmapScroller;
      if (x !== null && scroller) {
        const rect = scroller.getBoundingClientRect();
        const delta = x < rect.left + EDGE_PX ? -SPEED_PX : x > rect.right - EDGE_PX ? SPEED_PX : 0;
        if (delta !== 0) {
          const before = scroller.scrollLeft;
          scroller.scrollLeft = before + delta;
          if (scroller.scrollLeft !== before) s.apply();
        }
      }
      s.raf = requestAnimationFrame(tick);
    };
    this.edgeScroll = { owner, raf: requestAnimationFrame(tick), clientX, apply };
  }

  /** Without `owner` stops any loop (teardown). */
  private stopEdgeAutoScroll(owner?: 'region' | 'clip' | 'scrub'): void {
    if (!this.edgeScroll || (owner && this.edgeScroll.owner !== owner)) return;
    cancelAnimationFrame(this.edgeScroll.raf);
    this.edgeScroll = null;
  }

  // --- Timeline strip: seek / scrub ---
  onStripPointerDown(event: PointerEvent): void {
    if (this.stripPress || this.clipDrag || this.regionDrag) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    // No stopPropagation: the scroller must still see this pointer to track a pinch that
    // starts on the strip (onHeatmapPointerDown cancels the press on a second finger).
    if (event.pointerType !== 'touch') {
      event.preventDefault(); // no text selection while scrubbing
      this.releaseInputFocus();
    }
    // Captured for touch too: implicit touch capture targets the deepest hit — often a ruler
    // label, which a pinch-zoom can re-render away, and the press would never end. A native
    // pan still happens (touch-action) and still sends pointercancel here.
    (event.currentTarget as HTMLElement | null)?.setPointerCapture?.(event.pointerId);
    this.stripPress = { pointerId: event.pointerId, x0: event.clientX, y0: event.clientY, clientX: event.clientX, scrubbing: false, cancelled: false };
    this.heatmapHover.set(null);
  }

  onStripPointerMove(event: PointerEvent): void {
    const p = this.stripPress;
    if (!p) {
      if (event.pointerType !== 'touch' && !this.clipDrag && !this.regionDrag) this.stripHoverTime.set(this.heatmapTimeAt(event.clientX));
      return;
    }
    if (event.pointerId !== p.pointerId || p.cancelled) return;
    if (event.pointerType === 'touch') {
      // Touch never scrubs. A swipe that doesn't start a native pan (vertical — the scroller
      // only pans x) must not seek on release either: beyond a tap's slop it's not a tap.
      if (Math.hypot(event.clientX - p.x0, event.clientY - p.y0) > 10) p.cancelled = true;
      return;
    }
    p.clientX = event.clientX;
    if (!p.scrubbing) {
      if (Math.abs(event.clientX - p.x0) < AppComponent.DRAG_START_PX) return;
      p.scrubbing = true;
      this.stripHoverTime.set(null);
      this.startEdgeAutoScroll('scrub', () => this.stripPress?.scrubbing ? this.stripPress.clientX : null, () => this.applyScrub());
    }
    this.applyScrub();
  }

  private applyScrub(): void {
    const p = this.stripPress;
    if (!p?.scrubbing) return;
    const t = this.heatmapTimeAt(p.clientX);
    if (t !== null && t !== this.scrubTime()) this.scrubTime.set(t);
  }

  onStripPointerUp(event: PointerEvent): void {
    const p = this.stripPress;
    if (!p || event.pointerId !== p.pointerId) return;
    const scrubbed = this.scrubTime();
    this.onStripPointerCancel();
    if (p.cancelled) return;
    const t = p.scrubbing ? scrubbed : this.heatmapTimeAt(event.clientX);
    if (t !== null) this.seekFromStrip(t);
    if (event.pointerType !== 'touch') this.stripHoverTime.set(this.heatmapTimeAt(event.clientX));
  }

  onStripPointerCancel(event?: PointerEvent): void {
    const p = this.stripPress;
    if (!p || (event && event.pointerId !== p.pointerId)) return;
    this.stripPress = null;
    this.stopEdgeAutoScroll('scrub');
    this.scrubTime.set(null);
  }

  onStripPointerLeave(event: PointerEvent): void {
    if (event.pointerType !== 'touch') this.stripHoverTime.set(null);
  }

  // Double-click on the clip (or a few px around it) = zoom to clip / back. The two clicks
  // before it already seeked there — harmless, same spot.
  onStripDblClick(event: MouseEvent): void {
    const cv = this.clipView();
    const t = this.heatmapTimeAt(event.clientX);
    if (!cv || t === null) return;
    const slack = AppComponent.CLIP_SNAP_PX / (this.heatmapStep() / 15);
    if (t >= cv.start - slack && t <= cv.end + slack) this.toggleClipZoom();
  }

  // The strip's only route to the player — seekAbsolute is the canonical seek path.
  private seekFromStrip(t: number): void {
    this.seekAbsolute(t);
  }

  // --- Timeline strip: clip edge handles. Pointer capture on the handle, move/up bound on
  // it (no window listeners — they'd run app-wide change detection on every mouse move).
  startClipDrag(edge: 'start' | 'end', event: PointerEvent): void {
    const cv = this.clipView();
    if (!cv || this.clipDrag || this.stripPress || this.regionDrag) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation(); // not a strip press; keeps the scroller's pinch tracking out of it
    if (event.pointerType !== 'touch') this.releaseInputFocus();
    (event.currentTarget as HTMLElement | null)?.setPointerCapture?.(event.pointerId);
    this.clipDrag = { edge, key: cv.key, pointerId: event.pointerId, x0: event.clientX, clientX: event.clientX, moved: false, base: { start: cv.start, end: cv.end } };
    this.clipDragEdge.set(edge);
    this.heatmapHover.set(null);
    this.stripHoverTime.set(null);
  }

  onClipDrag(event: PointerEvent): void {
    const d = this.clipDrag;
    if (!d || event.pointerId !== d.pointerId) return;
    if (event.pointerType === 'mouse' && event.buttons === 0) {
      this.cancelClipDrag(); // its pointerup went elsewhere (the handle was re-rendered)
      return;
    }
    event.preventDefault();
    d.clientX = event.clientX;
    if (!d.moved) {
      if (Math.abs(event.clientX - d.x0) < AppComponent.DRAG_START_PX) return;
      d.moved = true;
      this.startEdgeAutoScroll('clip', () => this.clipDrag?.clientX ?? null, () => this.applyClipDrag());
    }
    this.applyClipDrag();
  }

  private applyClipDrag(): void {
    const d = this.clipDrag;
    if (!d) return;
    if (this.currentHighlight()?.startTime !== d.key) {
      this.cancelClipDrag(); // the selection changed under the drag (hotkey, live-poll)
      return;
    }
    // Bounded by the VOD, not the last chat bar (that can end earlier — a clip / marker /
    // speech segment past it would collapse to 0.1s on the first move of its end handle).
    const limit = Math.max(this.heatmapEndSec(), this.videoDurationSec(), d.base.end);
    const raw = this.heatmapTimeAt(d.clientX, limit);
    if (raw === null) return;
    const t = snapTime(raw, [Math.min(Math.max(0, this.playerTime()), limit)], this.heatmapStep() / 15, AppComponent.CLIP_SNAP_PX);
    const next = moveClipEdge(d.base, d.edge, t, limit);
    const cur = this.clipDraft();
    if (!cur || cur.start !== next.start || cur.end !== next.end) this.clipDraft.set({ key: d.key, ...next });
  }

  endClipDrag(event: PointerEvent): void {
    const d = this.clipDrag;
    if (!d || event.pointerId !== d.pointerId) return;
    const draft = this.clipDraft();
    this.finishClipDrag();
    if (this.currentHighlight()?.startTime !== d.key) return;
    if (!d.moved) {
      // A press without a drag = go to that edge (like Shift+I / Shift+O).
      this.seekAbsolute(d.edge === 'start' ? d.base.start : d.base.end);
      return;
    }
    if (!draft || (draft.start === d.base.start && draft.end === d.base.end)) return;
    const idx = this.highlights().findIndex(h => h.startTime === d.key);
    if (idx < 0) return;
    const h = this.highlights()[idx];
    this.history.transact(`${d.edge === 'start' ? 'Начало' : 'Конец'} клипа ${this.momentLabel(h)}`,
      () => this.twitchChatService.setClipTimes(idx, draft.start, draft.end), { focus: h });
  }

  // pointercancel / Esc / selection change: drop the draft, nothing is committed.
  cancelClipDrag(): void {
    if (this.clipDrag) this.finishClipDrag();
  }

  private finishClipDrag(): void {
    this.clipDrag = null;
    this.clipDragEdge.set(null);
    this.clipDraft.set(null);
    this.stopEdgeAutoScroll('clip');
  }

  toggleClipZoom(): void {
    const el = this.heatmapScroller;
    if (!el || !(this.heatmapEndSec() > 0)) return;
    const z = this.clipZoom();
    const zoomed = !!z && this.heatmapStep() === z.step;
    const cv = this.clipView();
    const limits = this.heatmapLimitsFor(el);
    if (z && zoomed && (!cv || cv.key === z.key)) {
      const step = clampStep(z.prevStep, limits);
      this.clipZoom.set(null);
      this.applyHeatmapZoom(el, step, scrollLeftForStart(z.prevStartSec, step));
      return;
    }
    if (!cv) return;
    const r = centeredZoom(clipZoomWindow(cv, this.heatmapEndSec()), this.heatmapInnerWidth(el), limits);
    // Already zoomed to another clip → zoom to this one; «back» still returns to the view
    // before the first zoom.
    this.clipZoom.set(z && zoomed
      ? { ...z, key: cv.key, step: r.step }
      : { key: cv.key, step: r.step, prevStep: this.heatmapStep(), prevStartSec: this.heatmapView().start });
    this.applyHeatmapZoom(el, r.step, r.scrollLeft);
  }

  // Selecting a moment brings its clip into view — scroll only, the zoom stays. Already fully
  // visible → nothing moves (↑/↓ through nearby moments doesn't jiggle the view).
  private ensureClipVisible(h: Highlight): void {
    const el = this.heatmapScroller;
    const end = this.heatmapEndSec();
    if (!el || !(end > 0)) return;
    const range = { start: Math.min(h.clipStartTime, h.startTime), end: Math.max(h.clipEndTime, h.endTime + 15) };
    const start = ensureVisibleStart(this.heatmapView(), range, end);
    if (start === null) return;
    // The seek to the clip start already moved the playhead — keep the page-flip from
    // fighting this (smooth) scroll.
    this.holdFollow(1500);
    const left = scrollLeftForStart(start, this.heatmapStep());
    // A smooth scroll across most of a long stream at deep zoom is just a blur.
    el.scrollTo({ left, behavior: Math.abs(left - el.scrollLeft) > 2 * el.clientWidth ? 'auto' : 'smooth' });
  }

  // Off-screen chevron: bring the playhead into view (centred).
  revealPlayhead(): void {
    const el = this.heatmapScroller;
    const end = this.heatmapEndSec();
    if (!el || !(end > 0)) return;
    const t = Math.min(Math.max(0, this.playerTime()), end);
    const start = ensureVisibleStart(this.heatmapView(), { start: t, end: t }, end);
    if (start === null) return;
    el.scrollLeft = scrollLeftForStart(start, this.heatmapStep());
    this.heatmapScrollLeft.set(el.scrollLeft);
  }

  // Page-flip while playing (Premiere-style): the playhead was on screen and has just left it →
  // the view jumps so it's back in (followStart). A view the user moved away stays put —
  // wasVisible is re-checked on every VIEW change too, not only when the playhead moves.
  // Writing scrollLeft changes heatmapView → the effect runs once more with the same time
  // and only records «visible».
  private followWasVisible = true;
  private followLastT = -1;
  private followHoldUntil = 0;

  private holdFollow(ms = 1000): void {
    this.followHoldUntil = Math.max(this.followHoldUntil, performance.now() + ms);
  }

  private followPlayhead(t: number, view: ViewWindow): void {
    const end = this.heatmapEndSec();
    const el = this.heatmapScroller;
    const moved = t !== this.followLastT;
    this.followLastT = t;
    if (moved && el && !this.clipDrag && !this.regionDrag && !this.stripPress?.scrubbing && performance.now() >= this.followHoldUntil) {
      const start = followStart(this.followWasVisible, t, view, end);
      if (start !== null) {
        el.scrollLeft = scrollLeftForStart(start, this.heatmapStep());
        this.heatmapScrollLeft.set(el.scrollLeft);
      }
    }
    this.followWasVisible = isTimeVisible(t, this.heatmapView(), end);
  }

  // VOD switch: no drag, scrub, zoom-to-clip memory or follow state may leak into the next VOD.
  // The zoom is kept across VODs on purpose (overview levels), but not clip-editing depth:
  // a zoom-to-clip goes back to its previous step, anything deeper than the old max (13)
  // comes back to 13 — the next VOD would otherwise open showing a minute of stream.
  private resetTimelineUiState(): void {
    this.cancelClipDrag();
    this.cancelRegionDrag();
    this.onStripPointerCancel();
    const z = this.clipZoom();
    if (z && this.heatmapStepWanted() === z.step) this.heatmapStepWanted.set(z.prevStep);
    if (this.heatmapStepWanted() > AppComponent.HEATMAP_EXACT_CLICK_STEP) this.heatmapStepWanted.set(AppComponent.HEATMAP_EXACT_CLICK_STEP);
    this.clipZoom.set(null);
    this.stripHoverTime.set(null);
    this.followWasVisible = true;
    this.followLastT = -1;
  }

  // --- Legend popover («?» in the heatmap gutter) ---
  legendPopover = signal<{ top: number; left: number; ready: boolean } | null>(null);
  private legendTimer: ReturnType<typeof setTimeout> | null = null;
  private legendPinned = false; // opened by click/tap — survives mouseleave

  onLegendHover(anchor: HTMLElement, enter: boolean): void {
    if (this.legendTimer) clearTimeout(this.legendTimer);
    this.legendTimer = null;
    if (enter) {
      if (!this.legendPopover()) this.legendTimer = setTimeout(() => this.openLegend(anchor), 150);
    } else if (!this.legendPinned) {
      this.legendTimer = setTimeout(() => this.closeLegend(), 150);
    }
  }

  onLegendPopoverHover(enter: boolean): void {
    if (this.legendTimer) clearTimeout(this.legendTimer);
    this.legendTimer = null;
    if (!enter && !this.legendPinned) this.legendTimer = setTimeout(() => this.closeLegend(), 150);
  }

  toggleLegend(anchor: HTMLElement, event: MouseEvent): void {
    event.stopPropagation();
    if (this.legendTimer) clearTimeout(this.legendTimer);
    this.legendTimer = null;
    if (this.legendPinned && this.legendPopover()) {
      this.closeLegend();
      return;
    }
    this.legendPinned = true;
    if (!this.legendPopover()) this.openLegend(anchor);
  }

  private closeLegend(): void {
    this.legendPinned = false;
    if (this.legendTimer) clearTimeout(this.legendTimer);
    this.legendTimer = null;
    if (this.legendPopover()) this.legendPopover.set(null);
  }

  // Render hidden, measure, then place with the player as keep-out rect.
  private openLegend(anchor: HTMLElement): void {
    this.legendPopover.set({ top: -10000, left: -10000, ready: false });
    this.cdRef.detectChanges();
    const el = this.legendPopoverRef?.nativeElement;
    if (!el) return;
    const size = el.getBoundingClientRect();
    const p = placeFloating(
      anchor.getBoundingClientRect(),
      { width: size.width, height: size.height },
      { width: window.innerWidth, height: window.innerHeight },
      ['right', 'bottom', 'left', 'top'],
      this.playerKeepOut(),
      6,
      4,
    );
    this.legendPopover.set({ top: p.top, left: p.left, ready: true });
  }

  // Wheel on heatmap: pan horizontally by default (vertical wheel → scrollLeft);
  // with Ctrl held, zoom. Zoom anchors the bin under the cursor so it
  // stays visually under the pointer after the resize — matches Figma / browser
  // / video editor convention. preventDefault only fires when we actually consume
  // the event so a wheel over a heatmap that fits the viewport falls through to
  // normal page scroll.
  //
  // Anchor math: bar k's left edge in content-local coordinates is k*step
  // (step = bar + 1px gap). The scroller has px-4 padding (16px) before the
  // bars start. To keep the bin under the cursor visually fixed, we scale the
  // cursor's position-within-bars by newStep/oldStep, then restore the padding
  // offset and subtract the cursor's viewport-x to get the new scrollLeft.
  // Earlier we scaled by bar width alone, without gap or padding, which made the
  // heatmap drift on every step.
  private static readonly HEATMAP_PAD_LEFT = 16; // px-4 on the scroller
  private static readonly HEATMAP_BAR_GAP = 1;   // gap-px between bars
  onHeatmapWheel(event: WheelEvent): void {
    const scroller = event.currentTarget as HTMLElement | null;
    if (!scroller) return;

    if (event.ctrlKey) {
      // Hit clamp or actually zoomed — either way preventDefault so the page
      // doesn't zoom (Ctrl+wheel is the browser's page-zoom shortcut).
      event.preventDefault();
      this.holdFollow();
      const rect = scroller.getBoundingClientRect();
      this.zoomHeatmapAt(scroller, event.clientX - rect.left, AppComponent.wheelZoomFactor(event));
      this.refreshHeatmapHover(event);
      return;
    }

    // Pan mode — only preventDefault when there's actual horizontal overflow,
    // otherwise let the page scroll naturally.
    if (event.deltaY === 0) return;
    if (scroller.scrollWidth <= scroller.clientWidth) return;
    event.preventDefault();
    this.holdFollow();
    scroller.scrollLeft += event.deltaY;
    this.refreshHeatmapHover(event);
  }

  // Ctrl+wheel / trackpad pinch → zoom factor. A mouse notch (deltaY ±100 px) is ×1.16 —
  // about the old one-px-per-notch pace over the old 2..12 px range; a trackpad's small
  // deltas now zoom smoothly instead of a full step per event.
  private static wheelZoomFactor(event: WheelEvent): number {
    const unit = event.deltaMode === 1 ? 33 : event.deltaMode === 2 ? 300 : 1;
    const dy = Math.max(-300, Math.min(300, event.deltaY * unit));
    return Math.exp(-dy * 0.0015);
  }

  private heatmapInnerWidth(scroller: HTMLElement): number {
    return scroller.clientWidth - 2 * AppComponent.HEATMAP_PAD_LEFT;
  }

  // Limits for a zoom gesture: measure the scroller now rather than wait for the
  // ResizeObserver (it reports a resize a frame late).
  private heatmapLimitsFor(scroller: HTMLElement): ZoomLimits {
    if (scroller.clientWidth !== this.heatmapViewportW()) this.heatmapViewportW.set(scroller.clientWidth);
    return this.heatmapZoomLimits();
  }

  // Shared zoom-anchor math — Ctrl+wheel and touch pinch-zoom (onHeatmapPointerMove).
  // Anchors the bin under mouseInViewport so it stays visually fixed after the resize.
  // Zooms from the unquantized step while it still rounds to the current one: a trackpad's
  // tiny deltas each round back to the same 1/64 px step (zoomed out, 1/64 px is a few %),
  // so without the carry-over a slow pinch never zoomed at all.
  private heatmapStepExact = STEP_DEFAULT;
  private zoomHeatmapAt(scroller: HTMLElement, mouseInViewport: number, factor: number): void {
    const limits = this.heatmapLimitsFor(scroller);
    const oldStep = this.heatmapStep();
    const base = clampStep(this.heatmapStepExact, limits) === oldStep ? this.heatmapStepExact : oldStep;
    this.heatmapStepExact = Math.min(limits.max, Math.max(limits.min, base * factor));
    const newStep = clampStep(this.heatmapStepExact, limits);
    if (newStep === oldStep) return;

    const P = AppComponent.HEATMAP_PAD_LEFT;
    const mouseInContent = mouseInViewport + scroller.scrollLeft;
    // Strip the padding before scaling: only the bars area scales with zoom,
    // the padding stays at a fixed 16px and shouldn't be multiplied.
    const mouseInBars = mouseInContent - P;
    const newMouseInBars = mouseInBars * (newStep / oldStep);
    this.applyHeatmapZoom(scroller, newStep, P + newMouseInBars - mouseInViewport);
  }

  // Flush CD synchronously so the new bar widths land in the DOM in this
  // same task — then we set scrollLeft against the new layout, all before
  // the browser paints. Using rAF here instead let the old layout paint
  // first with the old scrollLeft, producing a visible 1-frame drift
  // before the anchor snapped into place.
  private applyHeatmapZoom(scroller: HTMLElement, step: number, scrollLeft: number): void {
    this.heatmapStepWanted.set(step);
    this.cdRef.detectChanges();
    scroller.scrollLeft = scrollLeft;
    this.heatmapScrollLeft.set(scroller.scrollLeft);
  }

  // --- Zoom bar under the heatmap (components/heatmap-zoom-bar.component.ts) ---
  onHeatmapScroll(): void {
    const el = this.heatmapScroller;
    if (el && el.scrollLeft !== this.heatmapScrollLeft()) this.heatmapScrollLeft.set(el.scrollLeft);
  }

  onZoomBarPan(startSec: number): void {
    const el = this.heatmapScroller;
    if (!el) return;
    this.holdFollow();
    el.scrollLeft = scrollLeftForStart(startSec, this.heatmapStep());
    this.heatmapScrollLeft.set(el.scrollLeft);
  }

  onZoomBarEdge(drag: ZoomBarEdgeDrag): void {
    const el = this.heatmapScroller;
    if (!el) return;
    const r = zoomToWindow(drag, drag.edge, this.heatmapInnerWidth(el), this.heatmapLimitsFor(el));
    if (r.step === this.heatmapStep() && Math.abs(r.scrollLeft - el.scrollLeft) < 0.5) return;
    this.holdFollow();
    this.applyHeatmapZoom(el, r.step, r.scrollLeft);
  }

  // Double-click: the whole stream; already there → back to detail (the first DETAIL_LADDER
  // step clearly closer than the fit — not STEP_MAX, that's clip-editing depth) centred on
  // the click.
  onZoomBarToggleFit(atSec: number): void {
    const el = this.heatmapScroller;
    if (!el) return;
    const inner = this.heatmapInnerWidth(el);
    const limits = this.heatmapLimitsFor(el);
    const fit = limits.min;
    this.holdFollow();
    if (Math.abs(this.heatmapStep() - fit) > 1 / 16) {
      this.applyHeatmapZoom(el, fit, 0);
      return;
    }
    const detail = detailStep(fit, limits);
    if (detail <= fit) return;
    this.applyHeatmapZoom(el, detail, scrollLeftForStart(atSec, detail) - (inner + 1) / 2);
  }

  // --- Touch pinch-zoom on the heatmap (mirrors Ctrl+wheel zoom above) ---
  // Pointer Events fire for touch AND mouse, but the 2-pointer branch below
  // is unreachable with a single mouse pointer, so no gating on touch/mobile
  // is needed — these listeners are harmless no-ops for desktop mouse users.
  private activeHeatmapPointers = new Map<number, PointerEvent>();
  private lastHeatmapPinchDistance: number | null = null;

  onHeatmapPointerDown(event: PointerEvent): void {
    this.activeHeatmapPointers.set(event.pointerId, event);
    if (this.activeHeatmapPointers.size !== 2) this.lastHeatmapPinchDistance = null;
    // A second finger turns a strip tap into a pinch — lifting the first must not seek.
    if (this.activeHeatmapPointers.size >= 2 && this.stripPress) this.stripPress.cancelled = true;
    if (event.pointerType === 'touch') this.holdFollow();
  }

  onHeatmapPointerMove(event: PointerEvent): void {
    if (event.pointerType !== 'touch' && !this.regionDrag && !this.clipDrag && !this.stripPress) {
      if (AppComponent.isStripEvent(event)) {
        if (this.heatmapHover() !== null) this.heatmapHover.set(null);
      } else {
        this.updateHeatmapHover(event.clientX);
        if (this.stripHoverTime() !== null) this.stripHoverTime.set(null);
      }
    }
    if (!this.activeHeatmapPointers.has(event.pointerId)) return;
    this.activeHeatmapPointers.set(event.pointerId, event);
    if (this.activeHeatmapPointers.size !== 2) return;

    const [a, b] = Array.from(this.activeHeatmapPointers.values());
    const distance = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    if (this.lastHeatmapPinchDistance !== null) {
      const delta = distance - this.lastHeatmapPinchDistance;
      // Small dead-zone so tiny jitter between frames doesn't cause zoom churn.
      if (Math.abs(delta) > 4) {
        const scroller = event.currentTarget as HTMLElement | null;
        if (scroller) {
          const rect = scroller.getBoundingClientRect();
          const midpointX = (a.clientX + b.clientX) / 2 - rect.left;
          this.zoomHeatmapAt(scroller, midpointX, distance / this.lastHeatmapPinchDistance);
        }
        this.lastHeatmapPinchDistance = distance;
      }
    } else {
      this.lastHeatmapPinchDistance = distance;
    }
  }

  onHeatmapPointerEnd(event: PointerEvent): void {
    this.activeHeatmapPointers.delete(event.pointerId);
    if (this.activeHeatmapPointers.size !== 2) this.lastHeatmapPinchDistance = null;
  }

  // Heatmap bar height. Two scales user-toggleable from interface settings:
  //   - 'log' (default): log1p(count) / log1p(max). Compresses tail so xQc-tier
  //     spikes don't crush other bins, while preserving distinction at the low
  //     end (5 vs 11 msgs visibly different). Good general-purpose default.
  //   - 'linear': count / max. Strict proportionality without any clipping —
  //     for users who want raw "this bin had 2x more messages" visual cue and
  //     accept that one outlier compresses the rest.
  // Bar max = HEATMAP_BAR_MAX: the scroller (195px in the 217px box — together with the
  // controls row it's exactly the old heatmap + clip editor height) minus the 48px time strip,
  // the 20px pin / readout zone under it and the bottom padding.
  private static readonly HEATMAP_BAR_MAX = 120;
  getBarHeight(count: number): number {
    if (count <= 0) return 4;
    const max = this.maxActivity();
    if (max <= 0) return 4;
    const ratio = this.heatmapScale() === 'log'
      ? Math.min(1, Math.log1p(count) / Math.log1p(max))
      : Math.min(1, count / max);
    return Math.max(4, ratio * AppComponent.HEATMAP_BAR_MAX);
  }

  // A column is one bin, or a group of bins when zoomed out (heatmap-zoom.ts) — a group
  // takes the strongest state of its bins (selected > search match > potential peak).
  getColumnColor(col: HeatmapColumn<ActivityBin>): string {
    // Selected highlight takes priority over everything
    if (this.isCurrentHighlightColumn(col)) {
      return 'bg-indigo-400 hover:bg-indigo-300';
    }

    // When a keyword search is active, amber-tint bins with matches by intensity.
    // Bins without matches go dim so the signal is easy to spot visually.
    const query = this.searchQuery();
    if (query) {
      const count = this.columnSearchMatches(col);
      if (count > 0) {
        const max = this.maxSearchBinMatches() || 1;
        const intensity = count / max;
        if (intensity > 0.66) return 'bg-amber-400 hover:bg-amber-300';
        if (intensity > 0.33) return 'bg-amber-500/80 hover:bg-amber-400';
        return 'bg-amber-600/70 hover:bg-amber-500';
      }
      return 'bg-gray-800 hover:bg-gray-700';
    }

    const potential = this.potentialPeakBinTimes();
    for (let t = col.startTime; t <= col.endTime; t += 15) {
      if (potential.has(t)) return 'bg-yellow-500/80 hover:bg-yellow-400';
    }

    return 'bg-gray-700 hover:bg-gray-600';
  }

  isCurrentHighlightColumn(col: HeatmapColumn<ActivityBin>): boolean {
    const ch = this.currentHighlight();
    return !!ch && col.endTime >= ch.startTime && col.startTime <= ch.endTime;
  }

  private columnSearchMatches(col: HeatmapColumn<ActivityBin>): number {
    const matches = this.searchBinMatches();
    let max = 0;
    for (let t = col.startTime; t <= col.endTime; t += 15) max = Math.max(max, matches.get(t) ?? 0);
    return max;
  }

  formatTime(seconds: number): string {
    if (isNaN(seconds) || seconds < 0) {
      seconds = 0;
    }
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }

  // `bin` = the clicked column's busiest bin (the bin itself unless zoomed out). Zoomed in past
  // the old max (a bar is a wide 15s block) the click seeks to the pointer, not the bin start.
  onHeatmapBinClick(bin: ActivityBin, columnIndex: number, event?: MouseEvent) {
    let t = bin.startTime;
    if (event && this.heatmapStep() > AppComponent.HEATMAP_EXACT_CLICK_STEP && this.heatmapBinsPerColumn() === 1) {
      const at = this.heatmapTimeAt(event.clientX);
      if (at !== null) t = Math.min(Math.max(at, bin.startTime), bin.startTime + 15);
    }
    this.seekAbsolute(t);
    // Touch has no hover — a tap shows the readout for the tapped column instead.
    if (this.viewport.prefersCoarsePointer) this.heatmapHover.set(columnIndex);
    const onSpeech = this.activeTab() === 'speech';
    const inTrash = this.activeTab() === 'deleted';
    // With a region on, a bin outside it only seeks — selecting a moment hidden from the
    // list would leave the editor on something the user can't see (pinned are exempt).
    // The current moment wins when it covers the bin: a click inside its own peak (now the
    // natural way to put the playhead there) must not jump to an overlapping neighbour.
    const region = this.activeRegion();
    const covers = (h: Highlight) =>
      (onSpeech || !isTranscriptSource(h.source)) && (inTrash || !h.isDeleted)
      && bin.startTime >= h.startTime && bin.startTime <= h.endTime && isVisibleInRegion(h, region);
    const cur = this.currentHighlight();
    const h = cur && covers(cur) ? cur : this.highlights().find(covers);
    if (h) {
      this.twitchChatService.selectHighlight(h.startTime);
    }
  }

  // Click on a stream-marker triangle: route through the component-level
  // selectHighlight so behavior matches a click on the marker's card in the
  // moments list — seek to clipStartTime (= startTime - 15 for markers, the
  // "beginning of the moment"), play, scroll heatmap, scroll list. Calling
  // service-level selectHighlight directly skips the seek+play+scroll plumbing
  // and leaves the player at marker.startTime instead of clipStartTime, which
  // reads as "+15s into the moment" to the user.
  onHeatmapPinClick(m: Highlight) {
    this.selectHighlight(m.startTime);
  }

  toggleChatOverlay() {
    this.twitchChatState.isChatOverlayEnabled.update(v => !v);
  }

  setChatLayout(layout: 'auto' | 'overlay' | 'side') {
    this.twitchChatState.chatLayoutPreference.set(layout);
  }

  onRewindOffsetChange(event: Event) {
    const val = Number((event.target as HTMLInputElement).value);
    this.history.transactParam('rewind', () => this.twitchChatService.updateRewindOffset(val),
      { coalesceKey: 'preroll' });
  }

  // While an <input> has focus the hotkey handler ignores every key — drop the focus after a
  // mouse change on the Pre-Roll slider (keyboard users keep it to go on adjusting).
  blurTarget(event: Event) {
    (event.target as HTMLElement | null)?.blur?.();
  }

  // A slider drag is one undo step, pauses included (keyboard steps coalesce by time).
  sliderGestureStart(key: string, event: PointerEvent) {
    // A right / middle click isn't a drag — and a context menu can swallow its pointerup.
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    this.history.beginGesture(key);
  }

  sliderGestureEnd(event: Event) {
    this.history.endGesture();
    this.blurTarget(event);
  }

  setSortOrder(order: 'time' | 'count') {
    this.twitchChatState.sortOrder.set(order);
  }

  setActiveTab(tab: 'all' | 'pinned' | 'deleted' | 'search' | 'clips' | 'speech') {
    this.twitchChatState.activeTab.set(tab);
  }

  clearSpeechResults(source: 'transcript-ai' | 'transcript-search') {
    this.history.transact(source === 'transcript-ai' ? 'Очистка участков речи' : 'Очистка ИИ-поиска по речи',
      () => this.transcript.clearResults(source));
  }

  // --- «Речь» handlers (admin-only UI) ---
  isTranscriptHighlight(h: Highlight | null | undefined): boolean {
    return !!h && isTranscriptSource(h.source);
  }

  setSpeechMode(mode: 'segments' | 'text' | 'ai-search') {
    this.speechMode.set(mode);
  }

  onSpeechBandClick(h: Highlight) {
    this.twitchChatState.activeTab.set('speech');
    this.speechMode.set(h.source === 'transcript-search' ? 'ai-search' : 'segments');
    this.selectHighlight(h.startTime);
  }

  onTranscriptSeek(t: number) {
    this.seekAbsolute(t);
  }

  onSpeechModelChange(kind: 'seg' | 'search', event: Event) {
    const v = (event.target as HTMLSelectElement).value === 'flash' ? 'flash' : 'pro';
    (kind === 'seg' ? this.transcript.segModel : this.transcript.searchModel).set(v);
  }

  onSpeechAiInput(event: Event) {
    this.transcript.aiQueryInput.set((event.target as HTMLInputElement).value);
  }

  transcriptBadgeClass(h: Highlight): string {
    if (h.source === 'transcript-search') return TRANSCRIPT_SEARCH_STYLE.badge;
    return TRANSCRIPT_CATEGORIES[h.transcriptCategory as TranscriptCategory]?.badge ?? TRANSCRIPT_SEARCH_STYLE.badge;
  }

  transcriptCategoryLabel(h: Highlight): string {
    return TRANSCRIPT_CATEGORIES[h.transcriptCategory as TranscriptCategory]?.label ?? 'Речь';
  }

  speechChatTooltip(): string {
    if (!this.transcript.chatAvailable()) return 'Чат этого VOD не загружен — скачайте чат, чтобы ИИ учитывал его вместе с речью';
    return this.transcript.useChat()
      ? 'ИИ видит чат рядом с речью (без ников, с фильтрами чата): на какой вопрос отвечает стример, где чат смеётся или взрывается'
      : 'Учитывать чат вместе с речью';
  }

  speechChatToggleClass(): string {
    return this.transcript.useChat() && this.transcript.chatAvailable()
      ? 'px-2 rounded border text-xs transition-colors bg-sky-900/50 text-sky-200 border-sky-600/60'
      : 'px-2 rounded border text-xs transition-colors bg-gray-800 text-gray-400 border-gray-700 hover:text-gray-200 disabled:opacity-40 disabled:hover:text-gray-400';
  }

  speechChipClass(cat: TranscriptCategory): string {
    return this.speechCategoryFilter().has(cat)
      ? 'flex items-center gap-1 px-2 py-0.5 rounded border text-[11px] transition-colors ' + TRANSCRIPT_CATEGORIES[cat].chipOn
      : 'flex items-center gap-1 px-2 py-0.5 rounded border text-[11px] transition-colors bg-gray-800/60 text-gray-500 border-gray-700/50 line-through';
  }

  formatHoursShort(sec: number): string {
    const m = Math.round(sec / 60);
    return m >= 60 ? `${Math.floor(m / 60)}ч ${String(m % 60).padStart(2, '0')}м` : `${m}м`;
  }

  formatSpan(sec: number): string {
    const s = Math.max(0, Math.round(sec));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  }

  formatTokens(n: number): string {
    return n >= 1000 ? `${(n / 1000).toFixed(n >= 100000 ? 0 : 1)}k` : String(n);
  }

  // --- Keyword search ---
  searchInputValue = this.twitchChatState.searchInputValue;
  searchQuery = this.twitchChatState.searchQuery;
  searchExactMatch = this.twitchChatState.searchExactMatch;
  searchHistory = this.twitchChatState.searchHistory;
  searchBinMatches = this.twitchChatState.searchBinMatches;
  searchMode = this.twitchChatState.searchMode;

  searchHighlights = computed(() => this.twitchChatState.searchHighlights());

  // Aggregate match count across all current search highlights (for "Найдено: N в M")
  totalSearchMatches = computed(() => {
    let total = 0;
    for (const h of this.searchHighlights()) total += h.matchCount ?? 0;
    return total;
  });

  // Max per-bin match count in current search — drives amber intensity in heatmap overlay
  maxSearchBinMatches = computed(() => {
    let max = 0;
    for (const v of this.searchBinMatches().values()) if (v > max) max = v;
    return max;
  });

  // AI-mode subset of searchHighlights() — keyword and AI results can coexist
  // on the ПОИСК tab (mode switch alone doesn't clear either), so the AI
  // result-count line and history visibility need a count that doesn't include
  // leftover keyword hits from before the user switched modes.
  aiSearchHighlightsCount = computed(() => this.searchHighlights().filter(h => h.source === 'search-ai').length);

  onSearchInput(event: Event) {
    this.searchInputValue.set((event.target as HTMLInputElement).value);
  }

  setSearchMode(mode: 'keyword' | 'ai') {
    this.twitchChatState.searchMode.set(mode);
  }

  // Single entry point for Enter-key / submit-button — dispatches to the
  // right engine based on the current mode toggle.
  onSearchSubmit() {
    if (this.searchMode() === 'ai') {
      this.runAiSearch();
    } else {
      this.runKeywordSearch();
    }
  }

  runKeywordSearch() {
    const q = this.searchInputValue().trim();
    if (!q) return;
    const before = this.twitchChatState.searchHighlights().length;
    let count = 0;
    this.history.transact(`Поиск «${AppComponent.shorten(q)}»`, () => {
      count = this.twitchChatService.searchByKeywords(q);
    });
    // Auto-switch to search tab on first successful search so user sees results immediately
    if (count > 0 || before > 0) {
      this.twitchChatState.activeTab.set('search');
    }
  }

  runSearchFromHistory(q: string) {
    if (this.searchMode() === 'ai') {
      this.searchInputValue.set(q);
      this.onSearchSubmit();
      return;
    }
    // One step with the query swap — undo puts the previous text back into the box too.
    this.history.transact(`Поиск «${AppComponent.shorten(q)}»`, () => {
      this.searchInputValue.set(q);
      this.runKeywordSearch();
    });
  }

  clearSearchResults() {
    this.history.transact('Очистка результатов поиска', () => {
      this.twitchChatService.clearSearchResults();
      this.searchInputValue.set('');
    });
  }

  toggleSearchExactMatch() {
    const toggle = () => this.twitchChatState.searchExactMatch.update(v => !v);
    // No active query — just the checkbox, nothing to undo.
    if (!this.searchQuery()) { toggle(); return; }
    this.history.transact(() => this.searchExactMatch() ? 'Включение точного совпадения' : 'Выключение точного совпадения', () => {
      toggle();
      this.runKeywordSearch(); // re-run to reflect the new mode
    });
  }

  togglePromoteHighlight(startTime: number, event?: Event) {
    event?.stopPropagation();
    const h = this.momentAt(startTime);
    this.history.transact(`${h?.isPromoted ? 'Убрано из «Всех»' : 'Во «Все»'} ${this.momentLabel(h)}`,
      () => this.twitchChatService.togglePromoteHighlight(startTime), { focus: h });
  }

  isSearchHighlight(h: Highlight | null | undefined): boolean {
    return !!h && (h.source === 'search-keyword' || h.source === 'search-ai');
  }

  // Compact syntax cheat-sheet next to the search input (click the ? icon)
  showSearchSyntaxHelp = signal<boolean>(false);

  toggleSearchSyntaxHelp(event: Event) {
    event.stopPropagation();
    this.showSearchSyntaxHelp.update(v => !v);
  }

  closeSearchSyntaxHelp() {
    this.showSearchSyntaxHelp.set(false);
  }

  // --- Samples popover (click on search badge → floating panel with matched messages) ---
  samplesPopover = signal<{ startTime: number; top: number; left: number; width: number } | null>(null);

  popoverHighlight = computed(() => {
    const p = this.samplesPopover();
    if (!p) return null;
    return this.highlights().find(h => h.startTime === p.startTime) ?? null;
  });

  toggleSamplesPopover(startTime: number, event: Event) {
    event.stopPropagation();
    const current = this.samplesPopover();
    if (current?.startTime === startTime) {
      this.samplesPopover.set(null);
      return;
    }
    const anchor = event.currentTarget as HTMLElement;
    const rect = anchor.getBoundingClientRect();
    // Popover grows downward from the badge, up to 320px wide — narrowed on
    // viewports too small to fit that (phones), so it never overflows the screen.
    const width = Math.min(320, window.innerWidth - 16);
    const viewportWidth = window.innerWidth;
    const preferredLeft = rect.left;
    // If not enough room to the right, shift leftward so it fits
    const left = Math.max(8, Math.min(preferredLeft, viewportWidth - width - 8));
    // Body's max-h-[50vh] bounds actual height; estimate the same cap here so
    // a badge near the bottom of a short viewport flips the popover upward
    // instead of rendering it partly (or fully) below the fold.
    const estimatedHeight = Math.min(320, window.innerHeight * 0.5);
    let top = rect.bottom + 4;
    if (top + estimatedHeight > window.innerHeight - 8) {
      top = Math.max(8, rect.top - estimatedHeight - 4);
    }
    this.samplesPopover.set({ startTime, top, left, width });
  }

  closeSamplesPopover() {
    this.samplesPopover.set(null);
  }

  /**
   * Split "username: body" sample and return the author plus the body broken into
   * match/non-match segments for safe templated rendering (no @let in JIT).
   */
  renderSample(text: string, matchedKeywords: string[] | undefined): { author: string; segments: Array<{ text: string; isMatch: boolean }> } {
    const idx = text.indexOf(': ');
    const author = idx >= 0 ? text.slice(0, idx) : '';
    const body = idx >= 0 ? text.slice(idx + 2) : text;
    const segments = this.twitchChatService.highlightMatchesInSample(body, matchedKeywords);
    return { author, segments };
  }

  mergeSelectedHighlights() {
    const selected = this.twitchChatState.selectedToMerge();
    const originals = this.highlights().filter(h => selected.has(h.startTime));
    if (originals.length < 2) return;
    const first = originals.reduce((a, b) => (b.startTime < a.startTime ? b : a));
    const count = originals.length;
    const id = this.history.transact(`Объединение ${count} ${AppComponent.pluralMoments(count)}`,
      () => this.twitchChatService.mergeSelectedHighlights(),
      { focus: first, focusAfter: () => this.currentHighlight(), aiKey: () => first.startTime });
    if (id === null) return;
    // «Объединено N … · Отменить» for 10s — while this merge is still the step Ctrl+Z would undo.
    if (this.mergeBannerTimer) clearTimeout(this.mergeBannerTimer);
    this.mergeBannerState.set({ entryId: id, count });
    this.mergeBannerTimer = setTimeout(() => {
      this.mergeBannerTimer = null;
      if (this.mergeBannerState()?.entryId === id) this.mergeBannerState.set(null);
    }, 10_000);
  }

  undoFromMergeBanner() {
    if (this.mergeBanner()) this.runHistory('undo');
    this.mergeBannerState.set(null);
  }

  toggleMergeSelection(startTime: number) {
    this.twitchChatState.selectedToMerge.update(set => {
      const newSet = new Set(set);
      if (newSet.has(startTime)) newSet.delete(startTime);
      else newSet.add(startTime);
      return newSet;
    });
  }

  selectHighlight(startTime: number) {
    this.twitchChatService.selectHighlight(startTime);
    const h = this.highlights().find(h => h.startTime === startTime);
    // The seek below moves the playhead far away — the page-flip mustn't race the scroll.
    if (h) this.holdFollow(1500);
    if (h && this.player) {
      this.seekAbsolute(h.clipStartTime);
      // Mark as user-initiated so the PLAY handler doesn't dismiss the upcoming event
      // as a seek-buffer resume. Without this, switching highlights while paused leaves
      // isPlayerPaused=true, freezing the rAF playhead loop even though audio plays.
      this.userPlayIntentTime = Date.now();
      this.player.play();
      // play() can pull focus into the iframe; restore so subsequent hotkeys
      // continue to reach the window:keydown handler.
      this.restoreAppFocus();
    }
    if (h) this.ensureClipVisible(h);
  }

  getSentimentRussian(sentiment: string): string {
    return sentiment || 'Нейтральный';
  }

  /**
   * Tailwind classes for the sentiment badge. Gemini returns one of five
   * values (Позитивный / Негативный / Нейтральный / Смешанный / Восторженный)
   * — we color-code them so users can scan tone at a glance.
   */
  getSentimentClasses(sentiment: string | undefined | null): string {
    switch (sentiment) {
      case 'Позитивный':
        return 'bg-green-900/40 text-green-300 border border-green-700/40';
      case 'Восторженный':
        return 'bg-amber-900/40 text-amber-300 border border-amber-700/40';
      case 'Негативный':
        return 'bg-red-900/40 text-red-300 border border-red-700/40';
      case 'Смешанный':
        return 'bg-purple-900/40 text-purple-300 border border-purple-700/40';
      case 'Нейтральный':
      default:
        return 'bg-gray-700/50 text-gray-400 border border-gray-600/40';
    }
  }

  /**
   * Highlight card visual state via ngClass. Stacks three independent visual
   * signals: source-type left stripe (search=amber / AI=purple / regular=none),
   * background tint per source, and full ring-outline when the card is the
   * currently selected highlight (indigo, sits on top of any source styling).
   *
   * Replaces the prior pile of [class.X]= bindings — those used underscore-
   * encoded class names (e.g. bg-indigo-900_20) which Angular wouldn't reject
   * but Tailwind never matched, so several states were silently no-op.
   */
  /** Multi-value `data-tour` attribute for highlight cards. Tour overlay uses
   *  `[data-tour~="value"]` to match cards by any of the listed tokens. Lets a single
   *  card carry both "highlight-card-first" and "highlight-card-ai" tags simultaneously. */
  cardDataTour(h: Highlight, isFirst: boolean): string | null {
    const tokens: string[] = [];
    if (isFirst) tokens.push(this.ANCHORS.HIGHLIGHT_CARD_FIRST);
    if (h.source === 'ai-deep-dive') tokens.push(this.ANCHORS.HIGHLIGHT_CARD_AI);
    return tokens.length > 0 ? tokens.join(' ') : null;
  }

  getHighlightCardClasses(h: Highlight): string[] {
    const classes: string[] = [];
    const isCurrent = this.currentHighlight()?.startTime === h.startTime;
    const isSearch = this.isSearchHighlight(h);
    const isAi = h.source === 'ai-deep-dive';
    const isStreamMarker = h.source === 'stream-marker';
    const isTranscript = isTranscriptSource(h.source);
    // Subsumed: this highlight's startTime falls inside a pinned highlight's clipRange.
    // Visually muted so the user can scan the list and skip already-covered moments
    // (the parent in Избранном holds the consolidated take). Bypassed for the currently
    // selected card — selection styling wins to keep navigation feedback obvious.
    const isContained = !isCurrent && this.containingPinnedByStartTime().has(h.startTime);

    // Background tint per source / state
    if (isCurrent) {
      classes.push('bg-indigo-900/30');
    } else if (isContained) {
      classes.push('bg-gray-900/30');
    } else if (isSearch && h.isPromoted) {
      classes.push('bg-amber-900/20');
    } else if (isSearch) {
      classes.push('bg-amber-950/30');
    } else if (isAi) {
      classes.push('bg-purple-950/30');
    } else if (isStreamMarker) {
      classes.push('bg-cyan-950/30');
    } else if (isTranscript) {
      classes.push('bg-slate-900/50');
    } else {
      classes.push('bg-gray-800/40');
    }

    // Left stripe color (always 4 px wide via static class on host)
    if (isSearch) {
      classes.push('border-l-amber-500');
    } else if (isTranscript) {
      classes.push(h.source === 'transcript-search'
        ? TRANSCRIPT_SEARCH_STYLE.stripe
        : (TRANSCRIPT_CATEGORIES[h.transcriptCategory as TranscriptCategory]?.stripe ?? TRANSCRIPT_SEARCH_STYLE.stripe));
    } else if (isAi) {
      classes.push('border-l-purple-500');
    } else if (isStreamMarker) {
      classes.push('border-l-cyan-500');
    } else if (isCurrent) {
      classes.push('border-l-indigo-500');
    } else {
      classes.push('border-l-transparent');
    }

    // Selection — full ring outline (sits over source styling)
    if (isCurrent) {
      classes.push('ring-2', 'ring-indigo-500', 'shadow-lg', 'shadow-indigo-900/40');
    }

    // Mute opacity for subsumed cards so they recede visually but stay readable on hover.
    if (isContained) {
      classes.push('opacity-60', 'hover:opacity-100');
    }

    return classes;
  }

  togglePinHighlight(startTime: number, event?: Event) {
    if (event) event.stopPropagation();

    // Auto-advance logic
    const isCurrent = this.currentHighlight()?.startTime === startTime;
    let nextStartTime: number | null = null;

    if (isCurrent) {
      const list = this.sortedHighlights();
      const idx = list.findIndex(h => h.startTime === startTime);
      if (idx !== -1 && idx < list.length - 1) {
        nextStartTime = list[idx + 1].startTime;
      }
    }

    const h = this.momentAt(startTime);
    this.history.transact(`${h?.isPinned ? 'Открепление' : 'Закрепление'} ${this.momentLabel(h)}`, () => {
      this.twitchChatService.togglePinHighlight(startTime);

      if (isCurrent && nextStartTime !== null) {
        this.selectHighlight(nextStartTime);
      }
    }, { focus: h });
  }

  toggleLibrary() {
    this.isLibraryOpen.update(v => !v);
  }

  toggleSidebar() {
    // Manual click → lock auto-resize for the rest of this session.
    this.hasManuallyToggledSidebar = true;
    this.isSidebarCollapsed.update(v => {
      const next = !v;
      if (typeof window !== 'undefined') {
        localStorage.setItem('peakster-sidebar-collapsed', String(next));
      }
      return next;
    });
  }

  onWindowResize() {
    if (this.hasManuallyToggledSidebar) return;
    if (typeof window === 'undefined') return;
    // Don't auto-collapse during onboarding — user needs the wizard (steps 1/2/3) visible.
    if (!this.isProcessed()) return;
    const shouldCollapse = window.innerWidth < AppComponent.SIDEBAR_AUTO_COLLAPSE_PX;
    if (this.isSidebarCollapsed() !== shouldCollapse) {
      this.isSidebarCollapsed.set(shouldCollapse);
    }
  }

  toggleSourceSection() {
    this.isSourceSectionCollapsed.update(v => !v);
  }

  toggleAvatarMenu(event: MouseEvent) {
    event.stopPropagation();
    this.isAvatarMenuOpen.update(v => !v);
  }

  closeLibrary() {
    this.isLibraryOpen.set(false);
  }

  private libraryOpenSeq = 0;

  async onVideoSelectedFromLibrary(videoInput: string) {
    this.sourceMode.set('url');

    // Extract videoId whether the input is a pure ID or a URL
    let videoId = videoInput;
    if (videoInput.includes('twitch.tv/videos/')) {
      const parts = videoInput.split('/');
      videoId = parts[parts.length - 1].split('?')[0]; // strip query params if any
    }

    // Set the complete URL for other parts of the app
    this.twitchUrl.set(`https://twitch.tv/videos/${videoId}`);

    // Same-VOD click (e.g. user clicks a top-clip in the library while already
    // viewing that exact VOD): skip the destroy/reload cycle entirely. The
    // existing flow below would `destroyPlayer()` then load the same state,
    // but the videoId signal is unchanged so the player-init effect (line ~1978)
    // does NOT fire — leaves the iframe destroyed with no replacement → black.
    // Just consume the seek hint left by processClip (if any) and close library.
    const currentVid = this.twitchChatState.videoId();
    if (currentVid && String(currentVid) === videoId) {
      const pendingSeek = this.twitchChatState.pendingSeekAfterLoad();
      if (pendingSeek !== null) {
        this.seekAbsolute(pendingSeek);
        this.twitchChatState.pendingSeekAfterLoad.set(null);
      }
      this.closeLibrary();
      return;
    }

    // If the current VOD has an in-flight chat download, snapshot its partial state
    // to IDB and bump the session id so old callbacks no-op. Without this, the
    // imminent loadStateObject / resetState below clobbers the in-memory chatComments
    // + cursor (no resume possible later) AND the in-flight callbacks would mutate
    // the new VOD's state when they next fire (corrupted UI).
    // Also with no current VOD: a fresh download whose video info hasn't arrived yet has
    // videoId null — its late onMeta would otherwise land on the VOD opened below.
    if ((currentVid && String(currentVid) !== videoId) || (!currentVid && this.twitchChatState.isChatDownloading())) {
      this.twitchChatService.supersedePartialDownload();
    }

    // Load state: local IDB vs cloud copy (Pro) — whichever is current.
    const openSeq = ++this.libraryOpenSeq;
    try {
      const opened = await this.twitchChatState.resolveProjectForOpen(videoId);
      // A cloud round-trip can take a moment — if the user picked another VOD
      // meanwhile, that later click wins.
      if (openSeq !== this.libraryOpenSeq) return;
      const state = opened.state;
      // Stop any active player
      if (this.player) this.destroyPlayer();

      if (state) {
        // A cloud copy (another device's edits) replaces ours — its undo history doesn't apply.
        this.twitchChatState.loadStateObject(state, opened.decision?.kind === 'remote' ? 'cloud' : 'local');
        this.twitchChatState.finishOpen(opened);
        // Auto-resume: a saved project where the chat download was interrupted
        // (chatDownloadComplete === false) restarts where it stopped. loadFromUrl
        // detects resume mode by checking the just-loaded chatDownloadComplete +
        // matching videoId + non-empty chatComments — it then calls GQL with the
        // persisted cursor instead of resetting state.
        if (state.chatDownloadComplete === false
            && state.chatComments
            && state.chatComments.length > 0) {
          this.twitchChatService.loadFromUrl(`https://twitch.tv/videos/${videoId}`);
        }
      } else {
        console.warn('No state found for video ID', videoId, 'Starting fresh download.');
        // Clear state to ensure we cleanly load a blank template
        this.twitchChatState.resetState();
        // Set the state's knowledge of the ID so it knows what to save later
        this.twitchChatState.videoId.set(videoId);
        // Fire the download service
        this.twitchChatService.loadFromUrl(`https://twitch.tv/videos/${videoId}`);
      }
    } catch (err) {
      console.error('Failed to load project from library', err);
    }
    this.closeLibrary();
  }

  needsChatRedownload = this.twitchChatState.needsChatRedownload;

  redownloadChatNow(): void {
    const vid = this.videoId();
    if (!vid) return;
    // loadFromUrl detects the redownload case (same VOD, no chatComments, bins
    // present) and skips resetState — analyzed highlights, AI descriptions,
    // pinned/deleted/merged states, custom clip ranges all survive. Only the
    // raw chat is repopulated; the post-download recalc rebuilds clusters with
    // identical signatures, so nothing visible changes except the missing-chat
    // CTA disappears and overlay/export start working.
    this.twitchChatService.loadFromUrl(`https://twitch.tv/videos/${vid}`);
  }

  softDeleteHighlight(startTimeToDelete: number, event?: Event) {
    if (event) event.stopPropagation();

    const isCurrent = this.currentHighlight()?.startTime === startTimeToDelete;
    let nextStartTime: number | null = null;

    if (isCurrent) {
      const list = this.sortedHighlights();
      const idx = list.findIndex(h => h.startTime === startTimeToDelete);
      if (idx !== -1 && idx < list.length - 1) {
        nextStartTime = list[idx + 1].startTime;
      }
    }

    const h = this.momentAt(startTimeToDelete);
    this.history.transact(`Удаление ${this.momentLabel(h)}`, () => {
      this.twitchChatService.softDeleteHighlight(startTimeToDelete);

      if (isCurrent && nextStartTime !== null) {
        this.selectHighlight(nextStartTime);
      }
    }, { focus: h });
  }

  restoreHighlight(startTimeToRestore: number, event?: Event) {
    if (event) event.stopPropagation();

    // Auto-advance if in Deleted tab and item disappears
    const isCurrent = this.currentHighlight()?.startTime === startTimeToRestore;
    let nextStartTime: number | null = null;

    if (isCurrent) {
      const list = this.sortedHighlights();
      const idx = list.findIndex(h => h.startTime === startTimeToRestore);
      if (idx !== -1 && idx < list.length - 1) {
        nextStartTime = list[idx + 1].startTime;
      }
    }

    const h = this.momentAt(startTimeToRestore);
    this.history.transact(`Восстановление ${this.momentLabel(h)}`, () => {
      this.twitchChatService.restoreHighlight(startTimeToRestore);

      if (isCurrent && nextStartTime !== null) {
        this.selectHighlight(nextStartTime);
      }
    }, { focus: h });
  }

  deleteHighlightPermanently(startTimeToDelete: number, event?: Event) {
    if (event) event.stopPropagation();

    const isCurrent = this.currentHighlight()?.startTime === startTimeToDelete;
    let nextStartTime: number | null = null;

    if (isCurrent) {
      const list = this.sortedHighlights();
      const idx = list.findIndex(h => h.startTime === startTimeToDelete);
      if (idx !== -1 && idx < list.length - 1) {
        nextStartTime = list[idx + 1].startTime;
      }
    }

    const h = this.momentAt(startTimeToDelete);
    this.history.transact(`Удаление навсегда ${this.momentLabel(h)}`, () => {
      this.twitchChatService.deleteHighlightPermanently(startTimeToDelete);
    }, { focus: h });

    if (isCurrent && nextStartTime !== null) {
      this.selectHighlight(nextStartTime);
    }
  }

  exportClipsTxt() {
    if (this.demoService.isDemoMode()) return;
    this.exportService.exportClipsTxt();
  }

  exportDownloadScript(platform: 'windows' | 'unix') {
    if (!this.userProfile()?.isPro && this.userProfile()?.role !== 'admin') {
      this.openProModal(null);
      return;
    }
    this.exportService.exportDownloadScript(platform);
  }

  openPathHelp(event?: Event) {
    if (event) event.preventDefault();
    this.showPathHelp.set(true);
  }

  closePathHelp() {
    this.showPathHelp.set(false);
  }

  openHelpModal() {
    this.showHelpModal.set(true);
  }

  closeHelpModal() {
    this.showHelpModal.set(false);
  }

  restartTourFromHelp() {
    this.showHelpModal.set(false);
    // Small delay so the help modal close animation doesn't compete with tour spotlight measurement.
    setTimeout(() => this.tourService.restart(), 200);
  }

  openMarkerSourcesModal() {
    this.isAvatarMenuOpen.set(false);
    this.showMarkerSourcesModal.set(true);
  }

  closeMarkerSourcesModal() {
    this.showMarkerSourcesModal.set(false);
  }

  openAdminPanel() {
    if (!this.isAdmin()) return;          // belt-and-suspenders; backend rejects too
    this.isAvatarMenuOpen.set(false);
    this.isAdminPanelOpen.set(true);
  }

  closeAdminPanel() {
    this.isAdminPanelOpen.set(false);
  }

  onWindowFocus() {
    // maybe refresh logic
  }

  onWindowBlur() {
    this.isPlayerFocused.set(false);
    // Its keyup would land elsewhere (alt-tab, the player iframe) — never leave it stuck.
    this.rightAltHeld = false;
    // Browsers fire window:blur as focus moves into a cross-origin iframe (Twitch
    // embed). Schedule a microtask to detect that case and yank focus back to
    // <body> so the window:keydown handler keeps receiving hotkeys. If the user
    // genuinely alt-tabbed, activeElement is not the iframe and this is a no-op.
    setTimeout(() => {
      const iframe = this.document.querySelector('#twitch-player iframe') as HTMLIFrameElement | null;
      if (iframe && this.document.activeElement === iframe) this.restoreAppFocus();
    }, 0);
  }

  // rAF loop: updates playerTime every frame via interpolation between SDK samples.
  // SDK's getCurrentTime only updates ~5 Hz; wallclock-based interpolation fills the gaps
  // so the playhead moves smoothly at display refresh rate.
  private startPlayheadTicking(): void {
    if (this.playheadRafId !== null) return;
    const tick = () => {
      if (!this.player) { this.playheadRafId = null; return; }

      // Periodic autoplay-next hijack check (1s throttle). Fallback for cases where
      // the PLAY event doesn't fire on the swapped video.
      const nowMs = Date.now();
      if (nowMs - this.lastVideoIdCheckMs > 1000) {
        this.lastVideoIdCheckMs = nowMs;
        if (this.checkVideoHijack()) { this.playheadRafId = null; return; }
      }

      // Late-arriving duration: getDuration() often returns 0 at READY for VODs whose
      // metadata hasn't loaded. Retry each tick (including while paused) until we get it.
      if (this.videoDurationSec() === 0) {
        try {
          const d = this.player.getDuration();
          if (typeof d === 'number' && d > 0) this.videoDurationSec.set(d);
        } catch (e) {}
      }

      // Frozen during pause — playerTime was set by whoever paused (K handler, timeline click,
      // or delayed PAUSE event re-read). Keep ticking (cheap) so we auto-resume on play.
      if (this.isPlayerPaused || this.isPausingNow) {
        this.playheadRafId = requestAnimationFrame(tick);
        return;
      }

      const now = performance.now();
      const duringSeek = Date.now() - this.lastSeekAtMs < this.SEEK_ACCUMULATE_WINDOW_MS;

      // Re-sample from SDK periodically. Twitch's getCurrentTime updates at low frequency
      // and may return stale values, so we only re-anchor when the raw value changes.
      // Strictly monotonic-forward: SDK ahead → snap forward, SDK behind → keep our interp.
      // Large drifts (>5s) are treated as stale SDK readings and ignored, BUT counted —
      // 3+ consecutive large drifts force a snap to SDK as a recovery backstop (the
      // "stuck after long buffer" trap that previously required a fresh seek to fix).
      if (now - this.lastSampleMs > 100) {
        try {
          const sdkTime = this.player.getCurrentTime();
          if (typeof sdkTime === 'number') {
            const sdkAtSeekTarget = Math.abs(sdkTime - this.lastSeekTargetSec) < 0.05;
            const recentSeek = Date.now() - this.lastSeekAtMs < 30_000;

            if (recentSeek && sdkAtSeekTarget) {
              // SDK is parked at the seek target — Twitch is buffering at the new position.
              // Anchor display to target so the post-buffer transition stays smooth (no
              // accumulated elapsed → forward-jump when buffer ends), and start/maintain
              // the stuck-since timer that gates the freeze branch below.
              if (this.sdkBufferStuckSinceMs === null) this.sdkBufferStuckSinceMs = now;
              this.lastSampleSec = this.lastSeekTargetSec;
              this.lastSampleMs = now;
              this.prevSdkSample = sdkTime;
            } else {
              // SDK has moved past target (or away from it) — playback active or stale
              // pre-seek value during seek window.
              this.sdkBufferStuckSinceMs = null;

              if (sdkTime !== this.prevSdkSample) {
                this.prevSdkSample = sdkTime;

                // During seek window, skip drift logic for non-target values — they may
                // be stale pre-seek readings (esp. on backward seeks) which would snap
                // interp forward to the pre-seek position via the `drift < 0` branch.
                if (!duringSeek) {
                  // Preempt ENDED: Twitch's auto-advance-to-next-video countdown is
                  // triggered by ENDED, and pause() after the fact doesn't cancel it.
                  // Stop playback ~2s before real end so ENDED never fires. Display
                  // playhead is anchored to duration so timeline visually "reaches end".
                  const dur = this.videoDurationSec();
                  if (dur > 0 && sdkTime >= dur - 2.0 && !this.didVideoEnd) {
                    this.didVideoEnd = true;
                    this.isPlayerPaused = true;
                    try { this.player.pause(); } catch (e) {}
                    this.lastSampleSec = dur;
                    this.lastSampleMs = now;
                  } else {
                    const currentInterp = this.lastSampleSec + ((now - this.lastSampleMs) / 1000) * this.currentPlaybackRate;
                    const drift = currentInterp - sdkTime;
                    if (Math.abs(drift) > 5.0) {
                      // SDK way off — usually a stale post-seek reading. Ignore for
                      // anchor purposes BUT count consecutive occurrences. 3+ in a row
                      // means our interp is genuinely out of sync (background tab
                      // throttling, long buffer where SDK caught up far behind), force
                      // re-anchor to SDK. Without this counter, the "stale" assumption
                      // traps interp permanently in the wandering position until the
                      // user does a fresh seek (which the user reported as the only
                      // workaround pre-fix).
                      this.consecutiveLargeDriftSamples++;
                      if (this.consecutiveLargeDriftSamples >= this.LARGE_DRIFT_RECOVERY_THRESHOLD) {
                        this.lastSampleSec = sdkTime;
                        this.lastSampleMs = now;
                        this.consecutiveLargeDriftSamples = 0;
                        this.resetPlaybackRateSample();
                      }
                    } else if (drift < 0) {
                      // SDK ahead of interp — catch up forward (visible jump is forward, harmless)
                      this.lastSampleSec = sdkTime;
                      this.lastSampleMs = now;
                      this.consecutiveLargeDriftSamples = 0;
                    } else {
                      // SDK at or behind interp — keep interp as anchor. Never jump back.
                      this.lastSampleSec = currentInterp;
                      this.lastSampleMs = now;
                      this.consecutiveLargeDriftSamples = 0;
                    }
                    this.updatePlaybackRate(sdkTime, now);
                  }
                }
              }
            }
          }
        } catch (e) {}
      }

      // Buffer-stuck freeze: when SDK has been reporting ≈seekTarget for >250ms after a
      // recent seek (set by re-sample block above), pin the playhead at target. Catches
      // slow distant seeks where Twitch's buffer exceeds SEEK_ACCUMULATE_WINDOW_MS — without
      // this, interp would race ahead of audio and the >5s drift guard would lock in the
      // offset on the first SDK sample that escapes target. Fast seeks transition past
      // target before the threshold trips, so smooth UX is preserved.
      if (this.sdkBufferStuckSinceMs !== null
          && now - this.sdkBufferStuckSinceMs > this.BUFFER_STUCK_THRESHOLD_MS) {
        this.playerTime.set(this.lastSeekTargetSec);
        this.playheadRafId = requestAnimationFrame(tick);
        return;
      }

      const elapsed = (now - this.lastSampleMs) / 1000;
      const interpolated = this.lastSampleSec + elapsed * this.currentPlaybackRate;
      // Clamp to video duration — at end-of-playback the SDK time freezes at duration
      // but our wallclock interp keeps ticking forward (drift > 0 path re-anchors to our
      // interp, compounding the overshoot). Without this clamp the playhead slowly
      // drifts past the timeline's right edge after video ends.
      const duration = this.videoDurationSec();
      this.playerTime.set(duration > 0 ? Math.min(interpolated, duration) : interpolated);
      this.playheadRafId = requestAnimationFrame(tick);
    };
    this.playheadRafId = requestAnimationFrame(tick);
  }

  private stopPlayheadTicking(): void {
    if (this.playheadRafId !== null) {
      cancelAnimationFrame(this.playheadRafId);
      this.playheadRafId = null;
    }
  }

  // Schedules play() to fire once the post-pause settle window has elapsed.
  // Self-correcting: if PAUSE event updates lastPauseTime after the initial schedule,
  // the timer re-checks elapsed on fire and re-defers if still too early.
  private schedulePlay(): void {
    const elapsed = Date.now() - this.lastPauseTime;
    const delay = this.MIN_PAUSE_PLAY_DELAY_MS - elapsed;
    if (delay <= 0) {
      if (this.pendingPlayTimeout) { clearTimeout(this.pendingPlayTimeout); this.pendingPlayTimeout = null; }
      this.playFromKeyboard();
      return;
    }
    if (this.pendingPlayTimeout) clearTimeout(this.pendingPlayTimeout);
    this.pendingPlayTimeout = setTimeout(() => {
      this.pendingPlayTimeout = null;
      // Re-check: PAUSE event may have updated lastPauseTime since this was scheduled.
      // Also don't play if Twitch PAUSE event hasn't fired yet (isPausingNow still true).
      const elapsedNow = Date.now() - this.lastPauseTime;
      if (elapsedNow < this.MIN_PAUSE_PLAY_DELAY_MS || this.isPausingNow) {
        this.schedulePlay(); // not enough time passed yet; re-defer
      } else {
        this.playFromKeyboard();
      }
    }, delay);
  }

  // Move focus off the embedded Twitch iframe back to <body> so window:keydown
  // continues to receive hotkeys. Cross-origin iframes capture keyboard input when
  // they hold focus, so any path that lets the iframe grab focus (player.play(),
  // a direct mouse click on the player) must end here.
  private restoreAppFocus(): void {
    const body = this.document.body;
    if (!body) return;
    if (!body.hasAttribute('tabindex')) body.setAttribute('tabindex', '-1');
    const iframe = this.document.querySelector('#twitch-player iframe') as HTMLIFrameElement | null;
    if (iframe && this.document.activeElement === iframe) iframe.blur();
    body.focus();
  }

  private playFromKeyboard(): void {
    if (!this.player) return;
    const iframe = this.document.querySelector('#twitch-player iframe') as HTMLIFrameElement | null;
    if (!iframe) { this.player.play(); return; }

    // Slide chat overlay off the iframe bbox so Twitch's visibility check passes.
    // detectChanges() forces Angular to flush the DOM update synchronously; reading
    // offsetHeight forces the browser to apply the transform before play() runs.
    // Restored on PLAY event (below). Safety timeout ONLY restores if play actually
    // succeeded — otherwise overlay stays hidden (avoids showing it on a paused player
    // when play() was rejected by Twitch).
    this.isOverlayShifted.set(true);
    if (this.overlayRestoreTimeout) clearTimeout(this.overlayRestoreTimeout);
    this.overlayRestoreTimeout = setTimeout(() => {
      this.overlayRestoreTimeout = null;
      if (!this.isPlayerPaused) this.isOverlayShifted.set(false);
    }, 3000);

    this.cdRef.detectChanges();
    const overlayInner = this.document.querySelector('.chat-overlay-inner') as HTMLElement | null;
    if (overlayInner) void overlayInner.offsetHeight;

    iframe.focus();
    this.userPlayIntentTime = Date.now(); // mark this play as user-initiated
    try { this.player.play(); } catch (e) { /* ignore */ }
    this.restoreAppFocus();
  }

  // Seek to an absolute target and sync all interpolation anchors so the timeline
  // playhead jumps instantly. Without this sync, the rAF loop keeps interpolating
  // from the old anchor and the >5s drift guard rejects the post-seek SDK sample
  // as stale — leaving the visible playhead frozen at the pre-seek position.
  private seekAbsolute(target: number): void {
    const duration = this.videoDurationSec();
    const upper = duration > 0 ? duration - 0.1 : Infinity;
    const clamped = Math.max(0, Math.min(target, upper));

    // Demo mode: no real player to seek; just snap the playhead.
    if (this.demoService.isDemoMode()) {
      this.lastSeekTargetSec = clamped;
      this.lastSeekAtMs = Date.now();
      this.lastSampleSec = clamped;
      this.lastSampleMs = performance.now();
      this.playerTime.set(clamped);
      return;
    }

    if (!this.player) return;
    // Clamp within [0, duration]. If duration is unknown (0 = not yet set on READY),
    // only clamp the lower bound. The -0.1 avoids landing exactly at the end, which
    // some players treat as "video ended" and block further actions.
    this.player.seek(clamped);
    this.lastSeekTargetSec = clamped;
    this.lastSeekAtMs = Date.now();
    this.lastSampleSec = clamped;
    this.lastSampleMs = performance.now();
    this.playerTime.set(clamped);
    // Seek breaks rate continuity (huge sdkDelta vs small wallDelta → poisoned EMA).
    this.resetPlaybackRateSample();
    // New seek invalidates any in-progress buffer-stuck detection from a prior seek.
    this.sdkBufferStuckSinceMs = null;
    // Reset drift counter — fresh seek means anchor is at target, no drift accumulated.
    this.consecutiveLargeDriftSamples = 0;
    // User seeked back inside the video — clear the "ended" auto-advance guard so
    // subsequent play() calls work normally.
    if (duration === 0 || clamped < duration - 1) this.didVideoEnd = false;
  }

  // Estimates current playback rate (1x/2x/0.5x/...) from the ratio of SDK-time delta
  // to wallclock delta between consecutive samples. Smoothed via EMA. Clamped to a
  // sane range as a safety net. Called from the rAF re-sample block.
  private updatePlaybackRate(sdkTime: number, nowMs: number): void {
    if (!this.ENABLE_ADAPTIVE_PLAYBACK_RATE) return;

    // First sample (or after pause/seek reset): anchor, can't compute yet.
    if (this.lastRateSampleSdkSec < 0) {
      this.lastRateSampleSdkSec = sdkTime;
      this.lastRateSampleWallMs = nowMs;
      return;
    }

    const sdkDelta = sdkTime - this.lastRateSampleSdkSec;
    const wallDelta = (nowMs - this.lastRateSampleWallMs) / 1000;

    // Too small a window — float noise dominates. Wait for next sample.
    if (wallDelta < 0.15) return;

    // Non-positive sdkDelta = player rewound or stalled. Skip so we don't poison EMA.
    if (sdkDelta <= 0) return;

    // Twitch speeds are 0.25–2.0. Clamp to [0.1, 4] as a safety net against outliers
    // (if the ratio ever goes haywire, the playhead speed stays bounded).
    const instant = Math.max(0.1, Math.min(4, sdkDelta / wallDelta));
    this.currentPlaybackRate =
      this.PLAYBACK_RATE_EMA_ALPHA * instant +
      (1 - this.PLAYBACK_RATE_EMA_ALPHA) * this.currentPlaybackRate;

    this.lastRateSampleSdkSec = sdkTime;
    this.lastRateSampleWallMs = nowMs;
  }

  // Invalidate the rate-sample pair so the next SDK sample re-anchors instead of
  // computing a bogus rate. Call on seek (huge time jump) and on pause (wallclock
  // keeps ticking while SDK time is frozen — would yield rate ≈ 0 on resume).
  private resetPlaybackRateSample(): void {
    this.lastRateSampleSdkSec = -1;
  }

  // Twitch's autoplay-next can swap a different VOD into our player instance after
  // the ENDED countdown. Detect the swap (getVideo() != expected) and rebuild the
  // player on the original video so selectHighlight / seek act on the right content.
  // Returns true if a rebuild was triggered (caller should bail out of further work).
  private checkVideoHijack(): boolean {
    const expected = this.videoId();
    if (!expected || !this.player) return false;
    try {
      const rawCurrent = this.player.getVideo?.();
      if (rawCurrent === null || rawCurrent === undefined) return false;
      // Normalize — Twitch may return "v123456" or just "123456" across versions.
      const current = String(rawCurrent).replace(/\D/g, '');
      const exp = String(expected).replace(/\D/g, '');
      if (current && exp && current !== exp) {
        this.initTwitchPlayer(exp);
        return true;
      }
    } catch (e) {}
    return false;
  }

  // Public — shared by the keyboard handler (J/L/arrows) and the mobile
  // control bar's on-screen seek buttons (no keyboard on a phone).
  seekRelative(deltaSec: number): void {
    if (!this.player) return;
    // If another seek happened recently, accumulate from the pending target
    // (the player may still be buffering and reporting a stale currentTime).
    const base = (Date.now() - this.lastSeekAtMs < this.SEEK_ACCUMULATE_WINDOW_MS)
      ? this.lastSeekTargetSec
      : (this.player.getCurrentTime() ?? 0);
    this.seekAbsolute(base + deltaSec);
  }

  // Extracted from the Space/K keyboard branch so the mobile control bar's
  // play/pause button can call the identical logic — not a mobile-only
  // duplicate, the keyboard handler now calls this too.
  togglePlayPause(): void {
    if (!this.player) return;
    const hasPendingPlay = this.pendingPlayTimeout !== null;
    // "Paused" includes "currently transitioning to pause" (between K and PAUSE event).
    const effectivelyPaused = this.isPlayerPaused || this.isPausingNow;
    // "Playing" = currently playing, OR paused-with-pending-play (will play soon).
    const effectivelyPlaying = !effectivelyPaused || hasPendingPlay;

    if (effectivelyPlaying) {
      // Flip to pause
      if (hasPendingPlay) {
        clearTimeout(this.pendingPlayTimeout);
        this.pendingPlayTimeout = null;
      } else {
        // Don't snapshot SDK time here — it may be stale (behind our interpolated
        // display), causing a visible backward jump. rAF freezes playerTime at the
        // current interp value on next tick (isPausingNow=true), and the delayed
        // PAUSE re-read below corrects it if needed, without jumping backward.
        this.player.pause();
        this.isPausingNow = true;
        this.lastPauseTime = Date.now(); // sync — don't wait for PAUSE event
      }
    } else {
      // Flip to play; defer until Twitch SDK post-pause window has closed.
      this.schedulePlay();
    }
  }

  // Extracted from the KeyI/KeyO (non-shift) keyboard branches — previously
  // had NO on-screen equivalent at all. Shared by the keyboard handler and
  // the mobile control bar's "начало клипа"/"конец клипа" buttons.
  // Repeated I / O on one moment within 1.5s fold into one undo step (fine-tuning an edge).
  setClipStartToPlayhead(): void {
    const h = this.currentHighlight();
    if (!h) return;
    this.history.transact(`Начало клипа ${this.momentLabel(h)}`,
      () => this.twitchChatService.setClipTimes(this.currentHighlightIndex(), this.playerTime(), h.clipEndTime),
      { focus: h, coalesceKey: `clip-in:${h.startTime}` });
  }

  setClipEndToPlayhead(): void {
    const h = this.currentHighlight();
    if (!h) return;
    this.history.transact(`Конец клипа ${this.momentLabel(h)}`,
      () => this.twitchChatService.setClipTimes(this.currentHighlightIndex(), h.clipStartTime, this.playerTime()),
      { focus: h, coalesceKey: `clip-out:${h.startTime}` });
  }

  // Plain method (not a signal) — read directly in the mobile control bar's
  // template binding. CD re-runs on every rAF playhead tick and every PLAY/
  // PAUSE event handler mutation already (zone.js patches both), so this
  // stays in sync without needing isPlayerPaused itself to become a signal —
  // avoids touching the tightly-guarded playhead-interpolation state machine
  // (see src/CLAUDE.md "Playhead interpolation" stress-test matrix).
  isPaused(): boolean {
    return this.isPlayerPaused;
  }

  // Mobile-only chat bottom-sheet toggle — turns chat on if it was off
  // (mirrors toggleChatOverlay) so tapping the sheet button always shows
  // something, then opens/closes the sheet itself.
  toggleMobileChatSheet(): void {
    if (!this.isChatOverlayEnabled()) {
      this.twitchChatState.isChatOverlayEnabled.set(true);
      this.isChatSheetOpen.set(true);
      return;
    }
    this.isChatSheetOpen.update(v => !v);
  }

  // Scrolls the highlights/moments column into view — used by the mobile
  // control bar since that panel only reveals itself by page-scroll on a
  // phone (no separate route/tab to switch to). Same idiom as the existing
  // auto-scroll-to-selected-highlight effect (scrollIntoView + smooth).
  jumpToHighlights(): void {
    if (typeof document === 'undefined') return;
    document.getElementById('highlights-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  onDocumentClick() {
    // Any outside click closes floating panels. The triggering element and the panel body
    // stopPropagation so clicks on them never reach this handler.
    if (this.samplesPopover()) this.samplesPopover.set(null);
    if (this.showSearchSyntaxHelp()) this.showSearchSyntaxHelp.set(false);
    if (this.isAvatarMenuOpen()) this.isAvatarMenuOpen.set(false);
    this.closeLegend();
  }

  // Physical right Alt held. On the Windows «Русская» layout right Alt is AltGr (the layout
  // has AltGr+8 = ₽), and Chrome reports AltGr+I with altKey/ctrlKey stripped — it looks
  // exactly like a plain I. Tracking the AltRight key itself is the only reliable signal.
  private rightAltHeld = false;

  onWindowKeyUp(event: KeyboardEvent) {
    if (event.code === 'AltRight') this.rightAltHeld = false;
  }

  // Ctrl+Z → undo; Ctrl+Shift+Z / Ctrl+Y → redo (Cmd on Mac, where Cmd+Y stays the browser's
  // History). The letter printed on the key decides on Latin layouts (QWERTZ has Z where
  // QWERTY has Y); Cyrillic layouts fall back to the physical key (Я / Н).
  private undoRedoCommand(e: KeyboardEvent): 'undo' | 'redo' | null {
    const mod = AppComponent.IS_MAC ? e.metaKey && !e.ctrlKey : e.ctrlKey && !e.metaKey;
    if (!mod || e.altKey) return null;
    if (this.rightAltHeld || e.getModifierState?.('AltGraph')) return null;
    const k = e.key.length === 1 ? e.key.toLowerCase() : '';
    const letter = k >= 'a' && k <= 'z' ? k : e.code === 'KeyZ' ? 'z' : e.code === 'KeyY' ? 'y' : '';
    if (letter === 'z') return e.shiftKey ? 'redo' : 'undo';
    if (letter === 'y' && !e.shiftKey && !AppComponent.IS_MAC) return 'redo';
    return null;
  }

  private static readonly NON_TEXT_INPUTS = new Set(['range', 'checkbox', 'radio', 'button', 'submit', 'reset', 'file', 'color', 'image']);

  private static isTextEntryTarget(t: EventTarget | null): boolean {
    if (t instanceof HTMLTextAreaElement) return true;
    if (t instanceof HTMLElement && t.isContentEditable) return true;
    return t instanceof HTMLInputElement && !AppComponent.NON_TEXT_INPUTS.has((t.type || 'text').toLowerCase());
  }

  handleKeyboardEvents(event: KeyboardEvent) {
    if (event.code === 'AltRight') this.rightAltHeld = true;

    // ESC closes any open floating panel before other handling.
    if (event.key === 'Escape') {
      if (this.clipDrag) { this.cancelClipDrag(); return; }
      if (this.regionDrag) { this.cancelRegionDrag(); return; }
      if (this.stripPress) { this.onStripPointerCancel(); return; }
      if (this.samplesPopover()) { this.samplesPopover.set(null); return; }
      if (this.showSearchSyntaxHelp()) { this.showSearchSyntaxHelp.set(false); return; }
      if (this.legendPopover()) { this.closeLegend(); return; }
    }

    // Undo / redo — before the modal + input guards: a focused range / checkbox / select must
    // not swallow it. Text fields keep the browser's own text undo, but an active drag is
    // cancelled first (like Esc). AltGr (right Alt on the RU layout) is never undo.
    const historyCmd = this.undoRedoCommand(event);
    if (historyCmd) {
      if (!this.clipDrag && !this.regionDrag && !this.stripPress && AppComponent.isTextEntryTarget(event.target)) return;
      event.preventDefault();
      if (event.repeat) return;
      this.runHistory(historyCmd);
      return;
    }

    // Shortcuts implementation
    if (this.showHelpModal() || this.showPathHelp()) return;

    // Ignore if typing in input
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) return;

    // Use event.code (physical key location) for letter / punctuation hotkeys so
    // shortcuts work regardless of keyboard layout — Russian ЙЦУКЕН has Л/О/Д/Ш/Щ/Ь
    // at the QWERTY positions of K/J/L/I/O/M, and event.key would only match the
    // English character. Arrow keys keep event.key because numpad arrows (NumLock
    // off) report their numpad code there.
    const code = event.code;
    const arrowKey = event.key;

    // «Область интереса»: Alt+I / Alt+O put the region edge at the playhead, Alt+P toggles.
    // Must come first — the branches below don't check modifiers, so Alt+I would otherwise
    // also move the clip start (plain I). Right Alt on a layout with AltGr (Windows
    // «Русская»): Chrome may report it as Ctrl+Alt, as AltGraph, or strip the modifiers
    // entirely — accept all three (rightAltHeld covers the stripped case).
    const altGraph = event.getModifierState?.('AltGraph') ?? false;
    const isAlt = !event.metaKey && (this.rightAltHeld || altGraph || (event.altKey && !event.ctrlKey));
    if (isAlt && (code === 'KeyI' || code === 'KeyO' || code === 'KeyP')) {
      event.preventDefault();
      if (!this.isProcessed()) return;
      if (code === 'KeyP') this.toggleRegion();
      else this.setRegionEdgeAtPlayhead(code === 'KeyI' ? 'in' : 'out');
      return;
    }

    if (code === 'Space' || code === 'KeyK') {
      event.preventDefault();
      this.togglePlayPause();
    } else if (code === 'KeyJ') {
      event.preventDefault();
      this.seekRelative(event.shiftKey ? -30 : -10);
    } else if (code === 'KeyL') {
      event.preventDefault();
      this.seekRelative(event.shiftKey ? 30 : 10);
    } else if (arrowKey === 'ArrowLeft') {
      event.preventDefault();
      this.seekRelative(-5);
    } else if (arrowKey === 'ArrowRight') {
      event.preventDefault();
      this.seekRelative(5);
    } else if (code === 'KeyI') {
      // Set start
      if (event.shiftKey) {
        const h = this.currentHighlight();
        if (h) this.seekAbsolute(h.clipStartTime);
      } else {
        this.setClipStartToPlayhead();
      }
    } else if (code === 'KeyO') {
      // Set end
      if (event.shiftKey) {
        const h = this.currentHighlight();
        if (h) this.seekAbsolute(h.clipEndTime);
      } else {
        this.setClipEndToPlayhead();
      }
    } else if (code === 'KeyZ' && !isAlt && !event.ctrlKey && !event.metaKey) {
      // Zoom to the clip ⇄ back (Ctrl+Z / Cmd+Z = undo, handled above).
      event.preventDefault();
      if (this.isProcessed()) this.toggleClipZoom();
    } else if (code === 'KeyM') {
      // Toggle merge for current (speech segments don't merge — they aren't chat peaks)
      const h = this.currentHighlight();
      if (h && !isTranscriptSource(h.source)) this.toggleMergeSelection(h.startTime);
    } else if (code === 'Comma') {
      const h = this.currentHighlight();
      if (h) {
        if (h.isDeleted) this.restoreHighlight(h.startTime, event);
        else this.softDeleteHighlight(h.startTime, event);
      }
    } else if (code === 'Period') {
      const h = this.currentHighlight();
      if (h) {
        // On ПОИСК / КЛИПЫ tabs with a result of that tab's source selected, `.` promotes
        // it to the main list. Everywhere else it keeps its original meaning — toggle pin.
        const isSearchPromote = this.activeTab() === 'search' && this.isSearchHighlight(h);
        const isClipPromote = this.activeTab() === 'clips' && h.source === 'user-clip';
        const isSpeechPromote = this.activeTab() === 'speech' && isTranscriptSource(h.source);
        if (isSearchPromote || isClipPromote || isSpeechPromote) {
          this.togglePromoteHighlight(h.startTime, event);
        } else {
          this.togglePinHighlight(h.startTime, event);
        }
      }
    } else if (arrowKey === 'ArrowUp') {
      event.preventDefault();
      const highlights = this.sortedHighlights();
      const currentIdx = highlights.findIndex(h => h.startTime === this.currentHighlight()?.startTime);
      if (currentIdx > 0) this.selectHighlight(highlights[currentIdx - 1].startTime);
    } else if (arrowKey === 'ArrowDown') {
      event.preventDefault();
      const highlights = this.sortedHighlights();
      const currentIdx = highlights.findIndex(h => h.startTime === this.currentHighlight()?.startTime);
      if (currentIdx > -1 && currentIdx < highlights.length - 1) this.selectHighlight(highlights[currentIdx + 1].startTime);
    }
  }

  private initTwitchPlayer(videoId: string) {
    if (this.player) this.destroyPlayer();

    // Determine the parent domain(s) for the Twitch embed
    const parents = new Set<string>();

    if (typeof window !== 'undefined') {
      // 1. Current Window Hostname
      const hostname = window.location.hostname;
      if (hostname && hostname !== '') parents.add(hostname);

      // 2. Ancestor Origins (Chrome/Safari specific - crucial for iframes)
      if ('ancestorOrigins' in window.location && window.location.ancestorOrigins) {
        const ancestors = (window.location as any).ancestorOrigins;
        for (let i = 0; i < ancestors.length; i++) {
          try {
            const url = new URL(ancestors[i]);
            if (url.hostname) parents.add(url.hostname);
          } catch (e) { }
        }
      }

      // 3. Document Referrer (Standard fallback for iframes)
      try {
        if (window.parent !== window && document.referrer) {
          const referrerUrl = new URL(document.referrer);
          if (referrerUrl.hostname) parents.add(referrerUrl.hostname);
        }
      } catch (e) { /* ignore security errors */ }

      // 4. Fallback: Parse href directly if hostname is empty (unlikely but possible in some containers)
      try {
        const hrefUrl = new URL(window.location.href);
        if (hrefUrl.hostname) parents.add(hrefUrl.hostname);
      } catch (e) { }

      // Always add local dev environments
      parents.add('localhost');
      parents.add('127.0.0.1');
    }

    const parentArray = Array.from(parents);

    const options = {
      width: '100%',
      height: '100%',
      video: videoId,
      parent: parentArray,
      autoplay: false,
    };

    if (typeof Twitch !== 'undefined' && Twitch.Player) {
      try {
        this.player = new Twitch.Player('twitch-player', options);

        this.player.addEventListener(Twitch.Player.READY, () => {
          this.player.setVolume(0.5);
          try {
            this.lastSampleSec = this.player.getCurrentTime() ?? 0;
          } catch (e) { this.lastSampleSec = 0; }
          this.lastSampleMs = performance.now();
          try {
            const d = this.player.getDuration();
            if (typeof d === 'number' && d > 0) this.videoDurationSec.set(d);
          } catch (e) {}
          this.startPlayheadTicking();
          // Consume the one-shot seek hint left by a top-clip click in the library:
          // jump straight to the clip's vod_offset instead of starting at the VOD's
          // beginning. Routed through seekAbsolute (canonical pathway) to update all
          // playhead anchors. Cleared so a subsequent load (Стримы tab, etc.) starts clean.
          const pendingSeek = this.twitchChatState.pendingSeekAfterLoad();
          if (pendingSeek !== null) {
            this.seekAbsolute(pendingSeek);
            this.twitchChatState.pendingSeekAfterLoad.set(null);
          }
        });

        this.player.addEventListener(Twitch.Player.PLAY, () => {
          // Autoplay-next often fires PLAY immediately after swapping in a new VOD —
          // catch the hijack before any other state mutates.
          if (this.checkVideoHijack()) return;
          // Distinguish a real user-initiated play from a seek-buffer resume event.
          // During a seek, Twitch fires internal PAUSE→PLAY that doesn't reflect real
          // user intent — those must NOT flip our paused/playing state.
          const inSeekWindow = Date.now() - this.lastSeekAtMs < this.SEEK_ACCUMULATE_WINDOW_MS;
          const userInitiated = Date.now() - this.userPlayIntentTime < 2000;
          // Auto-advance guard: if video ended and a PLAY fires without user intent,
          // Twitch is advancing to the next VOD. Pause immediately.
          if (this.didVideoEnd && !userInitiated) {
            try { this.player.pause(); } catch (e) {}
            return;
          }
          if (inSeekWindow && !userInitiated) return;
          this.isPlayerPaused = false;
          this.isPausingNow = false;
          if (this.overlayRestoreTimeout) { clearTimeout(this.overlayRestoreTimeout); this.overlayRestoreTimeout = null; }
          this.isOverlayShifted.set(false);
          // Refresh interpolation anchor — but only when NOT in a seek window. During seek,
          // SDK's getCurrentTime returns stale pre-seek values. Using max(sdkTime, playerTime)
          // would snap backward-seek results forward to the pre-seek position. seekRelative
          // already set lastSampleSec/lastSampleMs correctly, so we leave them alone.
          if (!inSeekWindow) {
            try {
              const t = this.player.getCurrentTime();
              if (typeof t === 'number') {
                this.lastSampleSec = Math.max(t, this.playerTime());
                this.lastSampleMs = performance.now();
              }
            } catch (e) {}
          }
        });
        this.player.addEventListener(Twitch.Player.PAUSE, () => {
          // Ignore seek-buffering pauses — they fire with isPausingNow=false (no user K press)
          // and would wrongly hide the chat overlay and schedule a stale re-read.
          if (Date.now() - this.lastSeekAtMs < this.SEEK_ACCUMULATE_WINDOW_MS && !this.isPausingNow) return;
          this.isPlayerPaused = true;
          this.isPausingNow = false;
          this.lastPauseTime = Date.now();
          // Pause freezes SDK time but wallclock keeps ticking — next post-resume sample
          // would compute rate ≈ 0 and poison the EMA. Invalidate now, re-anchor on resume.
          this.resetPlaybackRateSample();
          // Delayed re-read: Twitch SDK's getCurrentTime may still be stale at PAUSE event
          // time. Wait for it to settle, then fetch the accurate paused position.
          // Strictly monotonic-forward: only snap if SDK is forward of current display.
          // Skip if a seek occurred while we were waiting — SDK would return stale pre-seek
          // values which are typically forward of the seek target and would snap the
          // playhead backward to the pre-seek position.
          setTimeout(() => {
            if (!this.isPlayerPaused || !this.player) return;
            if (Date.now() - this.lastSeekAtMs < this.SEEK_ACCUMULATE_WINDOW_MS) return;
            try {
              const t = this.player.getCurrentTime();
              if (typeof t === 'number') {
                const current = this.playerTime();
                if (t >= current) {
                  this.playerTime.set(t);
                  this.lastSampleSec = t;
                  this.lastSampleMs = performance.now();
                }
              }
            } catch (e) {}
          }, 500);
          // Hide chat overlay on pause so iframe bbox is clear for the next play() call.
          this.isOverlayShifted.set(true);
        });
        this.player.addEventListener(Twitch.Player.ENDED, () => {
          this.isPlayerPaused = true;
          this.isPausingNow = false;
          this.isOverlayShifted.set(true);
          // Twitch auto-advances to the next VOD ~20s after ENDED. Pause explicitly to
          // stop the countdown, and re-pause on any subsequent PLAY event that fires
          // without a user-initiated play (autoplay-next triggering).
          this.didVideoEnd = true;
          try { this.player.pause(); } catch (e) {}
        });
      } catch (e) {
        console.error('Twitch Player init error:', e);
      }
    } else {
      setTimeout(() => this.initTwitchPlayer(videoId), 500);
    }
  }

  private destroyPlayer() {
    if (this.overlayRestoreTimeout) { clearTimeout(this.overlayRestoreTimeout); this.overlayRestoreTimeout = null; }
    if (this.pendingPlayTimeout) { clearTimeout(this.pendingPlayTimeout); this.pendingPlayTimeout = null; }
    this.stopPlayheadTicking();

    // Reset all player-session state so the next VOD starts from a clean slate.
    // Without this, switching VODs while the previous was playing carried isPlayerPaused=false
    // and currentPlaybackRate (e.g., 2.0) into the new session — rAF then advanced the
    // playhead on a freshly initialized (paused, autoplay=false) player as if it were
    // playing at the carried rate, with chat overlay visible. Stale lastSeekTargetSec /
    // sdkBufferStuckSinceMs from the prior VOD also produced spurious buffer-stuck freezes.
    this.isPlayerPaused = true;
    this.isPausingNow = false;
    // Hide overlay on switch — Twitch's bbox check rejects play() while overlay covers
    // the iframe, and our paused = hidden invariant matches user expectation that "no
    // playback ⇒ no chat overlay". PLAY event re-shows it.
    this.isOverlayShifted.set(true);
    this.videoDurationSec.set(0);
    this.didVideoEnd = false;
    this.currentPlaybackRate = 1.0;
    this.resetPlaybackRateSample();
    this.lastSampleSec = 0;
    this.lastSampleMs = 0;
    this.prevSdkSample = -1;
    this.lastSeekTargetSec = 0;
    this.lastSeekAtMs = 0;
    this.sdkBufferStuckSinceMs = null;
    this.consecutiveLargeDriftSamples = 0;
    this.userPlayIntentTime = 0;
    this.lastPauseTime = 0;
    this.lastVideoIdCheckMs = 0;
    this.playerTime.set(0);

    const container = document.getElementById('twitch-player');
    if (container) container.innerHTML = '';
    this.player = null;
  }

  ngOnDestroy() {
    if (this.historyToastTimer) clearTimeout(this.historyToastTimer);
    if (this.mergeBannerTimer) clearTimeout(this.mergeBannerTimer);
    this.tooltipService.setKeepOutProvider(null);
    this.heatmapScrollerRO?.disconnect();
    this.stopEdgeAutoScroll();
    this.destroyPlayer();
    if (this.progressTickInterval !== null) {
      clearInterval(this.progressTickInterval);
      this.progressTickInterval = null;
    }
  }
}
